import {
  BOARD_ORIGIN_X,
  BOARD_PIXEL_SIZE,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  TILE_PIXEL_SIZE,
  activeBoardPixelSize,
  activeBoardOriginX,
  activeTilePixelSize
} from "../game/constants";
import walkerImg1Url from "../../public/assets/raw/walking_character.png?url";
import walkerImg2Url from "../../public/assets/raw/walking_character2.png?url";
import walkerImg3Url from "../../public/assets/raw/walking_character3.png?url";
import { getLayout, type LayoutMetrics } from "../game/layout";
import { doomClockCapForLevel, versusScoreTargetForLevel } from "../game/levelConfig";
import { SOLO_MODES, soloModeAccent, soloModeLabel } from "../game/soloModes";
import { type DragState, type Rect, type UiRects, type LeaderboardScrollGesture } from "../game/input";
import type {
  BoardState,
  CollapseAnimState,
  GamePhase,
  MatchShape,
  Position,
  RotationAnimState,
  RunState,
  PerformanceSettings,
  PowerupKind,
  SoloMode,
  SwapAnimState,
  TileData
} from "../game/types";
import type { TutorialState } from "../game/tutorial";
import type {
  VersusMatchRenderData,
} from "../versus/types";
import { type RankedTier } from "../versus/ranked";
import { bpmForLevel } from "../game/audioConfig";
import { getCharacterById } from "../economy/characters";
import { drawPhoneFriendsScreen, type PhoneFriendsDeps } from "./screens/drawPhoneFriends";
import { drawPhoneWalletScreen } from "./screens/drawPhoneWallet";
import {
  drawVersusSuddenDeathBannerScreen,
  drawVersusBottomStatsScreen,
  drawMobileVersusStatsRowsScreen,
  drawSyncStatusBadgeScreen,
} from "./screens/drawVersusHud";
import { getCachedEquipped } from "../economy/inventoryManager";
import type { DebugSettings } from "../game/debugSettings";
import { clamp01, easeOutBack, easeOutCubic, lerp } from "../shared/easing";
import { resolveVersusFeedbackPolicy, type VersusFeedbackMode } from "../game/versusFeedbackPolicy";
import {
  createParticleSystem,
  drawParticles,
  emitDirectionalParticles,
  emitGameOverParticles,
  emitMatchParticles,
  emitRainbowStarParticles,
  updateParticles,
  type ParticleSystem
} from "./particles";
import { addFloatingText, createFloatingTextSystem, drawFloatingTexts, updateFloatingTexts, type FloatingTextSystem } from "./floatingText";
import { TextWidthCache } from "./textWidthCache";
import { drawButton } from "./ui";
import { drawDivider, drawDisconnectOverlay } from "./versusRenderer";
import {
  VERSUS_PANEL_WIDTH,
  VERSUS_OPPONENT_X,
  VERSUS_CANVAS_WIDTH,
  VERSUS_MOBILE_CANVAS_WIDTH,
} from "../versus/versusConstants";
import { type RadialMenuState } from "../game/radialMenu";
import { AssetManager } from "./assets/assetManager";
import type { SpriteId } from "./assets/types";
import { ShellRaveBorderFx } from "./raveBorderFx";
import { drawGameOverScreen, type GameOverFlashState } from "./screens/drawGameOver";
import { drawPhoneHighscoresScreen } from "./screens/drawPhoneHighscores";
import { drawVersusResultScreen, type VersusResultAnimState } from "./screens/drawVersusResult";
import { drawVersusLobbyScreen } from "./screens/drawVersusLobby";
import { drawGemShopScreen } from "./screens/drawGemShop";
import { drawSettingsScreen } from "./screens/drawSettings";
import { drawLeaderboardScreen } from "./screens/drawLeaderboardScreen";
import { drawDailyLoginModalScreen } from "./screens/drawDailyLoginModal";
import { drawTitleScreen } from "./screens/drawTitle";
import { drawTokenShopScreen } from "./screens/drawTokenShop";
import { drawNotificationsScreen } from "./screens/drawNotifications";
import { drawFriendsScreen } from "./screens/drawFriends";
import { drawBoardScreen } from "./screens/drawBoard";
import { drawProfileSettingsScreen } from "./screens/drawProfileSettings";
import { drawDebugOverlayScreen } from "./screens/drawDebugOverlay";
import { drawPauseMenuScreen } from "./screens/drawPauseMenu";
import { drawDoomMeterScreen } from "./screens/drawDoomMeter";
import { drawBackgroundScreen } from "./screens/drawBackground";
import { drawShopScreen } from "./screens/drawShop";
import { drawHeaderScreen, type HeaderMutableState } from "./screens/drawHeader";
import { drawAuthGateScreen } from "./screens/drawAuthGate";
import { drawSoloSelectScreen, type SoloSlideState } from "./screens/drawSoloSelect";
import { drawDailyRewardsScreen } from "./screens/drawDailyRewards";
import { drawDailyRewardsOpenScreen } from "./screens/drawDailyRewardsOpen";
import { drawOnboardingCallScreen } from "./screens/drawOnboardingCall";
import { drawUsernamePickScreen } from "./screens/drawUsernamePick";
import { drawProfileBrowseScreen } from "./screens/drawProfileBrowse";
import { drawProfileGridScreen } from "./screens/drawProfileGrid";
import { drawPhoneMessageDetailScreen } from "./screens/drawPhoneMessageDetail";
import { drawPhoneMessagesScreen } from "./screens/drawPhoneMessages";
import { drawOnboardingOverlayScreen } from "./screens/drawOnboardingOverlay";
import { drawPhoneReferFriendScreen } from "./screens/drawPhoneReferFriend";
import { drawPhoneAddFriendInlineScreen } from "./screens/drawPhoneAddFriendInline";
import { drawPhoneFriendDetailScreen } from "./screens/drawPhoneFriendDetail";
import { drawPhoneSettingsScreen } from "./screens/drawPhoneSettings";
import { drawPhoneEditUsernameScreen } from "./screens/drawPhoneEditUsername";
import { drawTutorialOverlayScreen } from "./screens/drawTutorialOverlay";
import { drawRadialMenuScreen } from "./screens/drawRadialMenu";
import { drawWorldMapScreen } from "./screens/drawWorldMap";
import { drawSidePanelTelemetryScreen } from "./screens/drawSidePanelTelemetry";
import { drawSideLaneThemeScreen } from "./screens/drawSideLaneTheme";
import { drawTrapFxOverlayScreen } from "./screens/drawTrapFxOverlay";
import {
  LeaderboardScrollController,
  type LeaderboardScrollConfig
} from "./leaderboardScrollController";
import type { GameplayDebugSnapshot } from "../game/debugSnapshot";
import { isMobileViewport, prefersReducedMotion } from "../game/platform";
import {
  buildProfileVisibleItems,
  type ProfileVisibleItem,
} from "../game/profileVisibleItems";

// ── Extracted constants, types, and helpers (Phase 1 refactor) ────────────
// Only symbols directly used in this file are imported; the rest are re-exported below.
import {
  GAME_FONT,
  mFont,
  mFontPlain,
  pxFont,
  TILE_COLORS,
  COMBO_BADGE_COLOR,
  TILE_DRAW_PADDING,
  MENU_CONTENT_OFFSET_PX,
  menuBtnH,
  menuBtnHLg,
  menuGap,
  UI_EDGE_PAD,
  UI_CLOSE_Y,
  uiCloseSize,
  GAMEPLAY_CUE_STACK_CASH_FONT_PX,
  GAMEPLAY_CUE_STACK_HYPE_FONT_PX,
  posKey,
  resolveGameplayTopRightHudRect,
  resolveVersusOptionsPanelRect,
  rowRushVisualParticleColors,
  POWERUP_ICON_PATHS,
  SOLO_SELECTOR_LOGO_PATHS,
  SOLO_MODE_BACKGROUND_PATHS,
  tileColorSpriteId,
  specialSpriteId,
  specialSpawnLabel,
  createOpponentAdaptivePerfState,
  updateOpponentAdaptivePerfState,
  resolveEffectiveOpponentDisplayMode,
  resolveMenuMessageBubbleWidth,
  tileBlinkFpsForLevel,
  musicPulseForLevel,
  isSinglePlayerHudPolishPhase,
  resolveDoomDangerFraction,
  resolveDoomDangerActive,
  resolveOnboardingRafsbyLayout,
  resolveWipeDissolveFxConfig,
  resolveCrateBreakFxConfig,
  resolveRowRushTargetRowVisual,
  shouldDrawSidePanelTelemetry,
  shouldRenderDebugOverlay,
  applyPhaseScopedShopUiRects,
  collapseStyleForTierAndFeedbackMode,
  resolveCollapseClearFxSpriteId,
  hypeScaleForFeedbackMode,
} from "./renderConstants";
import type {
  OpponentAdaptivePerfState,
  PhoneMetrics,
  GameplayCueStackLayout,
  RowRushTargetRowVisual,
} from "./renderConstants";

// ── Re-export previously-exported types from renderConstants ──────────────
// ── Extracted RenderModel interface (Phase 2 refactor) ────────────────────
import type { RenderModel } from "./renderModel";

// ── Extracted layout helpers (Phase 2 refactor) ──────────────────────────
import type {
  DrawBoardResult,
  DrawBoardOptions,
  TitleGemBurst,
} from "./renderConstants";
import {
  truncName,
  offsetUiRects,
  menuButtonColumns,
  roundedRectPath,
  drawImageContain,
  resolveMobileProfileFilterTabRects,
  resolvePortraitContainRect,
} from "./renderConstants";

export class CanvasRenderer {
  readonly canvas: HTMLCanvasElement;
  readonly canvasWrapper: HTMLDivElement;
  private shell: HTMLDivElement | null = null;
  private readonly ctx: CanvasRenderingContext2D;
  getContext(): CanvasRenderingContext2D { return this.ctx; }
  private readonly assetManager?: AssetManager;
  private readonly dpr: number;
  private readonly particles: ParticleSystem;
  private readonly floatingTexts: FloatingTextSystem;
  private lastCollapseStepIndex = -1;
  private emittedWipeParticles = new WeakSet<CollapseAnimState["steps"][number]>();
  private emittedRowRushTargetParticles = new WeakSet<CollapseAnimState["steps"][number]>();
  private emittedInterruptedSweepParticles = new WeakSet<CollapseAnimState["steps"][number]>();
  private lastRowRushAmbientEmitMs = 0;
  private lastFrameMs = 0;
  private beatPulse = 0;
  private currentWarningActive = false;
  private currentDragState: DragState | null = null;
  private currentHoverCell: Position | null = null;
  private titleLogo: HTMLImageElement | null = null;
  private titleLogoReady = false;
  private titleLogoFailed = false;
  private powerupIconImages: Partial<Record<PowerupKind, HTMLImageElement>> = {};
  private powerupIconFailed = new Set<PowerupKind>();
  private sideFxLeftCanvas: HTMLCanvasElement | null = null;
  private sideFxRightCanvas: HTMLCanvasElement | null = null;
  private sideFxLeftCtx: CanvasRenderingContext2D | null = null;
  private sideFxRightCtx: CanvasRenderingContext2D | null = null;
  private turntableLeftImage: HTMLImageElement | null = null;
  private turntableRightImage: HTMLImageElement | null = null;
  private turntableLeftActiveImage: HTMLImageElement | null = null;
  private turntableRightActiveImage: HTMLImageElement | null = null;
  private mixerImage: HTMLImageElement | null = null;
  private settingsIconImage: HTMLImageElement | null = null;
  private playerIconImage: HTMLImageElement | null = null;
  private uiCloseIcon: HTMLImageElement | null = null;
  private uiSettingsIcon: HTMLImageElement | null = null;
  private uiPadlockIcon: HTMLImageElement | null = null;
  private uiWriteIcon: HTMLImageElement | null = null;
  private uiContactIcon: HTMLImageElement | null = null;
  private uiShirtIcon: HTMLImageElement | null = null;
  private uiRefreshIcon: HTMLImageElement | null = null;
  private bgImage: HTMLImageElement | null = null;
  private gemImage: HTMLImageElement | null = null;
  private starScoreImage: HTMLImageElement | null = null;
  private checkCompleteImage: HTMLImageElement | null = null;
  private aboutHeadLogoImage: HTMLImageElement | null = null;
  private aboutWordmarkLogoImage: HTMLImageElement | null = null;
  private gemShopDoorImage: HTMLImageElement | null = null;
  private crateClosedImage: HTMLImageElement | null = null;
  private crateOpenImage: HTMLImageElement | null = null;
  private gemPackImages: (HTMLImageElement | null)[] = [null, null, null, null];
  private gemPackImageRects: (Rect | null)[] = [null, null, null, null];
  private rankVinylImages: Partial<Record<import("../versus/ranked").RankedTier, HTMLImageElement>> = {};
  private headerCoinIconRect: Rect | null = null;
  private headerGemIconRect: Rect | null = null;
  private titleBgImage: HTMLImageElement | null = null;
  private loadingCityBgImage: HTMLImageElement | null = null;
  private soloSelectorLogoImages: Record<SoloMode, HTMLImageElement | null> = {
    classic: null,
    "row-rush": null,
    clearout: null,
    "color-hunt": null,
    turbomaxxing: null
  };
  private soloModeBackgroundImages: Record<SoloMode, HTMLImageElement | null> = {
    classic: null,
    "row-rush": null,
    clearout: null,
    "color-hunt": null,
    turbomaxxing: null
  };
  private buildingImages: Record<string, HTMLImageElement | null> = { battle: null, shop: null, solo: null, lobby: null, apartment: null };
  // Shared cover-fit crop params set by drawBackground, reused by drawTitle
  private cityBgCrop = { sx: 0, sy: 0, sw: 0, sh: 0, dw: 0, dh: 0 };
  // Per-building hover progress (0–1), set by drawTitle, read by drawBackground
  private buildingHover: Record<string, number> = {};
  // Walking characters on title screen
  private walkingCharImages: Array<HTMLImageElement | null> = [null, null, null];
  private walkers: Array<{
    dir: 1 | -1;
    progress: number;
    speed: number;
    delay: number; // seconds to wait before respawning
    path: Array<{ x: number; y: number }>;
  }> = [
    { // Character 1 — upper street, walks right to left
      dir: -1, progress: 0, speed: 0.07, delay: 0,
      path: [
        { x: 1.08, y: 0.56 }, { x: 0.80, y: 0.62 },
        { x: 0.55, y: 0.72 }, { x: 0.28, y: 0.62 },
        { x: -0.08, y: 0.56 },
      ],
    },
    { // Character 2 — middle street, walks left to right
      dir: 1, progress: 0, speed: 0.06, delay: 3,
      path: [
        { x: -0.08, y: 0.62 }, { x: 0.18, y: 0.65 },
        { x: 0.40, y: 0.70 }, { x: 0.60, y: 0.72 },
        { x: 0.80, y: 0.68 }, { x: 1.08, y: 0.62 },
      ],
    },
    { // Character 3 — lower street, walks right to left
      dir: -1, progress: 0, speed: 0.065, delay: 7,
      path: [
        { x: 1.08, y: 0.86 }, { x: 0.85, y: 0.84 },
        { x: 0.55, y: 0.75 }, { x: 0.52, y: 0.81 },
        { x: 0.36, y: 0.83 }, { x: 0.15, y: 0.90 },
        { x: -0.08, y: 0.92 },
      ],
    },
  ];
  private dinerBgImage: HTMLImageElement | null = null;
  private shopBgImage: HTMLImageElement | null = null;
  private gemShopBgImage: HTMLImageElement | null = null;
  private backstageBgImage: HTMLImageElement | null = null;
  private apartmentBgImage: HTMLImageElement | null = null;
  private worldMapImage: HTMLImageElement | null = null;
  private soloSelectBgImage: HTMLImageElement | null = null;
  private cloudImage: HTMLImageElement | null = null;
  private phoneImage: HTMLImageElement | null = null;
  private rafsbyPortrait: HTMLImageElement | null = null;
  private rafsbyPhoneImage: HTMLImageElement | null = null;
  private shopPrevIdx = -1;
  private shopPrevSkinPath = "";
  private shopSlideStartMs = 0;
  private shopSlideDir = 0; // -1 = prev, +1 = next
  private soloPrevIdx = -1;
  private soloPrevMode: import("../game/types").SoloMode | null = null;
  private soloSlideStartMs = 0;
  private soloSlideDir = 0; // -1 = prev, +1 = next
  // Versus result: mutable animation state (passed by reference to drawVersusResultScreen)
  private versusResultAnimState: VersusResultAnimState = {
    versusRatingChangeBefore: 0,
    versusRatingChangeMs: 0,
    versusRemoteLeftMs: 0,
  };
  // Interior entrance zoom transition
  private interiorEnterMs = 0;
  private prevWasInterior = false;
  private shellRaveFx: ShellRaveBorderFx | null = null;
  private particleDensity = 1;
  private opponentAdaptivePerfState: OpponentAdaptivePerfState = createOpponentAdaptivePerfState();
  private characterImages = new Map<string, HTMLImageElement>();
  private characterImageFailed = new Set<string>();
  private characterImageLoading = new Set<string>();
  private reducedMotionPreferred = false;
  private gameplayDoomDangerActive = false;
  private static MAX_CONCURRENT_CHAR_LOADS = 4;
  private titleGemBursts: TitleGemBurst[] = [];
  private heartLifeImage: HTMLImageElement | null = null;
  private heartLifePremiumImage: HTMLImageElement | null = null;
  private phoneTabIcons: Record<string, HTMLImageElement> = {};
  private coinIconImage: HTMLImageElement | null = null;
  private cashIconImage: HTMLImageElement | null = null;
  private lastDailyLoginClaimRect: Rect | null = null;
  private currentLayout: LayoutMetrics = getLayout(600);
  private lastSeenGameplayLevel: number | null = null;
  private levelBadgeWiggleStartMs = -1;
  // Whirlwind ambient effect state
  private whirlwinds: { x: number; y: number; spawnMs: number; lifetimeMs: number; rotSpeed: number; size: number; hue: number }[] = [];
  private lastWhirlwindSpawnMs = 0;
  private bps: number = BOARD_PIXEL_SIZE; // board pixel size (mobile: 360, desktop: 320)
  private box: number = BOARD_ORIGIN_X;   // board origin x (mobile: 0, desktop: 38)
  private tps = TILE_PIXEL_SIZE;  // tile pixel size (mobile: 36, desktop: 32)

  /** Pixels to offset all content (not background) for iOS safe area. */
  private _nativeSafeAreaTop = 0;
  private _nativeSafeAreaBottom = 0;
  /** Menu-only safe area insets derived from CSS env() values. */
  private _menuSafeAreaTop = 0;
  /** Raw logical canvas height before safe area subtraction. */
  private _rawLogicalHeight = 600;

  /** Set the native safe area insets in canvas logical pixels. */
  setNativeSafeAreaInsets(top: number, bottom: number): void {
    this._nativeSafeAreaTop = top;
    this._nativeSafeAreaBottom = bottom;
    // Recompute layout with effective height (safe zone only)
    this.currentLayout = getLayout(this._rawLogicalHeight - top - bottom);
  }

  setMenuSafeAreaInsets(top: number, bottom: number): void {
    this._menuSafeAreaTop = Math.max(0, top);
    void bottom;
  }

  // Phase transition crossfade state
  private transitionSnapshot: ImageBitmap | null = null;
  private transitionElapsed = 0;
  private lastRenderedPhase: GamePhase | null = null;
  private static readonly TRANSITION_DURATION_MS = 250;
  private static readonly GAMEPLAY_PHASES: ReadonlySet<string> = new Set([
    "RUN_PLAYABLE", "RUN_RESOLVING", "RUN_LEVELUP", "RUN_GAMEOVER"
  ]);

  // Leaderboard screen scroll state
  private leaderboardScreenEnterTime = 0;
  private lastLeaderboardSurfaceKey: string | null = null;
  private runGameOverSurfaceId = 0;
  private leaderboardScrollGesture: LeaderboardScrollGesture = {
    held: false,
    deltaY: 0,
    lastInteractionMs: 0
  };
  private readonly leaderboardScrollController = new LeaderboardScrollController();
  private phoneOpenMs = 0;
  private phoneClosing = false;
  private phoneCloseMs = 0;
  private pauseMenuOpenMs = 0;
  private pauseMenuClosing = false;
  private pauseMenuCloseMs = 0;
  private prevPhase: string = "";

  // Button press feedback state
  private pressedButtonRect: Rect | null = null;
  private pressReleasedRect: Rect | null = null;
  private pressReleasedAt = 0;
  private hoverPos: { x: number; y: number } | null = null;
  /** Tracks per-button hover animation progress (0â†'1). Keyed by "x,y,w,h". */
  private hoverAnimations = new Map<string, number>();
  private lastHoverUpdateMs = 0;
  private static readonly HOVER_FADE_IN_MS = 120;
  private static readonly HOVER_FADE_OUT_MS = 180;
  private static readonly PRESS_RELEASE_FADE_MS = 100;

  // â"€â"€ Menu animation state â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  /** Timestamp when the current menu phase was entered (for stagger entrance). */
  private menuPhaseEnterMs = 0;
  /** The phase that was active when menuPhaseEnterMs was set. */
  private menuPhaseEnterPhase: GamePhase | null = null;
  /** Last settings page index â€" used to detect page changes for slide anim. */
  private settingsLastPage = -1;
  /** Timestamp when settings page last changed. */
  private settingsPageChangeMs = 0;
  /** Direction of the last settings page change (-1 = prev, 1 = next). */
  private settingsPageDir = 1;
  /** Last seen gem count â€" used to detect changes for bounce anim. */
  private lastSeenGems = -1;
  /** Last seen coin count. */
  private lastSeenCoins = -1;
  /** Timestamp when gems changed (for bounce). */
  private gemBounceMs = 0;
  /** Timestamp when coins changed (for bounce). */
  private coinBounceMs = 0;
  /** Mutable game-over flash state (passed by reference to drawGameOverScreen). */
  private gameOverFlashState: GameOverFlashState = {
    highScoreFlashTriggered: false,
    highScoreFlashMs: 0,
    levelUpFlashTriggered: false,
    levelUpFlashMs: 0,
  };
  /** Last equipped skin ID for equip flash. */
  private lastEquippedId = "";
  private lastEquipFlashMs = 0;
  private readonly textWidthCache = new TextWidthCache(1024);
  private readonly frameLinearGradientCache = new Map<string, CanvasGradient>();
  private frameNowMs = 0;

  constructor(assetManager?: AssetManager) {
    this.assetManager = assetManager;
    this.dpr = Math.min(window.devicePixelRatio || 1, 3);
    this.canvas = document.createElement("canvas");
    // Don't set initial canvas dimensions here -- bootstrap calls setCanvasSize()
    // with the correct platform-aware dimensions before first render.
    this.canvas.width = 1;
    this.canvas.height = 1;
    this.canvas.style.touchAction = "none";
    this.canvasWrapper = document.createElement("div");
    this.canvasWrapper.className = "canvas-wrapper";
    const context = this.canvas.getContext("2d");
    if (context == null) {
      throw new Error("Canvas 2D context unavailable");
    }
    context.scale(this.dpr, this.dpr);
    context.imageSmoothingEnabled = false;
    this.ctx = context;
    this.particles = createParticleSystem();
    this.floatingTexts = createFloatingTextSystem();
    this.loadTitleLogo();
    this.loadPowerupIcons();
    this.loadUiImages();
  }

  private nowMs(): number {
    return this.frameNowMs > 0 ? this.frameNowMs : performance.now();
  }

  private nowSec(): number {
    return this.nowMs() / 1000;
  }

  private measureTextWidth(text: string): number {
    return this.textWidthCache.get(
      this.ctx.font,
      text,
      (value) => this.ctx.measureText(value).width
    );
  }

  private measureTextWidthAtPx(text: string, px: number, weight = "bold"): number {
    const prevFont = this.ctx.font;
    this.ctx.font = pxFont(px, weight);
    const width = this.measureTextWidth(text);
    this.ctx.font = prevFont;
    return width;
  }

  private getFrameLinearGradient(
    key: string,
    createGradient: () => CanvasGradient
  ): CanvasGradient {
    const cached = this.frameLinearGradientCache.get(key);
    if (cached != null) {
      return cached;
    }
    const gradient = createGradient();
    this.frameLinearGradientCache.set(key, gradient);
    return gradient;
  }

  private readReducedMotionPreference(): boolean {
    this.reducedMotionPreferred = prefersReducedMotion();
    return this.reducedMotionPreferred;
  }

  /** Resize canvas for versus mode (wider) or back to solo. */
  setCanvasSize(width: number, height: number): void {
    this.canvas.width = width * this.dpr;
    this.canvas.height = height * this.dpr;
    this.canvas.style.aspectRatio = `${width} / ${height}`;
    this.bps = activeBoardPixelSize();
    this.box = activeBoardOriginX();
    this.tps = activeTilePixelSize();
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.ctx.imageSmoothingEnabled = false;
    this._rawLogicalHeight = height;
    this.currentLayout = getLayout(height - this._nativeSafeAreaTop - this._nativeSafeAreaBottom);
  }

  /** Dynamic board Y from the anchor-based layout. */
  private get boardY(): number {
    return this.currentLayout.boardOriginY;
  }

  /** Call each frame before draw() to sync pressed button state from InputManager. */
  setPressedButton(rect: Rect | null): void {
    if (rect != null) {
      this.pressedButtonRect = rect;
      this.pressReleasedRect = null;
    } else if (this.pressedButtonRect != null) {
      // Button was just released â€" start fade-out
      this.pressReleasedRect = this.pressedButtonRect;
      this.pressReleasedAt = this.nowMs();
      this.pressedButtonRect = null;
    }
    // Clean up expired release animation
    if (
      this.pressReleasedRect != null &&
      this.nowMs() - this.pressReleasedAt > CanvasRenderer.PRESS_RELEASE_FADE_MS
    ) {
      this.pressReleasedRect = null;
    }
  }

  /** Call each frame to sync hover pointer position from InputManager. */
  setHoverPosition(pos: { x: number; y: number } | null): void {
    this.hoverPos = pos;
  }

  /** Call each frame before draw() to sync leaderboard scroll gesture state. */
  setLeaderboardScrollGesture(gesture: LeaderboardScrollGesture): void {
    this.leaderboardScrollGesture = gesture;
  }

  /** Returns 0â€"1 hover animation progress for a button rect. */
  private getHoverProgress(rect: Rect): number {
    const key = `${rect.x},${rect.y},${rect.w},${rect.h}`;
    return this.hoverAnimations.get(key) ?? 0;
  }

  /** Tick hover animations for a set of button rects. Call once per frame. */
  updateHoverAnimations(rects: Rect[]): void {
    const now = this.nowMs();
    const dt = this.lastHoverUpdateMs > 0 ? now - this.lastHoverUpdateMs : 16;
    this.lastHoverUpdateMs = now;

    const activeKeys = new Set<string>();
    for (const rect of rects) {
      const key = `${rect.x},${rect.y},${rect.w},${rect.h}`;
      activeKeys.add(key);
      const current = this.hoverAnimations.get(key) ?? 0;
      const isOver = this.hoverPos != null &&
        this.hoverPos.x >= rect.x && this.hoverPos.y >= rect.y &&
        this.hoverPos.x <= rect.x + rect.w && this.hoverPos.y <= rect.y + rect.h;

      if (isOver) {
        const next = Math.min(1, current + dt / CanvasRenderer.HOVER_FADE_IN_MS);
        this.hoverAnimations.set(key, next);
      } else if (current > 0) {
        const next = Math.max(0, current - dt / CanvasRenderer.HOVER_FADE_OUT_MS);
        if (next > 0) {
          this.hoverAnimations.set(key, next);
        } else {
          this.hoverAnimations.delete(key);
        }
      }
    }
    // Clean up stale keys from previous screens
    for (const key of this.hoverAnimations.keys()) {
      if (!activeKeys.has(key)) this.hoverAnimations.delete(key);
    }
  }

  private isButtonPressed(rect: Rect): boolean {
    return this.pressedButtonRect != null &&
      rect.x === this.pressedButtonRect.x &&
      rect.y === this.pressedButtonRect.y &&
      rect.w === this.pressedButtonRect.w &&
      rect.h === this.pressedButtonRect.h;
  }

  private getButtonReleaseProgress(rect: Rect): number {
    if (
      this.pressReleasedRect == null ||
      rect.x !== this.pressReleasedRect.x ||
      rect.y !== this.pressReleasedRect.y ||
      rect.w !== this.pressReleasedRect.w ||
      rect.h !== this.pressReleasedRect.h
    ) {
      return 1;
    }
    const elapsed = this.nowMs() - this.pressReleasedAt;
    return Math.min(1, elapsed / CanvasRenderer.PRESS_RELEASE_FADE_MS);
  }

  /** Draws a button with press-feedback and hover awareness. */
  private drawBtn(rect: Rect, label: string, options?: Parameters<typeof drawButton>[3]): void {
    const ctx = this.ctx;
    const pressed = this.isButtonPressed(rect);
    const releaseProgress = this.getButtonReleaseProgress(rect);
    const hoverT = this.getHoverProgress(rect);

    if (pressed) {
      ctx.save();
      const cx = rect.x + rect.w / 2;
      const cy = rect.y + rect.h / 2;
      ctx.translate(cx, cy);
      ctx.scale(0.95, 0.95);
      ctx.translate(-cx, -cy);
      ctx.globalAlpha = (ctx.globalAlpha ?? 1) * 0.85;
      drawButton(ctx, rect, label, options);
      ctx.restore();
    } else if (releaseProgress < 1) {
      ctx.save();
      const cx = rect.x + rect.w / 2;
      const cy = rect.y + rect.h / 2;
      const scale = 0.95 + 0.05 * easeOutCubic(releaseProgress);
      const alpha = 0.85 + 0.15 * easeOutCubic(releaseProgress);
      ctx.translate(cx, cy);
      ctx.scale(scale, scale);
      ctx.translate(-cx, -cy);
      ctx.globalAlpha = (ctx.globalAlpha ?? 1) * alpha;
      drawButton(ctx, rect, label, options);
      ctx.restore();
    } else if (hoverT > 0) {
      const ease = easeOutCubic(hoverT);
      // Glow layer behind button (skip for lobby variant)
      if (options?.variant !== "lobby") {
        const glowColor = options?.stroke ?? "#5c8cd4";
        ctx.save();
        ctx.shadowColor = glowColor;
        ctx.shadowBlur = 14 * ease;
        ctx.fillStyle = glowColor;
        ctx.globalAlpha = 0.3 * ease;
        roundedRectPath(ctx, rect.x - 2, rect.y - 2, rect.w + 4, rect.h + 4, 11);
        ctx.fill();
        ctx.restore();
      }
      // Button scaled up smoothly
      ctx.save();
      const scale = 1 + 0.035 * ease;
      const cx = rect.x + rect.w / 2;
      const cy = rect.y + rect.h / 2;
      ctx.translate(cx, cy);
      ctx.scale(scale, scale);
      ctx.translate(-cx, -cy);
      drawButton(ctx, rect, label, options);
      ctx.restore();
    } else {
      drawButton(ctx, rect, label, options);
    }
  }

  private pageNavFontPx(tier: "compact" | "menu"): number {
    if (tier === "compact") {
      return 9;
    }
    return isMobileViewport() ? 12 : 10;
  }

  private drawPageNavGlyph(rect: Rect, label: string, color: string, tier: "compact" | "menu"): void {
    const ctx = this.ctx;
    ctx.fillStyle = color;
    ctx.font = mFont("bold", this.pageNavFontPx(tier));
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2);
  }

  private drawMenuPageNavButton(
    rect: Rect,
    label: string,
    style: { fill: string; stroke: string },
    tier: "compact" | "menu" = "menu"
  ): void {
    this.drawBtn(rect, label, {
      ...style,
      font: mFont("bold", this.pageNavFontPx(tier)),
      preserveFont: true
    });
  }

  /** Shared circle background + border for all circle buttons. Returns cx, cy, r. */
  private drawCircleBg(rect: Rect): { cx: number; cy: number; r: number } {
    const ctx = this.ctx;
    const cx = rect.x + rect.w / 2;
    const cy = rect.y + rect.h / 2;
    const r = rect.w / 2;
    ctx.fillStyle = "rgba(20,36,54,0.85)";
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(108,200,255,0.6)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    return { cx, cy, r };
  }

  /** Draw a circular close (X) button. */
  private drawCircleCloseButton(rect: Rect): void {
    const { cx, cy, r } = this.drawCircleBg(rect);
    if (this.uiCloseIcon) {
      const iconSize = r;
      this.ctx.drawImage(this.uiCloseIcon, cx - iconSize / 2, cy - iconSize / 2, iconSize, iconSize);
    } else {
      const ctx = this.ctx;
      ctx.strokeStyle = "#e9f7ff";
      ctx.lineWidth = 2;
      const xOff = r * 0.42;
      ctx.beginPath();
      ctx.moveTo(cx - xOff, cy - xOff);
      ctx.lineTo(cx + xOff, cy + xOff);
      ctx.moveTo(cx + xOff, cy - xOff);
      ctx.lineTo(cx - xOff, cy + xOff);
      ctx.stroke();
    }
  }

  /** Draw a circular pause button (two vertical bars inside a circle). */
  private drawCirclePauseButton(rect: Rect): void {
    const { cx, cy, r } = this.drawCircleBg(rect);
    const barW = r * 0.22;
    const barH = r * 0.7;
    const barGap = r * 0.24;
    this.ctx.fillStyle = "#e9f7ff";
    this.ctx.fillRect(cx - barGap - barW, cy - barH / 2, barW, barH);
    this.ctx.fillRect(cx + barGap, cy - barH / 2, barW, barH);
  }

  /** Draw a circular pencil (edit) button. */
  private drawCirclePencilButton(rect: Rect): void {
    const { cx, cy, r } = this.drawCircleBg(rect);
    const ctx = this.ctx;
    const editIcon = this.uiShirtIcon ?? this.uiWriteIcon;
    if (editIcon) {
      const iconSize = rect.w * 0.6;
      ctx.drawImage(editIcon, cx - iconSize / 2, cy - iconSize / 2, iconSize, iconSize);
    } else {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(-Math.PI / 4);
      ctx.strokeStyle = "#e9f7ff";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      const bodyH = r * 0.7;
      const bodyW = r * 0.28;
      const tipH = r * 0.3;
      ctx.beginPath();
      ctx.moveTo(-bodyW, -bodyH + tipH);
      ctx.lineTo(-bodyW, bodyH * 0.3);
      ctx.lineTo(bodyW, bodyH * 0.3);
      ctx.lineTo(bodyW, -bodyH + tipH);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-bodyW, -bodyH + tipH);
      ctx.lineTo(0, -bodyH);
      ctx.lineTo(bodyW, -bodyH + tipH);
      ctx.stroke();
      ctx.restore();
    }
  }

  /** Draw a circular arrow button. dir=-1 for left, dir=1 for right. */
  private drawCircleArrowButton(rect: Rect, dir: -1 | 1): void {
    const { cx, cy, r } = this.drawCircleBg(rect);
    const ctx = this.ctx;
    const aH = r * 0.5;
    const aW = r * 0.33;
    const nudge = dir * 1;
    ctx.strokeStyle = "#e9f7ff";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(cx + nudge - dir * aW, cy - aH);
    ctx.lineTo(cx + nudge + dir * aW, cy);
    ctx.lineTo(cx + nudge - dir * aW, cy + aH);
    ctx.stroke();
  }

  private drawNotificationPageNavButton(
    rect: Rect,
    label: string,
    desktop: boolean,
    panelStyle: { fill: string; stroke: string; radius?: number },
    glyphColor: string
  ): void {
    const ctx = this.ctx;
    if (desktop) {
      this.drawNotificationPanel(rect, panelStyle);
    } else {
      ctx.fillStyle = panelStyle.fill;
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
    }
    this.drawPageNavGlyph(rect, label, glyphColor, "compact");
  }

  private drawMenuMessageBubble(message: string, phase: GamePhase): void {
    const ctx = this.ctx;
    const messageText = truncName(message, 32);
    const bubbleH = 22;
    const bubbleY = phase === "PHONE" ? 84 : 74;

    ctx.save();
    ctx.font = mFont("bold", 5);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const textW = this.measureTextWidth(messageText);
    const bubbleW = resolveMenuMessageBubbleWidth(textW);
    const bubbleX = CANVAS_WIDTH / 2 - bubbleW / 2;

    roundedRectPath(ctx, bubbleX, bubbleY, bubbleW, bubbleH, 8);
    ctx.fillStyle = "rgba(8,14,26,0.9)";
    ctx.fill();
    roundedRectPath(ctx, bubbleX, bubbleY, bubbleW, bubbleH, 8);
    ctx.strokeStyle = "#74b9ff";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = "#f8fbff";
    ctx.fillText(messageText, bubbleX + bubbleW / 2, bubbleY + bubbleH / 2 + 0.5);
    ctx.restore();
  }

  /** Draw an animated hourglass (drain + flip cycle) at center (cx, cy). */
  private drawHourglass(cx: number, cy: number, size: number, color: string): void {
    const ctx = this.ctx;
    const cycleDuration = 2400;
    const drainDuration = 2000;
    const cycleT = this.nowMs() % cycleDuration;
    const fallback = (): void => {
      ctx.fillStyle = color;
      ctx.font = `bold ${Math.round(size * 0.5)}px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("\u29D6", cx, cy);
    };
    if (cycleT < drainDuration) {
      const emptyPct = 1 - cycleT / drainDuration;
      const timerId = this.assetManager?.resolveTimerIconId(emptyPct) ?? "ui_timer";
      this.drawSprite(timerId as SpriteId, cx - size / 2, cy - size / 2, size, size, fallback);
    } else {
      const flipT = (cycleT - drainDuration) / (cycleDuration - drainDuration);
      const angle = flipT * Math.PI;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle);
      const timerId = this.assetManager?.resolveTimerIconId(0) ?? "ui_timer";
      this.drawSprite(timerId as SpriteId, -size / 2, -size / 2, size, size, fallback);
      ctx.restore();
    }
  }

  // â"€â"€ Menu animation helpers â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  /**
   * Returns a 0â†'1 stagger-entrance progress for the i-th element on screen,
   * based on when the current menu phase was entered.
   * Each element is delayed by `delayMs` from the previous one.
   */
  private staggerAlpha(index: number, delayMs = 80, durationMs = 300): number {
    const elapsed = this.nowMs() - this.menuPhaseEnterMs;
    const itemStart = index * delayMs;
    if (elapsed < itemStart) return 0;
    return Math.min(1, (elapsed - itemStart) / durationMs);
  }

  private leaderboardStaggerAlpha(index: number, delayMs = 80, durationMs = 300): number {
    const elapsed = (this.nowSec() - this.leaderboardScreenEnterTime) * 1000;
    const itemStart = index * delayMs;
    if (elapsed < itemStart) return 0;
    return Math.min(1, (elapsed - itemStart) / durationMs);
  }

  private resolveLeaderboardScroll(config: LeaderboardScrollConfig): number {
    return this.leaderboardScrollController.resolve(
      config,
      this.leaderboardScrollGesture,
      this.nowMs()
    );
  }

  private drawLeaderboardTab(rect: Rect, label: string, active: boolean, accent: string, fontPx = 6): void {
    const ctx = this.ctx;
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 8);
    ctx.fillStyle = active ? "rgba(18,34,58,0.95)" : "rgba(10,18,34,0.72)";
    ctx.fill();
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 8);
    ctx.strokeStyle = active ? accent : "rgba(92,140,212,0.45)";
    ctx.lineWidth = active ? 1.4 : 1;
    ctx.stroke();
    ctx.fillStyle = active ? "#e9f3ff" : "#8fb0d8";
    ctx.font = mFont("bold", fontPx);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2 + 0.5);
  }

  private trimTextToWidth(text: string, maxWidth: number): string {
    if (text.length === 0 || maxWidth <= 0) {
      return "";
    }
    if (this.measureTextWidth(text) <= maxWidth) {
      return text;
    }
    let trimmed = text;
    while (trimmed.length > 1) {
      const candidate = `${trimmed.slice(0, -1)}...`;
      if (this.measureTextWidth(candidate) <= maxWidth) {
        return candidate;
      }
      trimmed = trimmed.slice(0, -1);
    }
    return "...";
  }

  private wrapTextToWidth(text: string, maxWidth: number, maxLines: number): string[] {
    if (text.trim().length === 0 || maxLines <= 0) {
      return [];
    }
    const words = text.trim().split(/\s+/);
    const lines: string[] = [];
    let line = "";
    let wordIndex = 0;

    while (wordIndex < words.length) {
      const word = words[wordIndex];
      const candidate = line.length > 0 ? `${line} ${word}` : word;
      if (this.measureTextWidth(candidate) <= maxWidth) {
        line = candidate;
        wordIndex += 1;
        continue;
      }

      if (line.length === 0) {
        line = this.trimTextToWidth(word, maxWidth);
        wordIndex += 1;
      }

      lines.push(line);
      line = "";
      if (lines.length === maxLines) {
        const remainder = words.slice(wordIndex).join(" ");
        if (remainder.length > 0) {
          lines[maxLines - 1] = this.trimTextToWidth(`${lines[maxLines - 1]} ${remainder}`.trim(), maxWidth);
        }
        return lines;
      }
    }

    if (line.length > 0 && lines.length < maxLines) {
      lines.push(line);
    }

    if (wordIndex < words.length && lines.length > 0) {
      const remainder = words.slice(wordIndex).join(" ");
      const lastIndex = Math.min(lines.length, maxLines) - 1;
      const prefix = lines[lastIndex];
      const merged = prefix.length > 0 ? `${prefix} ${remainder}` : remainder;
      lines[lastIndex] = this.trimTextToWidth(merged, maxWidth);
    }

    if (lines.length > maxLines) {
      return lines.slice(0, maxLines);
    }
    return lines;
  }

  private drawNotificationPanel(
    rect: Rect,
    options: { fill: string; stroke?: string; accent?: string; radius?: number; lineWidth?: number }
  ): void {
    const ctx = this.ctx;
    const radius = options.radius ?? 6;
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, radius);
    ctx.fillStyle = options.fill;
    ctx.fill();
    if (options.stroke != null) {
      roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, radius);
      ctx.strokeStyle = options.stroke;
      ctx.lineWidth = options.lineWidth ?? 1;
      ctx.stroke();
    }
    if (options.accent != null) {
      ctx.fillStyle = options.accent;
      ctx.fillRect(rect.x, rect.y, 3, rect.h);
    }
  }

  private drawNotificationChip(rect: Rect, label: string, accent: string, fontPx: number): void {
    const ctx = this.ctx;
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 5);
    ctx.fillStyle = "rgba(11, 22, 42, 0.92)";
    ctx.fill();
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 5);
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = accent;
    ctx.font = `bold ${fontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2 + 0.5);
  }

  private drawNotificationEmptyState(
    bounds: Rect,
    title: string,
    subtitle: string,
    accent: string,
    metrics: PhoneMetrics
  ): void {
    const ctx = this.ctx;
    const cardW = Math.min(bounds.w - 8, metrics.desktop ? 248 : 232);
    const targetH = metrics.desktop ? 56 : 44;
    const cardH = Math.min(targetH, Math.max(36, bounds.h - 8));
    const cardX = bounds.x + (bounds.w - cardW) / 2;
    const cardY = bounds.y + Math.max(4, (bounds.h - cardH) / 2);
    this.drawNotificationPanel(
      { x: cardX, y: cardY, w: cardW, h: cardH },
      {
        fill: metrics.desktop ? "rgba(11, 20, 38, 0.96)" : "rgba(10, 18, 34, 0.88)",
        stroke: metrics.desktop ? "rgba(122,164,230,0.35)" : "rgba(92,140,212,0.28)",
        accent,
        radius: metrics.cardRadius
      }
    );
    ctx.fillStyle = "#e7f2ff";
    ctx.font = `bold ${metrics.labelFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    const titleLines = this.wrapTextToWidth(title, cardW - 18, metrics.desktop ? 2 : 1);
    titleLines.forEach((line, index) => {
      ctx.fillText(line, cardX + cardW / 2, cardY + 9 + index * (metrics.labelFontPx + 2));
    });
    ctx.fillStyle = "#7ea0c7";
    ctx.font = `${metrics.helperFontPx}px ${GAME_FONT}`;
    const subtitleY = cardY + Math.min(cardH - 12, 18 + titleLines.length * (metrics.labelFontPx + 2));
    ctx.fillText(subtitle, cardX + cardW / 2, subtitleY);
  }

  private leaderboardTierColor(tier: RankedTier): string {
    switch (tier) {
      case "Diamond": return "#c8a0ff";
      case "Gold": return "#ffd36a";
      case "Silver": return "#c9d5eb";
      default: return "#e0c8b0";
    }
  }

  /**
   * Draws a button with stagger-entrance animation (slide up + fade in).
   * Wraps drawBtn to apply the entrance effect.
   */
  private drawBtnStagger(
    rect: Rect, label: string, index: number,
    options?: Parameters<typeof drawButton>[3]
  ): void {
    const progress = this.staggerAlpha(index);
    if (progress <= 0) return;
    if (progress >= 1) {
      this.drawBtn(rect, label, options);
      return;
    }
    const ctx = this.ctx;
    ctx.save();
    const ease = easeOutCubic(progress);
    ctx.globalAlpha = (ctx.globalAlpha ?? 1) * ease;
    ctx.translate(0, 20 * (1 - ease));
    this.drawBtn(rect, label, options);
    ctx.restore();
  }

  /**
   * Draws a pulsing glow behind a rect (for CTA buttons like BATTLE, CLAIM).
   */
  private drawGlowPulse(rect: Rect, color: string, maxBlur = 18): void {
    const ctx = this.ctx;
    const pulse = 0.3 + 0.7 * Math.sin(this.nowMs() / 600);
    ctx.save();
    ctx.shadowColor = color;
    ctx.shadowBlur = maxBlur * pulse;
    ctx.globalAlpha = (ctx.globalAlpha ?? 1) * (0.4 + 0.5 * pulse);
    roundedRectPath(ctx, rect.x - 4, rect.y - 4, rect.w + 8, rect.h + 8, 10);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.restore();
  }

  /**
   * Returns the slide-in X offset for the current settings page transition.
   * Positive = sliding from right, negative = sliding from left.
   */
  private settingsSlideOffset(): number {
    const elapsed = this.nowMs() - this.settingsPageChangeMs;
    const duration = 280;
    if (elapsed >= duration) return 0;
    const progress = easeOutCubic(elapsed / duration);
    return (1 - progress) * 60 * this.settingsPageDir;
  }

  /**
   * Returns a bounce scale factor (1.0 = normal) for a value-change animation.
   */
  private valueBounceScale(changeMs: number, duration = 400): number {
    const elapsed = this.nowMs() - changeMs;
    if (elapsed >= duration || changeMs <= 0) return 1;
    const t = elapsed / duration;
    // Quick scale up then settle: peaks at ~1.3 then back to 1.0
    return 1 + 0.3 * Math.sin(t * Math.PI) * (1 - t);
  }

  attach(parent: HTMLElement): void {
    const shell = document.createElement("div");
    shell.className = "game-shell";
    this.shell = shell;
    this.canvas.classList.add("game-canvas");
    this.sideFxLeftCanvas = null;
    this.sideFxRightCanvas = null;
    this.sideFxLeftCtx = null;
    this.sideFxRightCtx = null;

    this.canvasWrapper.appendChild(this.canvas);
    shell.appendChild(this.canvasWrapper);
    this.shellRaveFx = new ShellRaveBorderFx(shell, this.canvasWrapper);
    parent.appendChild(shell);
  }

  private loadTitleLogo(): void {
    if (this.titleLogoReady || this.titleLogoFailed) {
      return;
    }
    const candidatePaths = ["/assets/icons/block-party-logo.png", "/assets/logo/title_logo.png", "/assets/ui/title_logo.png"];
    const tryLoad = (index: number): void => {
      if (index >= candidatePaths.length) {
        this.titleLogoFailed = true;
        this.titleLogo = null;
        return;
      }
      const img = new Image();
      img.onload = () => {
        this.titleLogo = img;
        this.titleLogoReady = true;
        this.titleLogoFailed = false;
      };
      img.onerror = () => {
        tryLoad(index + 1);
      };
      img.src = candidatePaths[index] ?? "";
    };
    tryLoad(0);
  }

  private loadPowerupIcons(): void {
    const entries = Object.entries(POWERUP_ICON_PATHS) as Array<[PowerupKind, string]>;
    for (const [kind, path] of entries) {
      if (this.powerupIconImages[kind] != null || this.powerupIconFailed.has(kind)) {
        continue;
      }
      const img = new Image();
      img.onload = () => {
        this.powerupIconImages[kind] = img;
      };
      img.onerror = () => {
        this.powerupIconFailed.add(kind);
      };
      img.src = path;
    }
  }

  private loadUiImages(): void {
    const loadImg = (path: string, onLoad: (img: HTMLImageElement) => void): void => {
      const img = new Image();
      img.onload = () => onLoad(img);
      img.onerror = () => {}; // fallback drawing handles missing images
      img.src = path;
    };
    loadImg("/assets/raw/dj_player_1.png", (img) => { this.turntableLeftImage = img; this.turntableRightImage = img; });
    loadImg("/assets/raw/dj_player_2.png", (img) => { this.turntableLeftActiveImage = img; this.turntableRightActiveImage = img; });
    loadImg("/assets/raw/dj_mixer.png", (img) => { this.mixerImage = img; });
    loadImg("/assets/icons/settings-flower.svg", (img) => { this.settingsIconImage = img; });
    loadImg("/assets/icons/player-person.svg", (img) => { this.playerIconImage = img; });
    loadImg("/assets/icons/ui-close.svg", (img) => { this.uiCloseIcon = img; });
    loadImg("/assets/icons/ui-settings.svg", (img) => { this.uiSettingsIcon = img; });
    loadImg("/assets/icons/ui-padlock.svg", (img) => { this.uiPadlockIcon = img; });
    loadImg("/assets/icons/ui-write.svg", (img) => { this.uiWriteIcon = img; });
    loadImg("/assets/icons/ui-contact.svg", (img) => { this.uiContactIcon = img; });
    loadImg("/assets/icons/ui-shirt.svg", (img) => { this.uiShirtIcon = img; });
    loadImg("/assets/icons/ui-refresh.svg", (img) => { this.uiRefreshIcon = img; });
    loadImg("/assets/icons/heart-life.svg", (img) => { this.heartLifeImage = img; });
    loadImg("/assets/icons/heart-life-premium.svg", (img) => { this.heartLifePremiumImage = img; });
    loadImg("/assets/icons/coin-icon.svg", (img) => { this.coinIconImage = img; });
    loadImg("/assets/icons/cash-icon.svg", (img) => { this.cashIconImage = img; });
    for (const tabKey of ["messages", "friends", "highscores", "settings", "wallet", "travel"]) {
      loadImg(`/assets/icons/phone-tab-${tabKey}.png`, (img) => { this.phoneTabIcons[tabKey] = img; });
    }
    loadImg("/assets/Backgrounds/game-bg-cyberpunk-arena.webp", (img) => { this.bgImage = img; });
    loadImg("/assets/raw/wipe_b.png", (img) => { this.gemImage = img; });
    loadImg("/assets/icons/star-score.svg", (img) => { this.starScoreImage = img; });
    loadImg("/assets/icons/check-complete.svg", (img) => { this.checkCompleteImage = img; });
    loadImg("/assets/branding/logo_beaststudiolab_head.svg", (img) => { this.aboutHeadLogoImage = img; });
    loadImg("/assets/branding/logo_beaststudiolab_wordmark.svg", (img) => { this.aboutWordmarkLogoImage = img; });
    loadImg("/assets/icons/gem-shop-door.png", (img) => { this.gemShopDoorImage = img; });
    loadImg("/assets/icons/crate-closed.png", (img) => { this.crateClosedImage = img; });
    loadImg("/assets/icons/crate-open.png", (img) => { this.crateOpenImage = img; });
    loadImg("/assets/icons/gem-pack-5.png", (img) => { this.gemPackImages[0] = img; });
    loadImg("/assets/icons/gem-pack-25.png", (img) => { this.gemPackImages[1] = img; });
    loadImg("/assets/icons/gem-pack-50.png", (img) => { this.gemPackImages[2] = img; });
    loadImg("/assets/icons/gem-pack-100.png", (img) => { this.gemPackImages[3] = img; });
    loadImg("/assets/icons/regular-vinyl.png", (img) => { this.rankVinylImages["Regular"] = img; });
    loadImg("/assets/icons/silver-vinyl.png", (img) => { this.rankVinylImages["Silver"] = img; });
    loadImg("/assets/icons/gold-vinyl.png", (img) => { this.rankVinylImages["Gold"] = img; });
    loadImg("/assets/icons/diamond-vinyl.png", (img) => { this.rankVinylImages["Diamond"] = img; });
    const soloLogoEntries = Object.entries(SOLO_SELECTOR_LOGO_PATHS) as Array<[SoloMode, string]>;
    for (const [mode, path] of soloLogoEntries) {
      loadImg(path, (img) => { this.soloSelectorLogoImages[mode] = img; });
    }
    const soloBackgroundEntries = Object.entries(SOLO_MODE_BACKGROUND_PATHS) as Array<[SoloMode, string]>;
    for (const [mode, path] of soloBackgroundEntries) {
      loadImg(path, (img) => { this.soloModeBackgroundImages[mode] = img; });
    }
    loadImg("/assets/raw/city.webp", (img) => { this.titleBgImage = img; });
    loadImg("/assets/icons/loading-city-bg.png", (img) => { this.loadingCityBgImage = img; });
    loadImg("/assets/raw/battle_building.webp", (img) => { this.buildingImages.battle = img; });
    loadImg("/assets/raw/shop_building.webp", (img) => { this.buildingImages.shop = img; });
    loadImg("/assets/raw/solo_building.webp", (img) => { this.buildingImages.solo = img; });
    loadImg("/assets/raw/lobby_building.webp", (img) => { this.buildingImages.lobby = img; });
    loadImg("/assets/raw/apartment_building.webp", (img) => { this.buildingImages.apartment = img; });
    const walkerUrls = [walkerImg1Url, walkerImg2Url, walkerImg3Url];
    for (let wi = 0; wi < 3; wi++) {
      loadImg(walkerUrls[wi]!, (img) => { this.walkingCharImages[wi] = img; });
    }
    loadImg("/assets/raw/diner_bg.webp", (img) => { this.dinerBgImage = img; });
    loadImg("/assets/raw/shop_bg.webp", (img) => { this.shopBgImage = img; });
    loadImg("/assets/raw/gem_shop_bg.webp", (img) => { this.gemShopBgImage = img; });
    loadImg("/assets/raw/backstage_bg.webp", (img) => { this.backstageBgImage = img; });
    loadImg("/assets/raw/apartment_bg.webp", (img) => { this.apartmentBgImage = img; });
    loadImg("/assets/raw/worldmap.webp", (img) => { this.worldMapImage = img; });
    loadImg("/assets/raw/solo-select-bg.webp", (img) => { this.soloSelectBgImage = img; });
    loadImg("/assets/raw/cloud_1.png", (img) => { this.cloudImage = img; });
    loadImg("/assets/raw/mobile.png", (img) => { this.phoneImage = img; });
    loadImg("/assets/icons/rafsby-portrait.png", (img) => { this.rafsbyPortrait = img; });
    loadImg("/assets/icons/rafsby-phone.png", (img) => { this.rafsbyPhoneImage = img; });
  }

  private loadCharacterImage(path: string): void {
    if (
      this.characterImages.has(path) ||
      this.characterImageFailed.has(path) ||
      this.characterImageLoading.has(path)
    ) return;
    if (this.characterImageLoading.size >= CanvasRenderer.MAX_CONCURRENT_CHAR_LOADS) return;
    this.characterImageLoading.add(path);
    const img = new Image();
    img.onload = () => {
      this.characterImages.set(path, img);
      this.characterImageLoading.delete(path);
    };
    img.onerror = () => {
      this.characterImageFailed.add(path);
      this.characterImageLoading.delete(path);
    };
    img.src = path;
  }

  private drawCharacterPortrait(
    path: string,
    x: number,
    y: number,
    w: number,
    h: number,
    verticalAlign: "center" | "bottom" = "center"
  ): void {
    const ctx = this.ctx;
    // Trigger on-demand loading if not yet loaded
    this.loadCharacterImage(path);
    const img = this.characterImages.get(path);
    if (img != null) {
      const drawRect = resolvePortraitContainRect(
        img.naturalWidth || img.width || 1,
        img.naturalHeight || img.height || 1,
        { x, y, w, h },
        verticalAlign
      );
      ctx.save();
      ctx.beginPath();
      roundedRectPath(ctx, x, y, w, h, 4);
      ctx.clip();
      ctx.drawImage(img, drawRect.x, drawRect.y, drawRect.w, drawRect.h);
      ctx.restore();
    } else if (!this.characterImageFailed.has(path)) {
      // Spinner while loading
      const cx = x + w / 2;
      const cy = y + h / 2;
      const r = Math.min(w, h) * 0.2;
      const angle = (performance.now() / 600) % (Math.PI * 2);
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, angle, angle + Math.PI * 1.4);
      ctx.strokeStyle = "rgba(255,255,255,0.35)";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.stroke();
      ctx.restore();
    }
  }

  /** Draw a character portrait clipped to a circle (like the top-bar profile pic). */
  private drawCircularPortrait(
    path: string,
    x: number,
    y: number,
    size: number,
  ): void {
    const ctx = this.ctx;
    this.loadCharacterImage(path);
    const img = this.characterImages.get(path);
    if (img != null) {
      const drawRect = resolvePortraitContainRect(
        img.naturalWidth || img.width || 1,
        img.naturalHeight || img.height || 1,
        { x, y, w: size, h: size },
        "center"
      );
      ctx.save();
      ctx.beginPath();
      ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(img, drawRect.x, drawRect.y, drawRect.w, drawRect.h);
      ctx.restore();
    }
  }

  private resolveParticleDensity(mode: PerformanceSettings["particleMode"]): number {
    if (mode === "OFF") return 0;
    if (mode === "REDUCED") return 0.5;
    return 1;
  }

  private scaledParticleCount(baseCount: number, minimumWhenActive = 1): number {
    if (this.particleDensity <= 0) {
      return 0;
    }
    const scaled = Math.round(baseCount * this.particleDensity);
    return Math.max(minimumWhenActive, scaled);
  }

  private emitMatchBurst(x: number, y: number, color: string, baseCount: number): void {
    const count = this.scaledParticleCount(baseCount);
    if (count <= 0) {
      return;
    }
    emitMatchParticles(this.particles, x, y, color, count);
  }

  private emitRainbowBurst(x: number, y: number, baseCount: number): void {
    const count = this.scaledParticleCount(baseCount);
    if (count <= 0) {
      return;
    }
    emitRainbowStarParticles(this.particles, x, y, count);
  }

  private emitDirectionalBurst(
    x: number,
    y: number,
    colors: string[],
    baseCount: number,
    options?: Parameters<typeof emitDirectionalParticles>[5]
  ): void {
    const count = this.scaledParticleCount(baseCount, 0);
    if (count <= 0) {
      return;
    }
    emitDirectionalParticles(this.particles, x, y, colors, count, options);
  }

  private emitGameOverBurst(canvasWidth: number, baseCount: number): void {
    const count = this.scaledParticleCount(baseCount, 0);
    if (count <= 0) {
      return;
    }
    emitGameOverParticles(this.particles, canvasWidth, count);
  }

  private queueTitleGemBurst(model: RenderModel, claimRect: Rect | undefined, gemRect: Rect | undefined, coinRect?: Rect | undefined): void {
    const isGemBurstPhase = model.phase === "TITLE" || model.phase === "GEM_SHOP";
    if (!isGemBurstPhase) {
      this.titleGemBursts.length = 0;
      this.lastDailyLoginClaimRect = null;
      return;
    }

    if (model.phase === "TITLE") {
      if (model.dailyLoginModalOpen && claimRect != null) {
        this.lastDailyLoginClaimRect = { ...claimRect };
      } else if (!model.dailyLoginModalOpen && !model.gemBurstPending) {
        this.lastDailyLoginClaimRect = null;
      }
    }

    if (!model.gemBurstPending || gemRect == null) {
      return;
    }

    // In GEM_SHOP, burst from the purchased pack image
    let burstSourceRect: Rect | undefined = claimRect ?? (
      model.phase === "TITLE" && model.dailyLoginModalOpen
        ? this.lastDailyLoginClaimRect ?? undefined
        : undefined
    );
    if (model.phase === "GEM_SHOP" && model.gemBurstPackIdx >= 0) {
      burstSourceRect = this.gemPackImageRects[model.gemBurstPackIdx] ?? undefined;
    }

    const sourceX = burstSourceRect?.x != null
      ? burstSourceRect.x + burstSourceRect.w / 2
      : CANVAS_WIDTH / 2;
    const sourceY = burstSourceRect?.y != null
      ? burstSourceRect.y + burstSourceRect.h / 2
      : model.phase === "GEM_SHOP"
        ? 400
        : 354;

    // Target icon centers (not full hotspot which includes text)
    const gemTarget = this.headerGemIconRect ?? gemRect;
    const coinTarget = this.headerCoinIconRect ?? coinRect;

    // Gem burst
    this.titleGemBursts.push({
      startMs: this.nowMs(),
      durationMs: 900,
      count: 12,
      fromX: sourceX,
      fromY: sourceY,
      toX: gemTarget.x + gemTarget.w / 2,
      toY: gemTarget.y + gemTarget.h / 2,
      kind: "gem"
    });

    // Coin burst (only on title, not gem shop — gem shop only gains gems)
    if (coinTarget != null && model.phase !== "GEM_SHOP") {
      this.titleGemBursts.push({
        startMs: this.nowMs() + 80,
        durationMs: 900,
        count: 12,
        fromX: sourceX,
        fromY: sourceY,
        toX: coinTarget.x + coinTarget.w / 2,
        toY: coinTarget.y + coinTarget.h / 2,
        kind: "coin"
      });
    }
  }

  private drawTitleGemBursts(model: RenderModel): void {
    if ((model.phase !== "TITLE" && model.phase !== "GEM_SHOP") || this.titleGemBursts.length === 0) {
      return;
    }
    const ctx = this.ctx;
    const now = this.nowMs();
    this.titleGemBursts = this.titleGemBursts.filter((burst) => now - burst.startMs <= burst.durationMs + 220);

    for (const burst of this.titleGemBursts) {
      const progress = clamp01((now - burst.startMs) / burst.durationMs);
      const isCoin = burst.kind === "coin";
      const size = 14;
      for (let i = 0; i < burst.count; i += 1) {
        // Stagger each particle's start
        const delay = (i / burst.count) * 0.25;
        const t = clamp01((progress - delay) / (1 - delay));
        if (t <= 0) continue;
        const et = easeOutCubic(t);
        // Arc path: spread out then converge with a curve
        const spread = Math.sin(et * Math.PI) * (30 + (i % 5) * 8);
        const arcDir = (i % 2 === 0 ? 1 : -1) * (1 + (i % 3) * 0.4);
        const x = lerp(burst.fromX, burst.toX, et) + spread * arcDir;
        const arcY = Math.sin(et * Math.PI) * -40 * (1 + (i % 3) * 0.3);
        const y = lerp(burst.fromY, burst.toY, et) + arcY;
        if (isCoin) {
          this.drawCoinIcon(x - size / 2, y - size / 2, size);
        } else {
          this.drawGemIcon(x - size / 2, y - size / 2, size);
        }
      }
      if (progress > 0.78) {
        const sparkle = 1 - clamp01((progress - 0.78) / 0.22);
        ctx.save();
        ctx.globalAlpha = sparkle;
        ctx.fillStyle = isCoin ? "rgba(255,215,0,0.9)" : "rgba(164,240,255,0.9)";
        ctx.beginPath();
        ctx.arc(burst.toX, burst.toY, 8 + (1 - sparkle) * 10, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }
  }

  private drawSprite(
    id: SpriteId,
    x: number,
    y: number,
    w: number,
    h: number,
    fallback: () => void
  ): void {
    if (this.assetManager == null) {
      fallback();
      return;
    }
    this.assetManager.drawSpriteOrFallback(this.ctx, id, x, y, w, h, fallback);
  }

  private drawCrateSprite(x: number, y: number, size: number): void {
    const ctx = this.ctx;
    this.drawSprite("crate", x, y, size, size, () => {
      ctx.fillStyle = "#7d5538";
      ctx.fillRect(x, y, size, size);
      ctx.fillStyle = "#d8b18a";
      const stripeInsetX = Math.max(3, Math.round(size * 0.1));
      const stripeInsetY = Math.max(3, Math.round(size * 0.12));
      const stripeW = Math.max(4, size - stripeInsetX * 2);
      const stripeH = Math.max(2, Math.round(size * 0.07));
      const stripeGap = Math.max(3, Math.round(size * 0.14));
      ctx.fillRect(x + stripeInsetX, y + stripeInsetY, stripeW, stripeH);
      ctx.fillRect(x + stripeInsetX, y + stripeInsetY + stripeGap, stripeW, stripeH);
    });
  }

  private drawSteelSprite(x: number, y: number, size: number): void {
    const ctx = this.ctx;
    this.drawSprite("steel", x, y, size, size, () => {
      ctx.fillStyle = "#6c778b";
      ctx.fillRect(x, y, size, size);
      ctx.fillStyle = "#bac5d8";
      ctx.fillRect(x + 2, y + 2, Math.max(2, size - 6), 2);
    });
  }

  private drawTileSprite(
    tile: TileData,
    x: number,
    y: number,
    size: number,
    angleRad = 0,
    scaleX = 1,
    scaleY = 1
  ): void {
    const ctx = this.ctx;
    const spriteId = tile.special === "NONE" ? tileColorSpriteId(tile.color) : specialSpriteId(tile.special);

    const drawTileContent = (drawX: number, drawY: number): void => {
      this.drawSprite(spriteId, drawX, drawY, size, size, () => {
        const color = TILE_COLORS[tile.color % TILE_COLORS.length];
        ctx.fillStyle = color;
        ctx.fillRect(drawX, drawY, size, size);

        // Add subtle pattern based on color index for better distinction
        const colorIndex = tile.color % TILE_COLORS.length;
        ctx.strokeStyle = "rgba(255,255,255,0.15)";
        ctx.lineWidth = 1;

        // Different patterns for different color groups
        if (colorIndex % 3 === 0) {
          // Diagonal lines for every 3rd color
          ctx.beginPath();
          ctx.moveTo(drawX + size * 0.3, drawY);
          ctx.lineTo(drawX, drawY + size * 0.3);
          ctx.moveTo(drawX + size * 0.7, drawY);
          ctx.lineTo(drawX, drawY + size * 0.7);
          ctx.moveTo(drawX + size, drawY + size * 0.3);
          ctx.lineTo(drawX + size * 0.3, drawY + size);
          ctx.moveTo(drawX + size, drawY + size * 0.7);
          ctx.lineTo(drawX + size * 0.7, drawY + size);
          ctx.stroke();
        } else if (colorIndex % 3 === 1) {
          // Dots for every 3rd color
          ctx.fillStyle = "rgba(255,255,255,0.2)";
          const dotSize = 2;
          ctx.fillRect(drawX + size * 0.25 - dotSize/2, drawY + size * 0.25 - dotSize/2, dotSize, dotSize);
          ctx.fillRect(drawX + size * 0.75 - dotSize/2, drawY + size * 0.25 - dotSize/2, dotSize, dotSize);
          ctx.fillRect(drawX + size * 0.25 - dotSize/2, drawY + size * 0.75 - dotSize/2, dotSize, dotSize);
          ctx.fillRect(drawX + size * 0.75 - dotSize/2, drawY + size * 0.75 - dotSize/2, dotSize, dotSize);
        }
        // Third group gets no pattern (solid color)

        // Main highlight shine
        ctx.fillStyle = "rgba(255,255,255,0.35)";
        ctx.fillRect(drawX + 2, drawY + 2, Math.max(2, size - 8), 2);

        if (tile.special !== "NONE") {
          ctx.fillStyle = "#0a0f1f";
          ctx.font = mFont("bold", 6);
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          let text = "S";
          if (tile.special === "BOMB_3") text = "B3";
          if (tile.special === "BOMB_4") text = "B4";
          if (tile.special === "BOMB_5") text = "B5";
          if (tile.special === "WIPE") text = "W";
          ctx.fillText(text, drawX + size / 2, drawY + size / 2 + 1);
        }
      });

      if (tile.special === "WIPE") {
        const now = this.nowSec();
        const pulse = 0.5 + 0.5 * Math.sin(now * 6 + drawX * 0.06 + drawY * 0.04);
        ctx.strokeStyle = `rgba(220,246,255,${(0.3 + pulse * 0.45).toFixed(3)})`;
        ctx.lineWidth = 1;
        ctx.strokeRect(drawX + 0.75, drawY + 0.75, size - 1.5, size - 1.5);

        const shimmerOffset = ((now * 62 + drawX * 0.4 + drawY * 0.3) % (size * 2)) - size;
        ctx.save();
        ctx.beginPath();
        ctx.rect(drawX, drawY, size, size);
        ctx.clip();
        ctx.translate(drawX + shimmerOffset, drawY);
        ctx.rotate(-Math.PI / 4);
        const shimmer = ctx.createLinearGradient(0, 0, size * 1.8, 0);
        shimmer.addColorStop(0, "rgba(255,255,255,0)");
        shimmer.addColorStop(0.45, `rgba(210,245,255,${(0.18 + pulse * 0.2).toFixed(3)})`);
        shimmer.addColorStop(0.5, `rgba(255,255,255,${(0.38 + pulse * 0.28).toFixed(3)})`);
        shimmer.addColorStop(0.55, `rgba(210,245,255,${(0.18 + pulse * 0.2).toFixed(3)})`);
        shimmer.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = shimmer;
        ctx.fillRect(-size, -size * 0.25, size * 2.8, size * 0.5);
        ctx.restore();
      }

      if (tile.lockHp > 0) {
        this.drawSprite("lock", drawX + 1, drawY + 1, size - 2, size - 2, () => {
          ctx.strokeStyle = "#f8f8ff";
          ctx.lineWidth = 2;
          ctx.strokeRect(drawX + 2, drawY + 2, size - 4, size - 4);
          ctx.lineWidth = 1;
        });
      }
    };

    if (angleRad === 0 && scaleX === 1 && scaleY === 1) {
      drawTileContent(x, y);
      return;
    }

    ctx.save();
    const cx = x + size / 2;
    const cy = y + size / 2;
    ctx.translate(cx, cy);
    if (angleRad !== 0) {
      ctx.rotate(angleRad);
    }
    if (scaleX !== 1 || scaleY !== 1) {
      ctx.scale(scaleX, scaleY);
    }
    drawTileContent(-size / 2, -size / 2);
    ctx.restore();
  }

  /**
   * Spawn, update, and draw subtle whirlwind/swirl ambient effects on the grid.
   * Only called during RUN_PLAYABLE-family phases.
   */
  private updateAndDrawWhirlwinds(): void {
    const now = this.nowMs();
    const ctx = this.ctx;
    const gridSize = Math.round(this.bps / this.tps);

    // Spawn a new whirlwind every 3-5 seconds (max 3 active)
    if (this.whirlwinds.length < 3 && now - this.lastWhirlwindSpawnMs > 3000 + Math.random() * 2000) {
      const col = Math.floor(Math.random() * gridSize);
      const row = Math.floor(Math.random() * gridSize);
      this.whirlwinds.push({
        x: this.box + col * this.tps + this.tps / 2,
        y: this.boardY + row * this.tps + this.tps / 2,
        spawnMs: now,
        lifetimeMs: 1500 + Math.random() * 500,
        rotSpeed: 2 + Math.random() * 1.5,
        size: this.tps * (0.3 + Math.random() * 0.2),
        hue: 180 + Math.random() * 40, // cyan to light-blue range
      });
      this.lastWhirlwindSpawnMs = now;
    }

    // Update and draw
    for (let i = this.whirlwinds.length - 1; i >= 0; i--) {
      const w = this.whirlwinds[i];
      const age = now - w.spawnMs;
      if (age > w.lifetimeMs) {
        this.whirlwinds.splice(i, 1);
        continue;
      }
      const t = age / w.lifetimeMs; // 0..1
      // Fade in first 25%, fade out last 25%
      const alpha = t < 0.25 ? t / 0.25 : t > 0.75 ? (1 - t) / 0.25 : 1;
      const drawAlpha = alpha * 0.15; // max 0.15 opacity — very subtle
      const angle = (age / 1000) * w.rotSpeed * Math.PI * 2;

      ctx.save();
      ctx.translate(w.x, w.y);
      ctx.rotate(angle);
      ctx.globalAlpha = drawAlpha;

      // Draw a small spiral with 2 arcs
      for (let arm = 0; arm < 2; arm++) {
        ctx.beginPath();
        const armOffset = arm * Math.PI;
        for (let s = 0; s < 20; s++) {
          const frac = s / 20;
          const r = frac * w.size;
          const a = frac * Math.PI * 3 + armOffset;
          const px = Math.cos(a) * r;
          const py = Math.sin(a) * r;
          if (s === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.strokeStyle = `hsla(${w.hue}, 80%, 75%, 1)`;
        ctx.lineWidth = 1.2;
        ctx.stroke();
      }

      ctx.globalAlpha = 1;
      ctx.restore();
    }
  }

  private createRowRushGradient(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    visual: RowRushTargetRowVisual,
    alphaScale = 1
  ): CanvasGradient {
    const gradient = this.ctx.createLinearGradient(x0, y0, x1, y1);
    gradient.addColorStop(0, `hsla(${visual.hueA.toFixed(1)},96%,62%,${(visual.outlineAlpha * alphaScale).toFixed(3)})`);
    gradient.addColorStop(0.5, `hsla(${visual.hueB.toFixed(1)},98%,68%,${(Math.min(1, visual.outlineAlpha + 0.08) * alphaScale).toFixed(3)})`);
    gradient.addColorStop(1, `hsla(${visual.hueC.toFixed(1)},96%,62%,${(visual.outlineAlpha * alphaScale).toFixed(3)})`);
    return gradient;
  }

  private drawRowRushTargetRowUnderlays(targetRows: number[], goalDone: boolean): void {
    if (targetRows.length === 0) {
      return;
    }
    const ctx = this.ctx;
    const nowMs = this.nowMs();
    for (const row of targetRows) {
      const y = this.boardY + row * this.tps;
      const visual = resolveRowRushTargetRowVisual(nowMs, row, this.reducedMotionPreferred, goalDone);
      const scanThickness = Math.max(2, Math.floor(this.tps / 14));
      const centerBandH = Math.max(4, Math.floor(this.tps * 0.34));
      const centerBandY = y + Math.floor((this.tps - centerBandH) / 2);
      const fillGradient = ctx.createLinearGradient(this.box, y, this.box + this.bps, y + this.tps);
      fillGradient.addColorStop(0, `hsla(${visual.hueA.toFixed(1)},94%,56%,${(visual.fillAlpha * 1.02).toFixed(3)})`);
      fillGradient.addColorStop(0.5, `hsla(${visual.hueB.toFixed(1)},100%,68%,${Math.min(1, visual.fillAlpha + 0.1).toFixed(3)})`);
      fillGradient.addColorStop(1, `hsla(${visual.hueC.toFixed(1)},94%,56%,${(visual.fillAlpha * 1.02).toFixed(3)})`);
      ctx.save();
      if (!this.reducedMotionPreferred) {
        ctx.shadowColor = `hsla(${visual.hueB.toFixed(1)},100%,70%,${(visual.shadowAlpha * 0.65).toFixed(3)})`;
        ctx.shadowBlur = Math.max(4, visual.shadowBlur * 0.7);
      }
      ctx.fillStyle = fillGradient;
      ctx.fillRect(this.box, y, this.bps, this.tps - 1);
      ctx.restore();

      const centerGlow = ctx.createLinearGradient(this.box, centerBandY, this.box + this.bps, centerBandY + centerBandH);
      centerGlow.addColorStop(0, `hsla(${visual.hueA.toFixed(1)},100%,74%,${(visual.innerAlpha * 0.2).toFixed(3)})`);
      centerGlow.addColorStop(0.5, `hsla(${visual.hueB.toFixed(1)},100%,84%,${(visual.innerAlpha * 0.5).toFixed(3)})`);
      centerGlow.addColorStop(1, `hsla(${visual.hueC.toFixed(1)},100%,74%,${(visual.innerAlpha * 0.2).toFixed(3)})`);
      ctx.fillStyle = centerGlow;
      ctx.fillRect(this.box, centerBandY, this.bps, centerBandH);

      ctx.fillStyle = this.createRowRushGradient(this.box, y, this.box + this.bps, y, visual, goalDone ? 1 : 0.9);
      ctx.fillRect(this.box, y + 1, this.bps, scanThickness);
      ctx.fillRect(this.box, y + this.tps - scanThickness - 2, this.bps, scanThickness);
    }
  }

  private drawRowRushTargetRowOverlays(targetRows: number[], goalDone: boolean): void {
    if (targetRows.length === 0) {
      return;
    }
    const ctx = this.ctx;
    const nowMs = this.nowMs();
    for (const row of targetRows) {
      const y = this.boardY + row * this.tps;
      const visual = resolveRowRushTargetRowVisual(nowMs, row, this.reducedMotionPreferred, goalDone);

      ctx.save();
      ctx.strokeStyle = this.createRowRushGradient(this.box, y, this.box + this.bps, y, visual, goalDone ? 1 : 0.98);
      ctx.lineWidth = goalDone ? 3.2 : 2.6;
      if (!this.reducedMotionPreferred) {
        ctx.shadowColor = `hsla(${visual.hueB.toFixed(1)},100%,68%,${visual.shadowAlpha.toFixed(3)})`;
        ctx.shadowBlur = visual.shadowBlur;
      }
      ctx.strokeRect(this.box + 1.5, y + 1.5, this.bps - 3, this.tps - 4);
      ctx.restore();

      ctx.strokeStyle = `rgba(255,255,255,${Math.min(1, visual.innerAlpha + 0.16).toFixed(3)})`;
      ctx.lineWidth = 1.4;
      ctx.strokeRect(this.box + 3.5, y + 3.5, this.bps - 7, this.tps - 8);
    }
  }

  private maybeEmitRowRushAmbientParticles(
    targetRows: number[],
    feedbackMode: VersusFeedbackMode,
    goalDone: boolean
  ): void {
    if (targetRows.length === 0 || feedbackMode === "off" || this.particleDensity <= 0) {
      return;
    }
    const nowMs = this.nowMs();
    const intervalMs = feedbackMode === "reduced" || this.reducedMotionPreferred ? 220 : 120;
    if ((nowMs - this.lastRowRushAmbientEmitMs) < intervalMs) {
      return;
    }
    this.lastRowRushAmbientEmitMs = nowMs;
    const baseCount = feedbackMode === "reduced" || this.reducedMotionPreferred ? 1 : 2;
    for (const row of targetRows) {
      const visual = resolveRowRushTargetRowVisual(nowMs, row, this.reducedMotionPreferred, goalDone);
      const particleColors = rowRushVisualParticleColors(visual);
      const px = this.box + Math.random() * this.bps;
      const py = this.boardY + row * this.tps + this.tps * (0.18 + Math.random() * 0.64);
      this.emitDirectionalBurst(px, py, particleColors, baseCount, {
        directionRad: -Math.PI / 2,
        spreadRad: this.reducedMotionPreferred ? 0.45 : 1.1,
        speedMin: 14,
        speedMax: feedbackMode === "reduced" ? 24 : 36,
        lifeMin: 0.28,
        lifeMax: this.reducedMotionPreferred ? 0.44 : 0.62,
        sizeMin: 1.4,
        sizeMax: feedbackMode === "reduced" ? 2.1 : 2.8,
        upwardBoost: 8,
        shape: "square",
        spinMin: -3,
        spinMax: 3
      });
    }
  }

  private drawRowRushClearedCellFx(
    step: CollapseAnimState["steps"][number],
    rawProgress: number,
    targetRows: number[],
    goalDone: boolean,
    feedbackMode: VersusFeedbackMode
  ): void {
    if (targetRows.length === 0 || step.clearedCells.length === 0) {
      return;
    }
    const targetRowSet = new Set(targetRows);
    const hitCells = step.clearedCells.filter((cell) => targetRowSet.has(cell.row));
    if (hitCells.length === 0) {
      return;
    }

    const nowMs = this.nowMs();
    const ctx = this.ctx;
    const boxAlpha = Math.max(0, 0.92 - rawProgress * 0.72);
    const outerInset = 1.2 + rawProgress * 1.15;
    const outerSize = Math.max(2, this.tps - outerInset * 2 - 1);
    const innerInset = outerInset + 2;
    const innerSize = Math.max(1, this.tps - innerInset * 2 - 1);

    for (const cell of hitCells) {
      const x = this.box + cell.col * this.tps;
      const y = this.boardY + cell.row * this.tps;
      const visual = resolveRowRushTargetRowVisual(nowMs, cell.row, this.reducedMotionPreferred, goalDone);
      ctx.fillStyle = this.createRowRushGradient(x, y, x + this.tps, y, visual, Math.min(1, boxAlpha * 0.18));
      ctx.fillRect(x + 2, y + 2, this.tps - 5, this.tps - 5);

      ctx.save();
      ctx.strokeStyle = this.createRowRushGradient(x, y, x + this.tps, y, visual, Math.min(1, boxAlpha * 1.08));
      ctx.lineWidth = feedbackMode === "reduced" ? 1.5 : 2.1;
      if (!this.reducedMotionPreferred && feedbackMode !== "off") {
        ctx.shadowColor = `hsla(${visual.hueB.toFixed(1)},100%,72%,${Math.min(1, visual.shadowAlpha + 0.08).toFixed(3)})`;
        ctx.shadowBlur = 6 + visual.pulse * 5;
      }
      ctx.strokeRect(x + outerInset, y + outerInset, outerSize, outerSize);
      ctx.restore();

      ctx.strokeStyle = `rgba(255,255,255,${Math.min(1, 0.44 + visual.pulse * 0.24).toFixed(3)})`;
      ctx.lineWidth = 1;
      ctx.strokeRect(x + innerInset, y + innerInset, innerSize, innerSize);
    }

    if (feedbackMode === "off" || this.emittedRowRushTargetParticles.has(step)) {
      return;
    }
    this.emittedRowRushTargetParticles.add(step);
    const baseCount = feedbackMode === "reduced" ? 2 : 5;
    for (const cell of hitCells) {
      const visual = resolveRowRushTargetRowVisual(nowMs, cell.row, this.reducedMotionPreferred, goalDone);
      const particleColors = rowRushVisualParticleColors(visual);
      const px = this.box + cell.col * this.tps + this.tps / 2;
      const py = this.boardY + cell.row * this.tps + this.tps / 2;
      this.emitDirectionalBurst(px, py, particleColors, baseCount, {
        directionRad: 0,
        spreadRad: Math.PI * 2,
        speedMin: 18,
        speedMax: feedbackMode === "reduced" ? 40 : 58,
        lifeMin: 0.32,
        lifeMax: 0.68,
        sizeMin: 1.8,
        sizeMax: feedbackMode === "reduced" ? 3 : 3.8,
        upwardBoost: 0,
        shape: "square",
        spinMin: -5,
        spinMax: 5
      });
    }
  }

  private maybeEmitInterruptedSweepParticles(
    step: CollapseAnimState["steps"][number],
    feedbackMode: VersusFeedbackMode
  ): void {
    if (
      feedbackMode === "off"
      || this.emittedInterruptedSweepParticles.has(step)
      || (step.specialFx !== "POWERUP_ROW_SWEEP" && step.specialFx !== "POWERUP_COL_SWEEP")
    ) {
      return;
    }
    const wipeOverlay = step.clearedTileOverlays?.find((overlay) => overlay.tile.special === "WIPE");
    if (wipeOverlay == null) {
      return;
    }
    this.emittedInterruptedSweepParticles.add(step);
    const px = this.box + wipeOverlay.col * this.tps + this.tps / 2;
    const py = this.boardY + wipeOverlay.row * this.tps + this.tps / 2;
    this.emitDirectionalBurst(
      px,
      py,
      ["#ff5ca8", "#ff8bd1", "#ffc3ea", "#ffe2f6"],
      feedbackMode === "reduced" ? 7 : 11,
      {
        directionRad: -Math.PI / 2,
        spreadRad: Math.PI * 2,
        speedMin: 24,
        speedMax: feedbackMode === "reduced" ? 48 : 68,
        lifeMin: 0.34,
        lifeMax: 0.72,
        sizeMin: 2,
        sizeMax: feedbackMode === "reduced" ? 3.2 : 4.2,
        upwardBoost: 4,
        shape: "star",
        spinMin: -7,
        spinMax: 7
      }
    );
  }

  private drawBoardSurface(
    board: BoardState,
    options?: {
      hiddenTileDestinationKey?: string;
      hiddenTileDestinations?: Set<string>;
      hiddenSteelDestinations?: Set<string>;
      tileCounterAngleRad?: number;
      hintCellKeys?: Set<string>;
      hintActive?: boolean;
      targetRows?: number[];
      rowRushGoalDone?: boolean;
    }
  ): void {
    const ctx = this.ctx;
    const hiddenTileDestinationKey = options?.hiddenTileDestinationKey;
    const hiddenTileDestinations = options?.hiddenTileDestinations;
    const hiddenSteelDestinations = options?.hiddenSteelDestinations;
    const tileCounterAngleRad = options?.tileCounterAngleRad ?? 0;
    const hintCellKeys = options?.hintCellKeys;
    const hintActive = options?.hintActive === true;
    const targetRows = options?.targetRows ?? [];
    const rowRushGoalDone = options?.rowRushGoalDone === true;

    ctx.fillStyle = "#111c32";
    ctx.fillRect(this.box, this.boardY, this.bps, this.bps);
    const bp = this.beatPulse;

    // Phase 5: Full rounded rect border with glow
    ctx.save();
    roundedRectPath(ctx, this.box - 1, this.boardY - 1, this.bps + 2, this.bps + 2, 3);
    if (this.currentWarningActive) {
      ctx.shadowColor = "#ff5050";
      ctx.shadowBlur = 4 + bp * 3;
      ctx.strokeStyle = `rgba(255,100,100,${(0.7 + bp * 0.3).toFixed(3)})`;
      ctx.lineWidth = 1.5 + bp * 1.2;
    } else {
      ctx.shadowColor = "#00d4ff";
      ctx.shadowBlur = 4 + bp * 3;
      ctx.strokeStyle = `rgba(128,180,255,${(0.6 + bp * 0.4).toFixed(3)})`;
      ctx.lineWidth = 1 + bp * 0.8;
    }
    ctx.stroke();
    ctx.restore();
    ctx.lineWidth = 1;

    this.drawRowRushTargetRowUnderlays(targetRows, rowRushGoalDone);

    for (let row = 0; row < board.size; row += 1) {
      for (let col = 0; col < board.size; col += 1) {
        const x = this.box + col * this.tps;
        const y = this.boardY + row * this.tps;
        const cell = board.cells[row][col];

        ctx.fillStyle = "#162443";
        ctx.fillRect(x, y, this.tps - 1, this.tps - 1);

        if (cell.kind === "CRATE") {
          this.drawCrateSprite(x + 1, y + 1, this.tps - 3);
          continue;
        }

        if (cell.kind === "STEEL") {
          if (hiddenSteelDestinations?.has(posKey(row, col)) === true) {
            continue;
          }
          this.drawSteelSprite(x + 1, y + 1, this.tps - 3);
          continue;
        }

        if (cell.kind !== "TILE" || cell.tile == null) {
          continue;
        }

        const cellKey = posKey(row, col);
        if (hiddenTileDestinationKey === cellKey || hiddenTileDestinations?.has(cellKey) === true) {
          continue;
        }

        let tileAngle = tileCounterAngleRad;
        let scaleX = 1;
        let scaleY = 1;
        if (hintActive && hintCellKeys?.has(cellKey) === true) {
          const t = this.nowMs() / 130 + row * 0.33 + col * 0.27;
          const pulse = 0.5 + 0.5 * Math.sin(t);
          const wobble = Math.sin(t * 1.8) * 0.07;
          tileAngle += wobble;
          scaleX = 1 + pulse * 0.06;
          scaleY = 1 + pulse * 0.04;
        }
        this.drawTileSprite(
          cell.tile,
          x + TILE_DRAW_PADDING,
          y + TILE_DRAW_PADDING,
          (this.tps - 2),
          tileAngle,
          scaleX,
          scaleY
        );
      }
    }

    this.drawRowRushTargetRowOverlays(targetRows, rowRushGoalDone);

    // Hover highlight (desktop, not dragging)
    const hover = this.currentHoverCell;
    if (hover != null && this.currentDragState == null) {
      const hx = this.box + hover.col * this.tps;
      const hy = this.boardY + hover.row * this.tps;
      ctx.fillStyle = "rgba(255,255,255,0.08)";
      ctx.fillRect(hx, hy, this.tps - 1, this.tps - 1);
      ctx.strokeStyle = "rgba(255,220,80,0.5)";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(hx + 0.5, hy + 0.5, this.tps - 2, this.tps - 2);
      ctx.lineWidth = 1;
    }
  }

  private drawRotationBoard(anim: RotationAnimState, targetRows: number[] = [], rowRushGoalDone = false): void {
    const ctx = this.ctx;
    const centerX = this.box + this.bps / 2;
    const centerY = this.boardY + this.bps / 2;

    const boardForPhase = anim.phase === "tile_recover" ? anim.rotatedBoard : anim.fromBoard;
    const tileCounter = anim.phase === "tile_recover" ? anim.tileCounterAngleRad : 0;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.scale(anim.zoom, anim.zoom);
    ctx.rotate(anim.boardAngleRad);
    ctx.translate(-centerX, -centerY);
    this.drawBoardSurface(boardForPhase, {
      tileCounterAngleRad: tileCounter,
      targetRows,
      rowRushGoalDone
    });
    ctx.restore();
  }

  private drawMatchOutline(cells: Position[], shape: MatchShape, rawProgress: number): void {
    if (cells.length === 0) {
      return;
    }
    const ctx = this.ctx;
    const cellSet = new Set(cells.map((cell) => posKey(cell.row, cell.col)));
    const centerX = cells.reduce((acc, cell) => acc + (this.box + cell.col * this.tps + this.tps / 2), 0) / cells.length;
    const centerY = cells.reduce((acc, cell) => acc + (this.boardY + cell.row * this.tps + this.tps / 2), 0) / cells.length;
    const expansion = 1 + rawProgress * 0.12;
    const alpha = Math.max(0.32, 1 - rawProgress * 0.58);
    const hueOffset = shape === "T" ? 45 : shape === "L" ? 20 : shape === "SQUARE" ? 75 : 0;
    const hue = (this.nowMs() / 4 + hueOffset) % 360;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.scale(expansion, expansion);
    ctx.translate(-centerX, -centerY);
    ctx.strokeStyle = `hsla(${hue.toFixed(1)},98%,65%,${alpha.toFixed(3)})`;
    ctx.shadowColor = `hsla(${hue.toFixed(1)},100%,70%,${(alpha * 0.9).toFixed(3)})`;
    ctx.shadowBlur = 16 + (1 - rawProgress) * 16;
    ctx.lineWidth = 3.2;
    ctx.setLineDash([]);
    ctx.beginPath();

    for (const cell of cells) {
      const x = this.box + cell.col * this.tps + 0.5;
      const y = this.boardY + cell.row * this.tps + 0.5;
      const s = this.tps - 1;
      if (!cellSet.has(posKey(cell.row - 1, cell.col))) {
        ctx.moveTo(x, y);
        ctx.lineTo(x + s, y);
      }
      if (!cellSet.has(posKey(cell.row + 1, cell.col))) {
        ctx.moveTo(x, y + s);
        ctx.lineTo(x + s, y + s);
      }
      if (!cellSet.has(posKey(cell.row, cell.col - 1))) {
        ctx.moveTo(x, y);
        ctx.lineTo(x, y + s);
      }
      if (!cellSet.has(posKey(cell.row, cell.col + 1))) {
        ctx.moveTo(x + s, y);
        ctx.lineTo(x + s, y + s);
      }
    }

    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = `hsla(${((hue + 30) % 360).toFixed(1)},100%,82%,${(alpha * 0.6).toFixed(3)})`;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.strokeStyle = `rgba(255,255,255,${Math.max(0.25, alpha * 0.45).toFixed(3)})`;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  }

  private drawCollapseHighlights(
    collapseAnim: CollapseAnimState,
    stepIndex: number,
    feedbackMode: VersusFeedbackMode = "full",
    targetRows: number[] = [],
    rowRushGoalDone = false
  ): void {
    const ctx = this.ctx;
    const step = collapseAnim.steps[stepIndex];
    if (step == null || step.clearedCells.length === 0) {
      return;
    }

    const rawProgress = clamp01(collapseAnim.durationSec <= 0 ? 1 : collapseAnim.elapsedSec / collapseAnim.durationSec);
    const alpha = (1 - rawProgress) * (step.tier === 5 ? 0.35 : step.tier === 4 ? 0.25 : 0.18);

    let color = "255,255,255";
    if (step.tier === 4) {
      color = "120,210,255";
    } else if (step.tier === 5) {
      color = "255,190,90";
    }

    ctx.fillStyle = `rgba(${color},${alpha.toFixed(3)})`;
    for (const cell of step.clearedCells) {
      const x = this.box + cell.col * this.tps;
      const y = this.boardY + cell.row * this.tps;
      ctx.fillRect(x + 1, y + 1, this.tps - 3, this.tps - 3);
      if (feedbackMode !== "off") {
        const bombExplosion = step.specialFx === "BOMB_EXPLOSION";
        const fxId = resolveCollapseClearFxSpriteId(step.specialFx, step.tier);
        const baseSize = this.tps - 3;
        const explosionScale = feedbackMode === "reduced" ? 1.05 : 1.2;
        const fxSize = bombExplosion ? baseSize * explosionScale : baseSize;
        const fxOffset = (baseSize - fxSize) / 2;
        this.drawSprite(fxId, x + 1 + fxOffset, y + 1 + fxOffset, fxSize, fxSize, () => {});
      }
    }

    this.drawRowRushClearedCellFx(step, rawProgress, targetRows, rowRushGoalDone, feedbackMode);
    this.maybeEmitInterruptedSweepParticles(step, feedbackMode);

    if (
      (step.specialFx === "POWERUP_ROW_SWEEP" || step.specialFx === "POWERUP_COL_SWEEP" || step.specialFx === "POWERUP_ZIGZAG" || step.specialFx === "POWERUP_HINT")
      && step.powerupPath != null
      && step.powerupPath.length > 0
    ) {
      ctx.save();
      if (step.specialFx === "POWERUP_ROW_SWEEP") {
        ctx.strokeStyle = "rgba(255,96,96,0.8)";
      } else if (step.specialFx === "POWERUP_COL_SWEEP") {
        ctx.strokeStyle = "rgba(100,180,255,0.82)";
      } else if (step.specialFx === "POWERUP_ZIGZAG") {
        ctx.strokeStyle = "rgba(255,196,80,0.86)";
      } else {
        ctx.strokeStyle = "rgba(206,140,255,0.88)";
      }
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i < step.powerupPath.length; i += 1) {
        const p = step.powerupPath[i];
        const px = this.box + p.col * this.tps + this.tps / 2;
        const py = this.boardY + p.row * this.tps + this.tps / 2;
        if (i === 0) {
          ctx.moveTo(px, py);
        } else {
          ctx.lineTo(px, py);
        }
      }
      ctx.stroke();
      ctx.restore();
    }

    const wipeGroups = step.wipeTargetGroups != null && step.wipeTargetGroups.length > 0
      ? step.wipeTargetGroups
      : step.wipeMatchedCells != null && step.wipeMatchedCells.length > 0
        ? [{ color: step.wipeColor ?? 0, cells: step.wipeMatchedCells }]
        : [];
    if (step.specialFx === "WIPE_DISSOLVE" && wipeGroups.length > 0) {
      const wipeProgress = easeOutCubic(rawProgress);
      const wipeScale = 1 + wipeProgress * 0.5;
      const wipeAngle = wipeProgress * Math.PI * 6; // 1080deg clockwise
      const wipeAlpha = Math.max(0, 1 - wipeProgress * 0.9);
      const wipeFx = resolveWipeDissolveFxConfig(feedbackMode);
      for (const group of wipeGroups) {
        const wipeTile: TileData = {
          color: group.color,
          special: "WIPE",
          lockHp: 0
        };
        for (const cell of group.cells) {
          const x = this.box + cell.col * this.tps;
          const y = this.boardY + cell.row * this.tps;
          ctx.save();
          ctx.globalAlpha = wipeAlpha;
          this.drawTileSprite(
            wipeTile,
            x + TILE_DRAW_PADDING,
            y + TILE_DRAW_PADDING,
            (this.tps - 2),
            wipeAngle,
            wipeScale,
            wipeScale
          );
          ctx.restore();

          if (wipeFx.showWipeWaveOverlay && wipeProgress > 0.55) {
            const pulse = 0.5 + 0.5 * Math.sin(this.nowMs() / 45 + cell.row * 0.9 + cell.col * 0.6);
            this.drawSprite("fx_wipewave", x - 2, y - 2, this.tps + 4, this.tps + 4, () => {});
            ctx.fillStyle = `rgba(255,255,255,${(0.08 + pulse * 0.12).toFixed(3)})`;
            ctx.fillRect(x + 1, y + 1, this.tps - 3, this.tps - 3);
          }
        }
      }

      const wipeActivatorCells = step.wipeActivatorCells ?? [];
      if (wipeActivatorCells.length > 1) {
        const activatorCenter = wipeActivatorCells.reduce(
          (acc, cell) => {
            acc.x += this.box + cell.col * this.tps + this.tps / 2;
            acc.y += this.boardY + cell.row * this.tps + this.tps / 2;
            return acc;
          },
          { x: 0, y: 0 }
        );
        const cx = activatorCenter.x / wipeActivatorCells.length;
        const cy = activatorCenter.y / wipeActivatorCells.length;
        const radius = wipeActivatorCells.reduce((maxRadius, cell) => {
          const px = this.box + cell.col * this.tps + this.tps / 2;
          const py = this.boardY + cell.row * this.tps + this.tps / 2;
          return Math.max(maxRadius, Math.hypot(px - cx, py - cy));
        }, this.tps * 0.7) + this.tps * 0.8;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(wipeProgress * Math.PI * 4);
        ctx.globalAlpha = Math.max(0, 0.38 - wipeProgress * 0.22);
        for (let arm = 0; arm < 3; arm += 1) {
          ctx.beginPath();
          const armOffset = (arm * Math.PI * 2) / 3;
          for (let s = 0; s <= 26; s += 1) {
            const frac = s / 26;
            const spiralRadius = frac * radius;
            const angle = frac * Math.PI * 3.8 + armOffset;
            const px = Math.cos(angle) * spiralRadius;
            const py = Math.sin(angle) * spiralRadius;
            if (s === 0) {
              ctx.moveTo(px, py);
            } else {
              ctx.lineTo(px, py);
            }
          }
          ctx.strokeStyle = `hsla(${(180 + arm * 70).toFixed(1)}, 100%, 78%, 1)`;
          ctx.lineWidth = feedbackMode === "reduced" ? 1.6 : 2.2;
          ctx.stroke();
        }
        ctx.restore();
      }

      if (wipeProgress >= 0.78 && !this.emittedWipeParticles.has(step)) {
        this.emittedWipeParticles.add(step);
        if (wipeActivatorCells.length > 1) {
          const center = wipeActivatorCells.reduce(
            (acc, cell) => {
              acc.x += this.box + cell.col * this.tps + this.tps / 2;
              acc.y += this.boardY + cell.row * this.tps + this.tps / 2;
              return acc;
            },
            { x: 0, y: 0 }
          );
          const cx = center.x / wipeActivatorCells.length;
          const cy = center.y / wipeActivatorCells.length;
          this.emitRainbowBurst(cx, cy, wipeFx.rainbowBurstCount + (feedbackMode === "reduced" ? 2 : 5));
        }
        for (const group of wipeGroups) {
          for (const cell of group.cells) {
            const px = this.box + cell.col * this.tps + this.tps / 2;
            const py = this.boardY + cell.row * this.tps + this.tps / 2;
            if (wipeFx.rainbowBurstCount > 0) {
              this.emitRainbowBurst(px, py, wipeFx.rainbowBurstCount);
            }
            this.emitMatchBurst(px, py, TILE_COLORS[group.color % TILE_COLORS.length], feedbackMode === "reduced" ? 3 : 6);
          }
        }
      }
    }

    if (step.tier === 5 && feedbackMode === "full") {
      const center = step.clearedCells.reduce(
        (acc, cell) => {
          acc.x += this.box + cell.col * this.tps + this.tps / 2;
          acc.y += this.boardY + cell.row * this.tps + this.tps / 2;
          return acc;
        },
        { x: 0, y: 0 }
      );
      const cx = center.x / step.clearedCells.length;
      const cy = center.y / step.clearedCells.length;
      this.drawSprite("fx_wipewave", cx - 20, cy - 20, 40, 40, () => {});
    }
  }

  private drawClearedTileOverlays(step: CollapseAnimState["steps"][number], rawProgress: number): void {
    if (step.clearedTileOverlays == null || step.clearedTileOverlays.length === 0) {
      return;
    }
    const alpha = Math.max(0, 1 - rawProgress * 0.95);
    if (alpha <= 0) {
      return;
    }
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    for (const overlay of step.clearedTileOverlays) {
      const x = this.box + overlay.col * this.tps;
      const y = this.boardY + overlay.row * this.tps;
      this.drawTileSprite(
        overlay.tile,
        x + TILE_DRAW_PADDING,
        y + TILE_DRAW_PADDING,
        (this.tps - 2)
      );
    }
    ctx.restore();

  }

  private drawCrateBreakOverlays(
    step: CollapseAnimState["steps"][number],
    rawProgress: number,
    feedbackMode: VersusFeedbackMode
  ): void {
    if (step.crateBreakCells == null || step.crateBreakCells.length === 0) {
      return;
    }
    const fx = resolveCrateBreakFxConfig(rawProgress, feedbackMode);
    if (!fx.visible) {
      return;
    }

    const ctx = this.ctx;
    const size = this.tps - 3;
    const halfSize = size / 2;
    const travelPx = fx.travelPx * (size / TILE_PIXEL_SIZE);
    const quadrants = [
      { clipX: 0, clipY: 0, dirX: -1, dirY: -1 },
      { clipX: halfSize, clipY: 0, dirX: 1, dirY: -1 },
      { clipX: 0, clipY: halfSize, dirX: -1, dirY: 1 },
      { clipX: halfSize, clipY: halfSize, dirX: 1, dirY: 1 }
    ] as const;

    ctx.save();
    ctx.globalAlpha *= fx.alpha;
    for (const cell of step.crateBreakCells) {
      const baseX = this.box + cell.col * this.tps + 1;
      const baseY = this.boardY + cell.row * this.tps + 1;
      for (const quadrant of quadrants) {
        const offsetX = quadrant.dirX * travelPx;
        const offsetY = quadrant.dirY * travelPx;
        ctx.save();
        ctx.beginPath();
        ctx.rect(baseX + quadrant.clipX + offsetX, baseY + quadrant.clipY + offsetY, halfSize, halfSize);
        ctx.clip();
        this.drawCrateSprite(baseX + offsetX, baseY + offsetY, size);
        ctx.restore();
      }
    }
    ctx.restore();
  }

  private drawCollapseOutlines(
    collapseAnim: CollapseAnimState,
    stepIndex: number,
    feedbackMode: VersusFeedbackMode = "full"
  ): void {
    if (feedbackMode === "off") {
      return;
    }
    const step = collapseAnim.steps[stepIndex];
    if (step == null || step.clearedCells.length === 0) {
      return;
    }
    const rawProgress = clamp01(collapseAnim.durationSec <= 0 ? 1 : collapseAnim.elapsedSec / collapseAnim.durationSec);
    const outlineGroups = step.matchedGroups.length > 0
      ? step.matchedGroups
      : [{ cells: step.clearedCells, shape: "COMPOSITE" as MatchShape, center: { row: 5, col: 5 } }];
    for (const group of outlineGroups) {
      this.drawMatchOutline(group.cells, group.shape, rawProgress);
    }
  }

  private drawCollapseBoard(
    board: BoardState,
    collapseAnim: CollapseAnimState,
    feedbackMode: VersusFeedbackMode = "full",
    targetRows: number[] = [],
    rowRushGoalDone = false
  ): void {
    const step = collapseAnim.steps[collapseAnim.stepIndex];
    if (step == null) {
      this.drawBoardSurface(board, { targetRows, rowRushGoalDone });
      return;
    }

    const rawProgress = clamp01(
      collapseAnim.durationSec <= 0 ? 1 : collapseAnim.elapsedSec / collapseAnim.durationSec
    );
    const style = collapseStyleForTierAndFeedbackMode(step.tier, rawProgress, feedbackMode);

    const hiddenTiles = new Set<string>();
    for (const motion of step.motions) {
      hiddenTiles.add(posKey(motion.toRow, motion.toCol));
    }
    const stepBoard = step.boardSnapshot ?? board;
    const hiddenSteels = new Set<string>();
    for (const motion of step.steelMotions ?? []) {
      const inBoard =
        motion.toRow >= 0
        && motion.toRow < stepBoard.size
        && motion.toCol >= 0
        && motion.toCol < stepBoard.size
        && motion.fellThroughEdge !== true;
      if (!inBoard) {
        continue;
      }
      hiddenSteels.add(posKey(motion.toRow, motion.toCol));
    }

    this.drawBoardSurface(stepBoard, {
      hiddenTileDestinations: hiddenTiles,
      hiddenSteelDestinations: hiddenSteels,
      targetRows,
      rowRushGoalDone
    });
    this.drawCollapseHighlights(collapseAnim, collapseAnim.stepIndex, feedbackMode, targetRows, rowRushGoalDone);
    this.drawClearedTileOverlays(step, rawProgress);

    const ctx = this.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.rect(this.box - 8, this.boardY - 8, this.bps + 16, this.bps + 16);
    ctx.clip();

    for (const motion of step.motions) {
      const fromX = this.box + motion.fromCol * this.tps;
      const fromY = this.boardY + motion.fromRow * this.tps;
      const toX = this.box + motion.toCol * this.tps;
      const toY = this.boardY + motion.toRow * this.tps;

      const drawX = lerp(fromX, toX, style.progress) + TILE_DRAW_PADDING;
      const drawY = lerp(fromY, toY, style.progress) + TILE_DRAW_PADDING + style.yLiftPx;

      this.drawTileSprite(
        motion.tile,
        drawX,
        drawY,
        (this.tps - 2),
        0,
        style.scaleX,
        style.scaleY
      );
    }

    for (const steelMotion of step.steelMotions ?? []) {
      const fromX = this.box + steelMotion.fromCol * this.tps;
      const fromY = this.boardY + steelMotion.fromRow * this.tps;
      const toX = this.box + steelMotion.toCol * this.tps;
      const toY = this.boardY + steelMotion.toRow * this.tps;
      const drawX = lerp(fromX, toX, style.progress) + 1;
      const drawY = lerp(fromY, toY, style.progress) + 1 + style.yLiftPx;
      this.drawSteelSprite(drawX, drawY, this.tps - 3);
    }

    this.drawCrateBreakOverlays(step, rawProgress, feedbackMode);

    ctx.restore();
    this.drawCollapseOutlines(collapseAnim, collapseAnim.stepIndex, feedbackMode);
  }

  private drawSwapBoard(
    board: BoardState,
    swapAnim: SwapAnimState,
    enhancedRejectFx = false,
    targetRows: number[] = [],
    rowRushGoalDone = false
  ): void {
    const ctx = this.ctx;
    const rawProgress = clamp01(
      swapAnim.durationSec <= 0 ? 1 : swapAnim.elapsedSec / swapAnim.durationSec
    );

    let slideProgress: number;
    if (swapAnim.rejected) {
      // There-and-back motion: slide halfway then rubber-band back
      slideProgress = Math.sin(rawProgress * Math.PI) * 0.5;
    } else {
      slideProgress = easeOutBack(rawProgress);
    }

    const hiddenTiles = new Set<string>();
    hiddenTiles.add(posKey(swapAnim.fromCell.row, swapAnim.fromCell.col));
    hiddenTiles.add(posKey(swapAnim.toCell.row, swapAnim.toCell.col));

    this.drawBoardSurface(board, {
      hiddenTileDestinations: hiddenTiles,
      targetRows,
      rowRushGoalDone
    });

    const fx = this.box + swapAnim.fromCell.col * this.tps;
    const fy = this.boardY + swapAnim.fromCell.row * this.tps;
    const tx = this.box + swapAnim.toCell.col * this.tps;
    const ty = this.boardY + swapAnim.toCell.row * this.tps;

    if (swapAnim.rejected) {
      if (enhancedRejectFx) {
        const rejectPulse = this.reducedMotionPreferred
          ? 0.82
          : 0.52 + 0.48 * Math.sin(rawProgress * Math.PI * 8);
        const tintAlpha = clamp01((1 - rawProgress) * 0.2 + rejectPulse * 0.18);
        ctx.fillStyle = `rgba(255,74,74,${tintAlpha.toFixed(3)})`;
        ctx.fillRect(fx, fy, this.tps - 1, this.tps - 1);
        ctx.fillRect(tx, ty, this.tps - 1, this.tps - 1);
        const outlineAlpha = clamp01((1 - rawProgress) * 0.45 + rejectPulse * 0.55);
        ctx.save();
        ctx.strokeStyle = `rgba(255,72,72,${outlineAlpha.toFixed(3)})`;
        ctx.lineWidth = 1.8;
        if (!this.reducedMotionPreferred) {
          ctx.shadowColor = `rgba(255,112,112,${(outlineAlpha * 0.95).toFixed(3)})`;
          ctx.shadowBlur = 7;
        }
        ctx.strokeRect(fx + 0.8, fy + 0.8, this.tps - 1.6, this.tps - 1.6);
        ctx.strokeRect(tx + 0.8, ty + 0.8, this.tps - 1.6, this.tps - 1.6);
        ctx.shadowBlur = 0;
        ctx.strokeStyle = `rgba(255,212,212,${(outlineAlpha * 0.55).toFixed(3)})`;
        ctx.lineWidth = 1;
        ctx.strokeRect(fx + 2.2, fy + 2.2, this.tps - 4.4, this.tps - 4.4);
        ctx.strokeRect(tx + 2.2, ty + 2.2, this.tps - 4.4, this.tps - 4.4);
        ctx.restore();
        ctx.lineWidth = 1;
      } else {
        // Red tint on rejected swap cells near end of animation
        if (rawProgress > 0.7) {
          const tintAlpha = (rawProgress - 0.7) / 0.3 * 0.2;
          ctx.fillStyle = `rgba(255,80,80,${tintAlpha.toFixed(3)})`;
          ctx.fillRect(fx, fy, this.tps - 1, this.tps - 1);
          ctx.fillRect(tx, ty, this.tps - 1, this.tps - 1);
        }
        const rejectPulse = this.reducedMotionPreferred
          ? 0.8
          : 0.45 + 0.55 * Math.sin(rawProgress * Math.PI);
        const outlineAlpha = clamp01((1 - rawProgress) * 0.72 + rejectPulse * 0.24);
        ctx.save();
        ctx.strokeStyle = `rgba(255,84,84,${outlineAlpha.toFixed(3)})`;
        ctx.lineWidth = 1.3;
        if (!this.reducedMotionPreferred) {
          ctx.shadowColor = `rgba(255,102,102,${(outlineAlpha * 0.88).toFixed(3)})`;
          ctx.shadowBlur = 5;
        }
        ctx.strokeRect(fx + 1, fy + 1, this.tps - 2, this.tps - 2);
        ctx.strokeRect(tx + 1, ty + 1, this.tps - 2, this.tps - 2);
        ctx.restore();
        ctx.lineWidth = 1;
      }
    }

    ctx.save();
    ctx.beginPath();
    ctx.rect(this.box - 4, this.boardY - 4, this.bps + 8, this.bps + 8);
    ctx.clip();

    // fromTile slides from fromCell toward toCell
    const fromStartX = this.box + swapAnim.fromCell.col * this.tps;
    const fromStartY = this.boardY + swapAnim.fromCell.row * this.tps;
    const toStartX = this.box + swapAnim.toCell.col * this.tps;
    const toStartY = this.boardY + swapAnim.toCell.row * this.tps;

    const axisX = toStartX - fromStartX;
    const axisY = toStartY - fromStartY;
    const axisLen = Math.max(1e-4, Math.hypot(axisX, axisY));
    const perpX = -axisY / axisLen;
    const perpY = axisX / axisLen;
    const rejectJitter =
      swapAnim.rejected
        ? (
            enhancedRejectFx
              ? Math.sin(rawProgress * Math.PI * 11) * (1 - rawProgress) * (this.reducedMotionPreferred ? 1.5 : 3.2)
              : Math.sin(rawProgress * Math.PI * 9) * (1 - rawProgress) * (this.reducedMotionPreferred ? 0.9 : 1.8)
          )
        : 0;

    const fromDrawX = lerp(fromStartX, toStartX, slideProgress) + TILE_DRAW_PADDING + rejectJitter * perpX;
    const fromDrawY = lerp(fromStartY, toStartY, slideProgress) + TILE_DRAW_PADDING + rejectJitter * perpY;
    this.drawTileSprite(swapAnim.fromTile, fromDrawX, fromDrawY, (this.tps - 2));

    // toTile slides from toCell toward fromCell
    const toDrawX = lerp(toStartX, fromStartX, slideProgress) + TILE_DRAW_PADDING - rejectJitter * perpX;
    const toDrawY = lerp(toStartY, fromStartY, slideProgress) + TILE_DRAW_PADDING - rejectJitter * perpY;
    this.drawTileSprite(swapAnim.toTile, toDrawX, toDrawY, (this.tps - 2));

    ctx.restore();
  }

  private drawTutorialOverlay(tutorial: TutorialState, uiRects: UiRects): void {
    drawTutorialOverlayScreen(
      { ctx: this.ctx, box: this.box, boardY: this.boardY, tps: this.tps, bps: this.bps },
      tutorial,
      uiRects,
    );
  }

  private drawDebugOverlay(debugViewModel: GameplayDebugSnapshot, debugSettings: DebugSettings): void {
    drawDebugOverlayScreen(
      {
        ctx: this.ctx,
        canvasWidth: this.canvas.width,
        canvasHeight: this.canvas.height,
        dpr: this.dpr,
      },
      debugViewModel,
      debugSettings,
    );
  }

  draw(model: RenderModel): UiRects {
    const ctx = this.ctx;
    this.frameNowMs = performance.now();
    this.frameLinearGradientCache.clear();

    // Phase transition: capture snapshot before drawing new phase
    const phaseChanged = this.lastRenderedPhase != null && model.phase !== this.lastRenderedPhase;
    const isGameplaySub = CanvasRenderer.GAMEPLAY_PHASES.has(model.phase) &&
      CanvasRenderer.GAMEPLAY_PHASES.has(this.lastRenderedPhase ?? "");
    if (phaseChanged && !isGameplaySub && typeof createImageBitmap === "function") {
      this.transitionElapsed = 0;
      // Capture current canvas as snapshot (async but usually instant for canvas)
      void createImageBitmap(this.canvas).then((bmp) => {
        this.transitionSnapshot = bmp;
      }).catch(() => { /* fallback: no transition */ });
    }
    if (model.phase === "RUN_GAMEOVER" && this.lastRenderedPhase !== "RUN_GAMEOVER") {
      this.runGameOverSurfaceId += 1;
    }
    const leaderboardSurfaceKey =
      model.phase === "LEADERBOARD"
        ? `leaderboard:${model.leaderboardTab}:${model.leaderboardTab === "solo" ? model.selectedSoloLeaderboardMode : "ranked"}`
        : model.phase === "PHONE" && model.phoneTab === "highscores"
          ? `phone-highscores:${model.leaderboardTab}:${model.leaderboardTab === "solo" ? model.selectedSoloLeaderboardMode : "ranked"}`
          : model.phase === "RUN_GAMEOVER"
            ? `gameover:${this.runGameOverSurfaceId}:${model.latestRunSoloMode}`
            : null;
    if (leaderboardSurfaceKey !== this.lastLeaderboardSurfaceKey) {
      this.leaderboardScreenEnterTime = this.nowSec();
      this.lastLeaderboardSurfaceKey = leaderboardSurfaceKey;
    }
    this.leaderboardScrollController.setSurface(leaderboardSurfaceKey, this.nowMs());
    this.lastRenderedPhase = model.phase;

    // Track menu phase entrance for stagger animations
    if (phaseChanged || this.menuPhaseEnterPhase !== model.phase) {
      this.menuPhaseEnterMs = this.nowMs();
      this.menuPhaseEnterPhase = model.phase;
    }
    // Track settings page changes for slide animation
    if (model.settingsPage !== this.settingsLastPage && this.settingsLastPage >= 0) {
      this.settingsPageDir = model.settingsPage > this.settingsLastPage ? 1 : -1;
      this.settingsPageChangeMs = this.nowMs();
    }
    this.settingsLastPage = model.settingsPage;
    // Track wallet value changes for bounce animation
    const animNowMs = this.nowMs();
    if (this.lastSeenGems >= 0 && model.gems !== this.lastSeenGems) {
      this.gemBounceMs = animNowMs;
    }
    if (this.lastSeenCoins >= 0 && model.coins !== this.lastSeenCoins) {
      this.coinBounceMs = animNowMs;
    }
    this.lastSeenGems = model.gems;
    this.lastSeenCoins = model.coins;

    this.updateShellSurfaceMode(model.phase);
    const feedbackPolicy = resolveVersusFeedbackPolicy({
      performance: model.performance,
      reducedMotionPreferred: this.readReducedMotionPreference()
    });
    this.particleDensity = this.resolveParticleDensity(model.performance.particleMode) * feedbackPolicy.particleScalar;
    if (this.particleDensity <= 0 && this.particles.particles.length > 0) {
      this.particles.particles.length = 0;
    }

    if (model.run != null && isSinglePlayerHudPolishPhase(model.phase)) {
      const dangerFrac = resolveDoomDangerFraction(model.doomClockSec, model.doomClockMaxSec);
      this.gameplayDoomDangerActive = resolveDoomDangerActive(
        this.gameplayDoomDangerActive,
        dangerFrac
      );
    } else {
      this.gameplayDoomDangerActive = false;
    }

    if (this.assetManager != null) {
      const frameClockSec = model.run?.beat.songTimeSec ?? this.nowSec();
      const level = Math.max(1, Math.min(20, model.run?.level ?? 1));
      this.assetManager.setFrameClockSeconds(frameClockSec);
      this.assetManager.setTileBlinkFps(tileBlinkFpsForLevel(level));
    }

    this.drawShellSideFx(model);

    ctx.save();

    const shakeX = (Math.random() * 2 - 1) * model.shakeMagnitude;
    const shakeY = (Math.random() * 2 - 1) * model.shakeMagnitude;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, shakeX * this.dpr, shakeY * this.dpr);

    if (model.run?.beat) {
      const bp = (model.run.beat.songTimeSec % model.run.beat.beatDurationSec) / model.run.beat.beatDurationSec;
      this.beatPulse = Math.sin(bp * Math.PI * 2) * 0.5 + 0.5;
    } else {
      this.beatPulse = 0;
    }

    this.currentWarningActive = model.warningActive;
    this.currentDragState = model.dragState;
    this.currentHoverCell = model.hoverCell;

    this.drawBackground(model);

    // Offset content below native safe area (bg already drawn full-bleed)
    if (this._nativeSafeAreaTop > 0) {
      ctx.translate(0, this._nativeSafeAreaTop);
    }

    const menuPhase =
      model.phase === "AUTH_GATE" ||
      model.phase === "TITLE" ||
      model.phase === "SETTINGS" ||
      model.phase === "LEADERBOARD" ||
      model.phase === "PROFILE" ||
      model.phase === "RUN_GAMEOVER" ||
      model.phase === "USERNAME_PICK" ||
      model.phase === "FRIENDS" ||
      model.phase === "ADD_FRIEND" ||
      model.phase === "VERSUS_LOBBY" ||
      model.phase === "VERSUS_COUNTDOWN" ||
      model.phase === "VERSUS_RESULT" ||
      model.phase === "SHOP" ||
      model.phase === "GEM_SHOP" ||
      model.phase === "TOKEN_SHOP" ||
      model.phase === "AD_GATE" ||
      model.phase === "ONBOARDING_CALL" ||
      model.phase === "PHONE" ||
      model.phase === "SOLO_SELECT";
    if (menuPhase) this.drawMenuVignette();

    const headerRects = this.drawHeader(model, feedbackPolicy.mode);

    let uiRects: UiRects = {};
    const menuContentOffsetPhase =
      model.phase === "TITLE"
      || model.phase === "SETTINGS"
      || model.phase === "PROFILE"
      || model.phase === "PROFILE_SETTINGS"
      || model.phase === "LEADERBOARD"
      || model.phase === "SHOP"
      || model.phase === "GEM_SHOP"
      || model.phase === "TOKEN_SHOP"
      || model.phase === "AD_GATE"
      || model.phase === "ONBOARDING_CALL"
      || model.phase === "USERNAME_PICK"
      || model.phase === "FRIENDS"
      || model.phase === "ADD_FRIEND"
      || model.phase === "VERSUS_LOBBY"
      || model.phase === "VERSUS_COUNTDOWN"
      || model.phase === "VERSUS_RESULT"
      || model.phase === "PHONE"
      || model.phase === "SOLO_SELECT"
      || model.phase === "DAILY_REWARDS"
      || model.phase === "DAILY_REWARDS_OPEN";

    if (menuContentOffsetPhase) {
      ctx.save();
      ctx.translate(0, MENU_CONTENT_OFFSET_PX);
    }

    if (model.phase === "AUTH_GATE") {
      uiRects = this.drawAuthGate(model);
    } else if (model.phase === "TITLE") {
      uiRects = this.drawTitle(model);
    } else if (model.phase === "SETTINGS") {
      uiRects = this.drawSettings(model);
    } else if (model.phase === "PROFILE") {
      uiRects = this.drawProfile(model);
    } else if (model.phase === "PROFILE_SETTINGS") {
      uiRects = this.drawProfileSettings(model);
    } else if (model.phase === "LEADERBOARD") {
      uiRects = this.drawLeaderboardScreen(model);
    } else if (model.phase === "SHOP") {
      uiRects = this.drawShop(model);
    } else if (model.phase === "GEM_SHOP") {
      uiRects = this.drawGemShop(model);
    } else if (model.phase === "TOKEN_SHOP") {
      uiRects = this.drawTokenShop(model);
    } else if (model.phase === "AD_GATE") {
      uiRects = this.drawAdGate(model);
    } else if (model.phase === "ONBOARDING_CALL") {
      uiRects = this.drawOnboardingCall(model);
    } else if (model.phase === "SOLO_SELECT") {
      uiRects = this.drawSoloSelect(model);
    } else if (model.phase === "DAILY_REWARDS") {
      uiRects = this.drawDailyRewards(model);
    } else if (model.phase === "DAILY_REWARDS_OPEN") {
      uiRects = this.drawDailyRewardsOpen(model);
    } else if (model.phase === "USERNAME_PICK") {
      uiRects = this.drawUsernamePick(model);
    } else if (model.phase === "FRIENDS") {
      uiRects = this.drawFriends(model);
    } else if (model.phase === "ADD_FRIEND") {
      uiRects = this.drawAddFriend(model);
    } else if (model.phase === "VERSUS_COUNTDOWN") {
      // Reuse the lobby searching layout — it shows the opponent skin when countdown data arrives
      uiRects = this.drawVersusLobby(model);
    } else if (model.phase === "VERSUS_LOBBY") {
      uiRects = this.drawVersusLobby(model);
    } else if (model.phase === "VERSUS_MATCH") {
      uiRects = this.drawVersusMatch(model, feedbackPolicy.mode);
    } else if (model.phase === "VERSUS_RESULT") {
      uiRects = this.drawVersusResult(model);
    } else if (model.phase === "RUN_GAMEOVER") {
      uiRects = this.drawGameOver(model);
    } else if (model.phase === "WORLD_MAP") {
      uiRects = this.drawWorldMap(model);
    } else if (model.phase === "PHONE") {
      if (this.prevPhase !== "PHONE") {
        this.phoneOpenMs = this.nowMs();
        this.phoneClosing = false;
      }
      uiRects = this.drawNotifications(model);
    } else if (model.phase === "PAUSE_MENU") {
      // Draw the game board (paused) underneath
      const boardUi = this.drawBoard(model, feedbackPolicy.mode);
      uiRects = {
        board: boardUi.board,
        boardGridSize: boardUi.boardGridSize,
      };
      this.drawDoomMeter(model);
      // Dark overlay
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(0, 0, CANVAS_WIDTH, this.currentLayout.canvasHeight);
      // Pause phone slide-up
      if (this.prevPhase !== "PAUSE_MENU") {
        this.pauseMenuOpenMs = this.nowMs();
        this.pauseMenuClosing = false;
      }
      const pauseUi = this.drawPauseMenu(model);
      Object.assign(uiRects, pauseUi);
    } else {
      const boardUi = this.drawBoard(model, feedbackPolicy.mode);
      uiRects = {
        board: boardUi.board,
        boardGridSize: boardUi.boardGridSize,
        rotateLeft: boardUi.rotateLeft,
        rotateRight: boardUi.rotateRight,
        keyTrigger: boardUi.keyTrigger,
        grooveMeter: boardUi.grooveMeter,
        grooveMixer: boardUi.grooveMixer,
        characterHub: boardUi.characterHub
      };

      if (model.powerupExecuting && model.powerupExecutionRemainingSec > 0) {
        const flicker = 0.12 + 0.1 * Math.sin(this.nowMs() / 35);
        ctx.fillStyle = `rgba(160,220,255,${flicker.toFixed(3)})`;
        ctx.fillRect(this.box, this.boardY, this.bps, this.bps);
        for (let i = 0; i < 18; i += 1) {
          const y = this.boardY + ((this.nowMs() * 0.18 + i * 17) % this.bps);
          ctx.fillStyle = `rgba(${120 + (i % 3) * 40},${180 + (i % 2) * 40},255,0.08)`;
          ctx.fillRect(this.box, y, this.bps, 1);
        }
      }

      this.drawDoomMeter(model);

      // Subtle whirlwind ambient animation on the grid
      if (model.phase === "RUN_PLAYABLE" && !this.reducedMotionPreferred) {
        this.updateAndDrawWhirlwinds();
      }
    }

    if (menuContentOffsetPhase) {
      ctx.restore();
      uiRects = offsetUiRects(uiRects, MENU_CONTENT_OFFSET_PX);
    }

    // Phone close animation: draw sliding-down phone overlay after leaving PHONE
    if (model.phase !== "PHONE" && this.prevPhase === "PHONE" && !this.phoneClosing) {
      this.phoneClosing = true;
      this.phoneCloseMs = this.nowMs();
    }
    if (this.phoneClosing) {
      const elapsed = this.nowMs() - this.phoneCloseMs;
      if (elapsed < 250) {
        this.drawNotifications(model);
      } else {
        this.phoneClosing = false;
      }
    }

    // Pause menu close animation
    if (model.phase !== "PAUSE_MENU" && this.prevPhase === "PAUSE_MENU" && !this.pauseMenuClosing) {
      this.pauseMenuClosing = true;
      this.pauseMenuCloseMs = this.nowMs();
    }
    if (this.pauseMenuClosing) {
      const elapsed = this.nowMs() - this.pauseMenuCloseMs;
      if (elapsed < 250) {
        ctx.fillStyle = "rgba(0,0,0,0.55)";
        ctx.fillRect(0, 0, CANVAS_WIDTH, this.currentLayout.canvasHeight);
        this.drawPauseMenu(model);
      } else {
        this.pauseMenuClosing = false;
      }
    }

    // Overlay Rafsby phone + bubble when visiting a building page during onboarding
    if (model.onboardingState?.visitingPage && model.phase !== "ONBOARDING_CALL") {
      this.drawOnboardingOverlay(model, uiRects);
    }

    this.prevPhase = model.phase;

    const burstGemRect = headerRects.gemShop ?? (model.phase === "GEM_SHOP" ? this.headerGemIconRect ?? undefined : undefined);
    const burstCoinRect = headerRects.coins ?? (model.phase === "GEM_SHOP" ? this.headerCoinIconRect ?? undefined : undefined);
    this.queueTitleGemBurst(model, uiRects.dailyLoginClaim, burstGemRect, burstCoinRect);
    this.drawTitleGemBursts(model);

    const debugViewModel = model.debugViewModel;
    if (shouldRenderDebugOverlay(debugViewModel)) {
      this.drawDebugOverlay(debugViewModel, model.debugSettings);
    }

    if (model.tutorialState?.active && (model.phase === "RUN_PLAYABLE" || model.phase === "RUN_RESOLVING" || model.phase === "RUN_LEVELUP")) {
      this.drawTutorialOverlay(model.tutorialState, uiRects);
    }

    // Rafsby speech bubble during how-to-play onboarding
    if (model.onboardingState?.active && model.onboardingState.section === "how-to-play" && model.onboardingStep) {
      this.drawOnboardingRafsby(ctx, model);
    }

    if (model.warningActive && model.phase !== "TITLE" && model.phase !== "RUN_GAMEOVER") {
      this.drawSprite(
        "fx_rotateflash",
        this.box + this.bps / 2 - 16,
        this.boardY + 2,
        32,
        16,
        () => {}
      );
    }

    const shouldDrawRunMessage =
      model.message.length > 0
      && !model.paused
      && (model.phase === "RUN_PLAYABLE" || model.phase === "RUN_RESOLVING" || model.phase === "RUN_LEVELUP");
    if (shouldDrawRunMessage) {
      const messageText = truncName(model.message, 28);
      const messageCenterX = this.box + this.bps / 2;
      const messageCenterY = this.boardY - 18;
      const bubbleH = 22;
      ctx.save();
      ctx.font = mFont("bold", 8);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const textW = this.measureTextWidth(messageText);
      const bubbleW = Math.min(this.bps - 12, Math.ceil(textW + 18));
      const bubbleX = Math.max(
        this.box + 6,
        Math.min(this.box + this.bps - bubbleW - 6, messageCenterX - bubbleW / 2)
      );
      const bubbleY = messageCenterY - bubbleH / 2;
      roundedRectPath(ctx, bubbleX, bubbleY, bubbleW, bubbleH, 8);
      ctx.fillStyle = "rgba(0,0,0,0.70)";
      ctx.fill();
      roundedRectPath(ctx, bubbleX, bubbleY, bubbleW, bubbleH, 8);
      ctx.strokeStyle = "#74b9ff";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = "#f8fbff";
      ctx.fillText(messageText, bubbleX + bubbleW / 2, messageCenterY + 0.5);
      ctx.restore();
    }
    const shouldDrawMenuMessage =
      model.message.length > 0
      && !model.paused
      && menuPhase
      && model.phase !== "RUN_GAMEOVER";
    if (shouldDrawMenuMessage) {
      this.drawMenuMessageBubble(model.message, model.phase);
    }

    const nowMs = this.nowMs();
    const frameDt = Math.min(0.1, (nowMs - this.lastFrameMs) / 1000);
    this.lastFrameMs = nowMs;

    if (model.collapseAnim?.active) {
      const stepIdx = model.collapseAnim.stepIndex;
      if (stepIdx !== this.lastCollapseStepIndex) {
        this.lastCollapseStepIndex = stepIdx;
        const step = model.collapseAnim.steps[stepIdx];
        if (step != null && step.clearedCells.length > 0) {
          this.addCollapseStepComboFloatingText(step);
          for (const cell of step.clearedCells) {
            const px = this.box + cell.col * this.tps + this.tps / 2;
            const py = this.boardY + cell.row * this.tps + this.tps / 2;
            const colorIdx = model.run?.board.cells[cell.row]?.[cell.col]?.tile?.color ?? 0;
            const particleCount = feedbackPolicy.mode === "off"
              ? 0
              : feedbackPolicy.mode === "reduced"
                ? (step.tier === 5 ? 3 : step.tier === 4 ? 2 : 1)
                : (step.tier === 5 ? 8 : step.tier === 4 ? 6 : 4);
            this.emitMatchBurst(px, py, TILE_COLORS[colorIdx % TILE_COLORS.length], particleCount);
          }
        }
        if (step != null && step.specialSpawns != null && step.specialSpawns.length > 0) {
          for (const spawn of step.specialSpawns) {
            const sx = this.box + spawn.col * this.tps + this.tps / 2;
            const sy = this.boardY + spawn.row * this.tps + this.tps / 2;
            const { label, color } = specialSpawnLabel(spawn.kind);
            addFloatingText(this.floatingTexts, label, sx, sy - this.tps * 0.4, color, 0.7, 8);
          }
        }
      }
    } else {
      this.lastCollapseStepIndex = -1;
    }

    if (model.lastScorePopup != null) {
      const popup = model.lastScorePopup;
      const px = this.box + popup.col * this.tps + this.tps / 2;
      const py = this.boardY + popup.row * this.tps;
      const label = popup.multiplier > 1 ? `+${popup.points} x${popup.multiplier}` : `+${popup.points}`;
      const color = popup.multiplier > 1 ? "#ffcc44" : "#ffd166";
      const scoreSize = popup.multiplier > 1 ? 10 : 8;
      addFloatingText(this.floatingTexts, label, px, py, color, 0.7, scoreSize);
    }

    if (model.phase === "RUN_LEVELUP") {
      this.drawLevelUpOverlay(model);
    }

    if (model.phase === "RUN_GAMEOVER") {
      this.emitGameOverBurst(CANVAS_WIDTH, 2);
    }

    if (this.particleDensity <= 0) {
      this.particles.particles.length = 0;
    } else {
      updateParticles(this.particles, frameDt);
    }
    updateFloatingTexts(this.floatingTexts, frameDt);
    if (model.phase === "VERSUS_MATCH") {
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      ctx.clip();
      drawParticles(ctx, this.particles);
      drawFloatingTexts(ctx, this.floatingTexts);
      ctx.restore();
    } else {
      drawParticles(ctx, this.particles);
      drawFloatingTexts(ctx, this.floatingTexts);
    }

    if (model.flashAlpha > 0) {
      const cw = this.canvas.width / this.dpr;
      const ch = this.canvas.height / this.dpr;
      ctx.fillStyle = `rgba(${model.flashColor},${model.flashAlpha.toFixed(3)})`;
      ctx.fillRect(0, 0, cw, ch);
    }

    if (model.radialMenu != null && model.radialMenu.active) {
      this.drawRadialMenu(ctx, model.radialMenu);
    }

    ctx.restore();

    const isVersusPhase =
      model.phase === "VERSUS_MATCH" ||
      model.phase === "VERSUS_LOBBY" ||
      model.phase === "VERSUS_COUNTDOWN" ||
      model.phase === "VERSUS_RESULT";
    if (!model.isOnline && !isVersusPhase) {
      const width = this.canvas.width / this.dpr;
      ctx.fillStyle = "#ff4444";
      ctx.beginPath();
      ctx.arc(width - 12, 12, 5, 0, Math.PI * 2);
      ctx.fill();
    }

    // Phase transition crossfade overlay
    if (this.transitionSnapshot != null) {
      this.transitionElapsed += frameDt * 1000;
      const progress = Math.min(1, this.transitionElapsed / CanvasRenderer.TRANSITION_DURATION_MS);
      const alpha = 1 - easeOutCubic(progress);
      if (alpha > 0.005) {
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.drawImage(this.transitionSnapshot, 0, 0);
        ctx.restore();
      }
      if (progress >= 1) {
        this.transitionSnapshot.close();
        this.transitionSnapshot = null;
      }
    }

    const scopedUiRects = applyPhaseScopedShopUiRects(model.phase, { ...headerRects, ...uiRects });
    this.frameNowMs = 0;
    return scopedUiRects;
  }

  private updateShellSurfaceMode(phase: string): void {
    const gameplaySurface = phase === "RUN_PLAYABLE" || phase === "RUN_REFILLING" || phase === "RUN_RESOLVING" || phase === "VERSUS_MATCH";
    const menuScroll = !gameplaySurface;
    this.canvasWrapper.dataset.phase = phase;
    if (this.shell != null) {
      this.shell.dataset.phase = phase;
      this.shell.classList.toggle("menu-scroll", menuScroll);
    }
    document.body.classList.toggle("allow-menu-scroll", menuScroll);
  }

  private syncShellSideCanvas(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D
  ): { width: number; height: number } {
    const width = Math.max(1, Math.floor(canvas.clientWidth));
    const height = Math.max(1, Math.floor(canvas.clientHeight));
    const targetPixelWidth = Math.max(1, Math.round(width * this.dpr));
    const targetPixelHeight = Math.max(1, Math.round(height * this.dpr));
    if (canvas.width !== targetPixelWidth || canvas.height !== targetPixelHeight) {
      canvas.width = targetPixelWidth;
      canvas.height = targetPixelHeight;
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.imageSmoothingEnabled = false;
    }
    return { width, height };
  }

  private drawShellSideFx(model: RenderModel): void {
    const showRaveFxByPhase =
      model.phase === "RUN_PLAYABLE"
      || model.phase === "RUN_RESOLVING"
      || model.phase === "RUN_LEVELUP"
      || model.phase === "VERSUS_MATCH";
    const sideFxMode = model.performance.sideFxMode;
    const showRaveFx = showRaveFxByPhase && sideFxMode !== "OFF";
    if (this.shellRaveFx != null) {
      const level = Math.max(1, Math.min(20, model.run?.level ?? 1));
      const songTimeSec = model.run?.beat.songTimeSec ?? (this.nowSec());
      this.shellRaveFx.render({
        active: showRaveFx,
        phase: model.phase,
        level,
        songTimeSec,
        grooveActive: model.run?.groove.active === true,
        sideFxMode,
        sideFxDensityMode: model.performance.sideFxDensityMode,
        backgroundStyleMode: model.performance.backgroundStyleMode,
        backgroundBloomMode: model.performance.backgroundBloomMode,
        backgroundGlowMode: model.performance.backgroundGlowMode,
        backgroundSaturationMode: model.performance.backgroundSaturationMode,
        backgroundMixMode: model.performance.backgroundMixMode,
        backgroundMotionMode: model.performance.backgroundMotionMode,
        musicEnabled: model.musicEnabled,
        musicVisual: model.musicVisual
      });
      return;
    }

    if (
      this.sideFxLeftCanvas == null ||
      this.sideFxRightCanvas == null ||
      this.sideFxLeftCtx == null ||
      this.sideFxRightCtx == null
    ) {
      return;
    }

    const leftSize = this.syncShellSideCanvas(this.sideFxLeftCanvas, this.sideFxLeftCtx);
    const rightSize = this.syncShellSideCanvas(this.sideFxRightCanvas, this.sideFxRightCtx);

    this.sideFxLeftCtx.clearRect(0, 0, leftSize.width, leftSize.height);
    this.sideFxRightCtx.clearRect(0, 0, rightSize.width, rightSize.height);

    const showSideFx = showRaveFx;
    if (!showSideFx) {
      return;
    }

    const level = Math.max(1, Math.min(20, model.run?.level ?? 1));
    const gameplayClockSec = model.run?.beat.songTimeSec ?? (this.nowSec());
    const drawTelemetry = shouldDrawSidePanelTelemetry(model.phase);
    if (model.phase === "RUN_LEVELUP" && model.levelUpDurationSec > 0) {
      const blend = clamp01(1 - model.levelUpTimer / model.levelUpDurationSec);
      const prevLevel = Math.max(1, level - 1);
      this.drawSideLaneTheme(this.sideFxLeftCtx, leftSize.width, leftSize.height, prevLevel, 1 - blend, false, gameplayClockSec, bpmForLevel(prevLevel));
      this.drawSideLaneTheme(this.sideFxLeftCtx, leftSize.width, leftSize.height, level, blend, false, gameplayClockSec, bpmForLevel(level));
      this.drawSideLaneTheme(this.sideFxRightCtx, rightSize.width, rightSize.height, prevLevel, 1 - blend, true, gameplayClockSec, bpmForLevel(prevLevel));
      this.drawSideLaneTheme(this.sideFxRightCtx, rightSize.width, rightSize.height, level, blend, true, gameplayClockSec, bpmForLevel(level));
      // Dim overlay
      this.sideFxLeftCtx.fillStyle = "rgba(0,0,0,0.3)";
      this.sideFxLeftCtx.fillRect(0, 0, leftSize.width, leftSize.height);
      this.sideFxRightCtx.fillStyle = "rgba(0,0,0,0.3)";
      this.sideFxRightCtx.fillRect(0, 0, rightSize.width, rightSize.height);
      // Telemetry cards
      if (drawTelemetry) {
        this.drawSidePanelTelemetry(this.sideFxLeftCtx, leftSize.width, leftSize.height, "left", model);
        this.drawSidePanelTelemetry(this.sideFxRightCtx, rightSize.width, rightSize.height, "right", model);
      }
      return;
    }

    this.drawSideLaneTheme(this.sideFxLeftCtx, leftSize.width, leftSize.height, level, 1, false, gameplayClockSec, bpmForLevel(level));
    this.drawSideLaneTheme(this.sideFxRightCtx, rightSize.width, rightSize.height, level, 1, true, gameplayClockSec, bpmForLevel(level));
    // Dim overlay
    this.sideFxLeftCtx.fillStyle = "rgba(0,0,0,0.3)";
    this.sideFxLeftCtx.fillRect(0, 0, leftSize.width, leftSize.height);
    this.sideFxRightCtx.fillStyle = "rgba(0,0,0,0.3)";
    this.sideFxRightCtx.fillRect(0, 0, rightSize.width, rightSize.height);
    // Telemetry cards
    if (drawTelemetry) {
      this.drawSidePanelTelemetry(this.sideFxLeftCtx, leftSize.width, leftSize.height, "left", model);
      this.drawSidePanelTelemetry(this.sideFxRightCtx, rightSize.width, rightSize.height, "right", model);
    }
  }

  private drawSidePanelTelemetry(
    ctx: CanvasRenderingContext2D,
    width: number, height: number,
    side: "left" | "right",
    model: RenderModel
  ): void {
    drawSidePanelTelemetryScreen(ctx, width, height, side, model);
  }

  private drawSideLaneTheme(
    ctx: CanvasRenderingContext2D,
    laneWidth: number,
    laneHeight: number,
    level: number,
    alpha: number,
    mirrored: boolean,
    gameplayClockSec: number,
    levelBpm: number
  ): void {
    drawSideLaneThemeScreen(ctx, laneWidth, laneHeight, level, alpha, mirrored, gameplayClockSec, levelBpm);
  }

  private drawBackgroundSection(x: number, y: number, w: number, h: number): void {
    const ctx = this.ctx;
    if (this.bgImage != null) {
      // Cover-fit: fill the target rect without distortion, cropping as needed
      const imgAspect = this.bgImage.naturalWidth / this.bgImage.naturalHeight;
      const targetAspect = w / h;
      let sx = 0, sy = 0, sw = this.bgImage.naturalWidth, sh = this.bgImage.naturalHeight;
      if (imgAspect < targetAspect) {
        const newH = sw / targetAspect;
        sy = (sh - newH) / 2;
        sh = newH;
      } else {
        const newW = sh * targetAspect;
        sx = (sw - newW) / 2;
        sw = newW;
      }
      ctx.drawImage(this.bgImage, sx, sy, sw, sh, x, y, w, h);
      return;
    }
    const grad = this.getFrameLinearGradient(
      `bg-section|${x}|${y}|${w}|${h}`,
      () => {
        const gradient = ctx.createLinearGradient(x, y, x + w, y + h);
        gradient.addColorStop(0, "#0b1325");
        gradient.addColorStop(1, "#131f38");
        return gradient;
      }
    );
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);
  }

  private drawBackground(model: RenderModel): void {
    const result = drawBackgroundScreen({
      ctx: this.ctx,
      canvasWidth: this.canvas.width / this.dpr,
      canvasHeight: this.canvas.height / this.dpr,
      dpr: this.dpr,
      gameplayBgImage: this.bgImage,
      titleBgImage: this.titleBgImage,
      backstageBgImage: this.backstageBgImage,
      dinerBgImage: this.dinerBgImage,
      shopBgImage: this.shopBgImage,
      gemShopBgImage: this.gemShopBgImage,
      apartmentBgImage: this.apartmentBgImage,
      worldMapImage: this.worldMapImage,
      soloSelectBgImage: this.soloSelectBgImage,
      soloModeBackgroundImages: this.soloModeBackgroundImages,
      buildingImages: this.buildingImages,
      buildingHover: this.buildingHover,
      walkingCharImages: this.walkingCharImages,
      walkers: this.walkers,
      interiorEnterMs: this.interiorEnterMs,
      prevWasInterior: this.prevWasInterior,
      cityBgCrop: this.cityBgCrop,
      nowMs: () => this.nowMs(),
      drawBackgroundSection: (x, y, w, h) => this.drawBackgroundSection(x, y, w, h),
      getFrameLinearGradient: (k, f) => this.getFrameLinearGradient(k, f),
    }, model);
    this.interiorEnterMs = result.interiorEnterMs;
    this.prevWasInterior = result.prevWasInterior;
  }

  private drawMenuVignette(): void {
    const ctx = this.ctx;
    const cx = CANVAS_WIDTH / 2;
    const ch = this.currentLayout.canvasHeight;
    const cy = ch * 0.38; // offset upward so logo stays bright
    const radius = Math.max(CANVAS_WIDTH, ch) * 0.85;

    // Ambient hue drift â€" colored glow that slowly shifts
    const hue = (this.nowMs() / 8000) % 360;
    const ambientGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius * 0.7);
    ambientGrad.addColorStop(0, `hsla(${hue}, 70%, 45%, 0.10)`);
    ambientGrad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = ambientGrad;
    ctx.fillRect(0, 0, CANVAS_WIDTH, ch);

    const grad = ctx.createRadialGradient(cx, cy, radius * 0.25, cx, cy, radius);
    grad.addColorStop(0, "rgba(0,0,0,0)");
    grad.addColorStop(1, "rgba(0,0,0,0.45)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, CANVAS_WIDTH, ch);
  }

  private drawHeader(model: RenderModel, feedbackMode: VersusFeedbackMode): Partial<UiRects> {
    const headerState: HeaderMutableState = {
      coinBounceMs: this.coinBounceMs,
      gemBounceMs: this.gemBounceMs,
      headerCoinIconRect: this.headerCoinIconRect,
      headerGemIconRect: this.headerGemIconRect,
      lastSeenGameplayLevel: this.lastSeenGameplayLevel,
      levelBadgeWiggleStartMs: this.levelBadgeWiggleStartMs,
    };
    const result = drawHeaderScreen(
      {
        ctx: this.ctx,
        state: headerState,
        reducedMotionPreferred: this.reducedMotionPreferred,
        nowMs: () => this.nowMs(),
        nowSec: () => this.nowSec(),
        measureTextWidth: (t) => this.measureTextWidth(t),
        measureTextWidthAtPx: (t, px, f) => this.measureTextWidthAtPx(t, px, f),
        drawCharacterPortrait: (p, x, y, w, h) => this.drawCharacterPortrait(p, x, y, w, h),
        loadCharacterImage: (p) => this.loadCharacterImage(p),
        drawCoinIcon: (x, y, s) => this.drawCoinIcon(x, y, s),
        drawGemIcon: (x, y, s) => this.drawGemIcon(x, y, s),
        drawHudStarIcon: (x, y, s) => this.drawHudStarIcon(x, y, s),
        drawHudCashIcon: (x, y, s) => this.drawHudCashIcon(x, y, s),
        drawTileSprite: (t, x, y, s) => this.drawTileSprite(t, x, y, s),
        drawCirclePauseButton: (r) => this.drawCirclePauseButton(r),
        drawComboBadge: (c, f) => this.drawComboBadge(c, f),
        drawGameplayCueStack: (m, l, fm) => this.drawGameplayCueStack(m, l, fm),
        valueBounceScale: (ms) => this.valueBounceScale(ms),
        getFrameLinearGradient: (k, f) => this.getFrameLinearGradient(k, f),
        uiSettingsIcon: this.uiSettingsIcon,
        settingsIconImage: this.settingsIconImage,
        uiContactIcon: this.uiContactIcon,
        playerIconImage: this.playerIconImage,
        rankVinylImages: this.rankVinylImages,
        heartLifeImage: this.heartLifeImage,
        heartLifePremiumImage: this.heartLifePremiumImage,
        titleLogo: this.titleLogo,
        titleLogoReady: this.titleLogoReady,
        checkCompleteImage: this.checkCompleteImage,
        characterImages: this.characterImages,
        menuSafeAreaTop: this._menuSafeAreaTop,
      },
      model,
      feedbackMode,
    );
    // Write back mutable state
    this.headerCoinIconRect = headerState.headerCoinIconRect;
    this.headerGemIconRect = headerState.headerGemIconRect;
    this.lastSeenGameplayLevel = headerState.lastSeenGameplayLevel;
    this.levelBadgeWiggleStartMs = headerState.levelBadgeWiggleStartMs;
    return result;
  }

  private drawGameplayCueStack(
    model: RenderModel,
    cueStackLayout: GameplayCueStackLayout | null,
    feedbackMode: VersusFeedbackMode
  ): void {
    if (cueStackLayout == null) {
      return;
    }

    if (model.cashPopupAmount > 0) {
      addFloatingText(
        this.floatingTexts,
        `+$${model.cashPopupAmount}`,
        cueStackLayout.cashPopupX,
        cueStackLayout.cashPopupY,
        "#9fffa4",
        0.7,
        GAMEPLAY_CUE_STACK_CASH_FONT_PX
      );
    }

    if (model.hypeText.length === 0 || model.hypeTimer <= 0) {
      return;
    }

    const ctx = this.ctx;
    const hypeAlpha = Math.min(1, model.hypeTimer * 2);
    const hypeScale = hypeScaleForFeedbackMode(feedbackMode, model.hypeTimer);
    const hypeY = model.cashPopupAmount > 0
      ? cueStackLayout.secondSlotY
      : cueStackLayout.firstSlotY;
    ctx.save();
    ctx.globalAlpha = hypeAlpha;
    ctx.translate(cueStackLayout.hypeTextX, hypeY);
    ctx.scale(hypeScale, hypeScale);
    ctx.fillStyle = "#fff";
    ctx.font = mFont("bold", GAMEPLAY_CUE_STACK_HYPE_FONT_PX);
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 3;
    ctx.strokeText(model.hypeText, 0, 0);
    ctx.fillText(model.hypeText, 0, 0);
    ctx.restore();
  }

  private addCollapseStepComboFloatingText(step: CollapseAnimState["steps"][number]): void {
    if (step.clearedCells.length === 0 || step.specialFx != null) {
      return;
    }

    const comboNum = step.chainIndex + 1;
    const comboLabel = `${comboNum} COMBO`;
    const comboCenter = step.clearedCells.reduce(
      (acc: { x: number; y: number }, cell) => {
        acc.x += this.box + cell.col * this.tps + this.tps / 2;
        acc.y += this.boardY + cell.row * this.tps + this.tps / 2;
        return acc;
      },
      { x: 0, y: 0 }
    );
    const comboX = comboCenter.x / Math.max(1, step.clearedCells.length);
    const comboY = comboCenter.y / Math.max(1, step.clearedCells.length);
    const comboColor =
      comboNum >= 5 ? "#ff4444" :
      comboNum >= 4 ? "#ff8833" :
      comboNum >= 3 ? "#ffcc00" :
      comboNum >= 2 ? "#44ddff" :
                      "#8ce6ff";
    const comboSize = Math.min(14, 9 + comboNum);
    addFloatingText(this.floatingTexts, comboLabel, comboX, comboY, comboColor, 0.8, comboSize);
  }

  private drawComboBadge(comboCount: number, comboFill: number): void {
    const ctx = this.ctx;
    const boardRight = this.box + this.bps;
    const comboY = this.boardY - 8;
    const comboCaptionY = comboY - 7;
    const comboLabel = `x${comboCount}`;
    const barW = 50;
    const barGap = 4;

    ctx.textBaseline = "middle";
    ctx.font = mFont("bold", 7);
    const labelW = this.measureTextWidth(comboLabel);
    const totalW = labelW + barGap + barW;
    const startX = boardRight - totalW;
    const centerX = startX + totalW / 2;

    ctx.textAlign = "center";
    ctx.font = mFont("bold", 4);
    ctx.fillStyle = COMBO_BADGE_COLOR;
    ctx.fillText("COMBO", centerX, comboCaptionY);

    ctx.textAlign = "left";
    ctx.font = mFont("bold", 7);
    ctx.fillText(comboLabel, startX, comboY);

    const barX = startX + labelW + barGap;
    const barH = 3;
    const barY = comboY - barH / 2;
    ctx.fillStyle = "rgba(24,48,74,0.95)";
    roundedRectPath(ctx, barX, barY, barW, barH, 1.5);
    ctx.fill();
    if (comboFill > 0) {
      ctx.fillStyle = COMBO_BADGE_COLOR;
      roundedRectPath(ctx, barX, barY, barW * comboFill, barH, 1.5);
      ctx.fill();
    }
  }


  private drawCoinIcon(x: number, y: number, size: number): void {
    if (this.coinIconImage != null) {
      this.ctx.drawImage(this.coinIconImage, x, y, size, size);
      return;
    }
    const ctx = this.ctx;
    const cx = x + size / 2;
    const cy = y + size / 2;
    const r = size * 0.4; // tighter circle with padding inside bounding box
    // Outer coin circle
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = "#fff14f";
    ctx.fill();
    ctx.strokeStyle = "#d3a100";
    ctx.lineWidth = 1;
    ctx.stroke();
    // Inner ring
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.65, 0, Math.PI * 2);
    ctx.strokeStyle = "#d3a100";
    ctx.lineWidth = 0.8;
    ctx.stroke();
    // "C" letter
    ctx.fillStyle = "#8b6914";
    ctx.font = `bold ${Math.floor(r * 1.0)}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("C", cx, cy + 0.5);
  }

  /** Draw a HUD cash icon at exact pixel size. */
  private drawHudCashIcon(x: number, y: number, size: number): void {
    if (this.cashIconImage != null) {
      this.ctx.drawImage(this.cashIconImage, x, y, size, size);
    }
  }

  /** Draw a HUD star icon (gold 5-point star) at exact pixel size. */
  private drawHudStarIcon(x: number, y: number, size: number): void {
    const ctx = this.ctx;
    if (this.starScoreImage != null) {
      ctx.drawImage(this.starScoreImage, x, y, size, size);
      return;
    }
    // Fallback: procedural star
    const cx = x + size / 2;
    const cy = y + size / 2;
    const outerR = size * 0.48;
    const innerR = outerR * 0.4;
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const outerAngle = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
      const innerAngle = outerAngle + Math.PI / 5;
      if (i === 0) ctx.moveTo(cx + outerR * Math.cos(outerAngle), cy + outerR * Math.sin(outerAngle));
      else ctx.lineTo(cx + outerR * Math.cos(outerAngle), cy + outerR * Math.sin(outerAngle));
      ctx.lineTo(cx + innerR * Math.cos(innerAngle), cy + innerR * Math.sin(innerAngle));
    }
    ctx.closePath();
    ctx.fillStyle = "#ffd700";
    ctx.fill();
    ctx.strokeStyle = "#b8960a";
    ctx.lineWidth = 0.8;
    ctx.stroke();
  }

  private drawGemIcon(x: number, y: number, size: number): void {
    const ctx = this.ctx;
    if (this.gemImage != null) {
      ctx.drawImage(this.gemImage, x, y, size, size);
      return;
    }
    // Fallback: procedural diamond
    const cx = x + size / 2;
    const cy = y + size / 2;
    const hw = size * 0.45;
    const hh = size * 0.5;
    ctx.beginPath();
    ctx.moveTo(cx, cy - hh);
    ctx.lineTo(cx + hw, cy);
    ctx.lineTo(cx, cy + hh);
    ctx.lineTo(cx - hw, cy);
    ctx.closePath();
    ctx.fillStyle = "#64d8ff";
    ctx.fill();
    ctx.strokeStyle = "#2a8ab8";
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  private drawBoard(
    model: RenderModel,
    feedbackMode: VersusFeedbackMode = "full",
    options: DrawBoardOptions = {}
  ): DrawBoardResult {
    const layout = {
      tps: this.tps,
      bps: this.bps,
      box: this.box,
      boardY: this.boardY,
    };
    const result = drawBoardScreen(
      {
        ctx: this.ctx,
        boardLayout: layout,
        reducedMotionPreferred: this.reducedMotionPreferred,
        nowMs: () => this.nowMs(),
        nowSec: () => this.nowSec(),
        layout: {
          spinsIndicatorY: this.currentLayout.spinsIndicatorY,
          controlRowY: this.currentLayout.controlRowY,
          characterBaseY: this.currentLayout.characterBaseY,
        },
        turntableLeftActiveImage: this.turntableLeftActiveImage,
        turntableRightActiveImage: this.turntableRightActiveImage,
        turntableLeftImage: this.turntableLeftImage,
        turntableRightImage: this.turntableRightImage,
        mixerImage: this.mixerImage,
        maybeEmitRowRushAmbientParticles: (t, f, g) => this.maybeEmitRowRushAmbientParticles(t, f, g),
        drawRotationBoard: (a, t, g) => this.drawRotationBoard(a, t, g),
        drawSwapBoard: (b, s, e, t, g) => this.drawSwapBoard(b, s, e, t, g),
        drawCollapseBoard: (b, c, f, t, g) => this.drawCollapseBoard(b, c, f, t, g),
        drawBoardSurface: (b, o) => this.drawBoardSurface(b, o),
        drawTileSprite: (t, x, y, s) => this.drawTileSprite(t, x, y, s),
        drawCharacterPortrait: (p, x, y, w, h) => this.drawCharacterPortrait(p, x, y, w, h),
        drawPortraitGrooveActiveFx: (c, x, y, s) => this.drawPortraitGrooveActiveFx(c, x, y, s),
      },
      model,
      feedbackMode,
      options
    );
    this.tps = layout.tps;
    return result;
  }

  private drawAuthGate(model: RenderModel): UiRects {
    return drawAuthGateScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        loadingCityBgImage: this.loadingCityBgImage,
        titleLogo: this.titleLogo,
        titleLogoReady: this.titleLogoReady,
        drawBtn: (rect, label, opts) => this.drawBtn(rect, label, opts),
        drawHourglass: (cx, cy, size, color) => this.drawHourglass(cx, cy, size, color),
      },
      model,
    );
  }

  private assignSoloLeaderboardModeRect(uiRects: UiRects, mode: SoloMode, rect: Rect): void {
    if (mode === "classic") {
      uiRects.soloLeaderboardClassic = rect;
      return;
    }
    if (mode === "row-rush") {
      uiRects.soloLeaderboardRowRush = rect;
      return;
    }
    if (mode === "clearout") {
      uiRects.soloLeaderboardClearout = rect;
      return;
    }
    if (mode === "color-hunt") {
      uiRects.soloLeaderboardColorHunt = rect;
      return;
    }
    uiRects.soloLeaderboardTurboMaxxing = rect;
  }

  private drawSoloSelectorLogo(mode: SoloMode, rect: Rect, mobile: boolean): void {
    const ctx = this.ctx;
    const logo = this.soloSelectorLogoImages[mode];
    if (logo != null) {
      drawImageContain(ctx, logo, rect);
      return;
    }

    const fallbackLines = soloModeLabel(mode).split(" ");
    const fontSize = mobile ? 4.5 : 5.5;
    const lineHeight = mobile ? 8 : 9;
    const startY = rect.y + (rect.h - fallbackLines.length * lineHeight) / 2;
    ctx.fillStyle = "#f5fbff";
    ctx.font = mFont("bold", fontSize);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    for (let i = 0; i < fallbackLines.length; i += 1) {
      const line = fallbackLines[i];
      if (!line) {
        continue;
      }
      ctx.fillText(line, rect.x + rect.w / 2, startY + i * lineHeight);
    }
  }

  private drawSoloLeaderboardModeChips(
    uiRects: UiRects,
    originX: number,
    originY: number,
    width: number,
    mobile: boolean,
    selectedMode: SoloMode
  ): number {
    const chipGap = mobile ? 6 : 8;
    const chipH = mobile ? 14 : 16;
    const chipW = Math.floor((width - chipGap) / 2);
    const rows = Math.ceil(SOLO_MODES.length / 2);
    for (let i = 0; i < SOLO_MODES.length; i += 1) {
      const mode = SOLO_MODES[i]!;
      const col = i % 2;
      const row = Math.floor(i / 2);
      const rect: Rect = {
        x: originX + col * (chipW + chipGap),
        y: originY + row * (chipH + chipGap),
        w: chipW,
        h: chipH
      };
      this.assignSoloLeaderboardModeRect(uiRects, mode, rect);
      this.drawLeaderboardTab(
        rect,
        soloModeLabel(mode),
        selectedMode === mode,
        soloModeAccent(mode),
        mobile ? 3.4 : 4.25
      );
    }
    return originY + rows * chipH + Math.max(0, rows - 1) * chipGap;
  }

  private drawSoloSelect(model: RenderModel): UiRects {
    const slideState: SoloSlideState = {
      soloPrevIdx: this.soloPrevIdx,
      soloPrevMode: this.soloPrevMode,
      soloSlideStartMs: this.soloSlideStartMs,
      soloSlideDir: this.soloSlideDir,
    };
    const result = drawSoloSelectScreen(
      {
        ctx: this.ctx,
        slideState,
        drawBtn: (rect, label, opts) => this.drawBtn(rect, label, opts),
        drawCircleCloseButton: (rect) => this.drawCircleCloseButton(rect),
        drawCircleArrowButton: (rect, dir) => this.drawCircleArrowButton(rect, dir),
        drawSoloSelectorLogo: (mode, rect, mobile) => this.drawSoloSelectorLogo(mode, rect, mobile),
        wrapTextToWidth: (text, maxW, maxLines) => this.wrapTextToWidth(text, maxW, maxLines),
        updateHoverAnimations: (rects) => this.updateHoverAnimations(rects),
      },
      model,
    );
    this.soloPrevIdx = slideState.soloPrevIdx;
    this.soloPrevMode = slideState.soloPrevMode;
    this.soloSlideStartMs = slideState.soloSlideStartMs;
    this.soloSlideDir = slideState.soloSlideDir;
    return result;
  }

  private drawTitle(model: RenderModel): UiRects {
    return drawTitleScreen(
      {
        ctx: this.ctx,
        nativeSafeAreaTop: this._nativeSafeAreaTop,
        cityBgCrop: this.cityBgCrop,
        canvasHeight: this.currentLayout.canvasHeight,
        hoverPos: this.hoverPos,
        buildingHover: this.buildingHover,
        phoneImage: this.phoneImage,
        uiPadlockIcon: this.uiPadlockIcon,
        drawBtn: (r, l, o) => this.drawBtn(r, l, o),
        getHoverProgress: (r) => this.getHoverProgress(r),
        nowSec: () => this.nowSec(),
        nowMs: () => this.nowMs(),
        drawDailyLoginEntryButton: (m, r, g) => this.drawDailyLoginEntryButton(m, r, g),
        drawDailyLoginModal: (m, h, g) => this.drawDailyLoginModal(m, h, g),
        updateHoverAnimations: (r) => this.updateHoverAnimations(r),
      },
      model,
    );
  }

  private drawDailyLoginEntryButton(model: RenderModel, rect: Rect, isGuestUser: boolean): void {
    const ctx = this.ctx;
    const status = model.dailyLoginStatus;
    const canClaimToday = !isGuestUser && status?.canClaimToday === true;
    const alreadyClaimed = !isGuestUser && status != null && !status.canClaimToday && status.currentDay > 0;
    const hover = this.getHoverProgress(rect);
    const badgeSize = 110;
    const badgeRect: Rect = { x: rect.x + (rect.w - badgeSize) / 2, y: rect.y + 28, w: badgeSize, h: badgeSize };
    ctx.save();

    const crateImg = alreadyClaimed ? this.crateOpenImage : this.crateClosedImage;
    if (crateImg != null) {
      ctx.save();
      const isHovered = hover > 0;
      if (canClaimToday) {
        const glowPulse = 0.5 + 0.5 * Math.sin(this.nowSec() * 3);
        ctx.shadowColor = "#ffffff";
        ctx.shadowBlur = 12 + glowPulse * 10;
      } else if (isHovered) {
        ctx.shadowColor = "#ffffff";
        ctx.shadowBlur = 16 + Math.sin(this.nowSec() * 4) * 4;
      }
      if (alreadyClaimed) {
        ctx.filter = "grayscale(0.85) brightness(0.5)";
      }
      ctx.drawImage(crateImg, badgeRect.x, badgeRect.y, badgeRect.w, badgeRect.h);
      ctx.restore();

      // Countdown timer overlay when already claimed
      if (alreadyClaimed) {
        const now = new Date();
        const utcMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
        const remainMs = Math.max(0, utcMs - now.getTime());
        const hrs = Math.floor(remainMs / 3_600_000);
        const mins = Math.floor((remainMs % 3_600_000) / 60_000);
        const secs = Math.floor((remainMs % 60_000) / 1000);
        const timeStr = `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
        const cx = badgeRect.x + badgeRect.w / 2;
        const cy = badgeRect.y + badgeRect.h / 2;
        ctx.fillStyle = "#ffffff";
        ctx.font = mFont("bold", 5);
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(timeStr, cx, cy);
      }
    }

    ctx.restore();
  }

  private drawDailyLoginModal(model: RenderModel, canvasH: number, isGuestUser: boolean): Partial<UiRects> {
    return drawDailyLoginModalScreen(
      {
        ctx: this.ctx,
        canvasH,
        isGuestUser,
        nowSec: () => this.nowSec(),
        measureTextWidth: (text) => this.measureTextWidth(text),
        drawBtn: (rect, label, options) => this.drawBtn(rect, label, options),
        drawCircleCloseButton: (rect) => this.drawCircleCloseButton(rect),
        drawCoinIcon: (x, y, size) => this.drawCoinIcon(x, y, size),
        drawGemIcon: (x, y, size) => this.drawGemIcon(x, y, size),
        drawDailyLoginStamp: (rect) => this.drawDailyLoginStamp(rect),
        drawGlowPulse: (rect, color, maxBlur) => this.drawGlowPulse(rect, color, maxBlur),
        crateClosedImage: this.crateClosedImage,
        crateOpenImage: this.crateOpenImage,
      },
      model
    );
  }

  private drawDailyLoginStamp(rect: Rect): void {
    const ctx = this.ctx;
    const stampRadius = Math.min(rect.w, rect.h) * 0.28;
    ctx.save();
    ctx.translate(rect.x + rect.w * 0.66, rect.y + rect.h * 0.62);
    ctx.rotate(-0.26);
    ctx.fillStyle = "rgba(120,18,44,0.18)";
    ctx.beginPath();
    ctx.arc(0, 0, stampRadius - 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#ff7c98";
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(0, 0, stampRadius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, 0, Math.max(3, stampRadius - 4), 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = "#ffd8df";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = mFont("bold", 3.5);
    ctx.fillText("BEAST", 0, -4);
    ctx.font = mFont("bold", 3);
    ctx.fillText("STAMP", 0, 4);
    ctx.restore();
  }

  // ── Daily Rewards Grid Screen ──────────────────────────────────────
  private drawDailyRewards(model: RenderModel): UiRects {
    return drawDailyRewardsScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        crateClosedImage: this.crateClosedImage,
        crateOpenImage: this.crateOpenImage,
        nowSec: () => this.nowSec(),
        drawCircleCloseButton: (rect) => this.drawCircleCloseButton(rect),
        drawCoinIcon: (x, y, size) => this.drawCoinIcon(x, y, size),
        drawGemIcon: (x, y, size) => this.drawGemIcon(x, y, size),
        staggerAlpha: (index, delayMs, durationMs) => this.staggerAlpha(index, delayMs, durationMs),
      },
      model,
    );
  }

  private drawDailyRewardsOpen(model: RenderModel): UiRects {
    return drawDailyRewardsOpenScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        crateClosedImage: this.crateClosedImage,
        nowSec: () => this.nowSec(),
        drawCoinIcon: (x, y, size) => this.drawCoinIcon(x, y, size),
        drawGemIcon: (x, y, size) => this.drawGemIcon(x, y, size),
      },
      model,
    );
  }

  private drawWorldMap(_model: RenderModel): UiRects {
    return drawWorldMapScreen({
      ctx: this.ctx,
      canvasHeight: this.currentLayout.canvasHeight,
      nowSec: () => this.nowSec(),
      cloudImage: this.cloudImage,
      drawCircleCloseButton: (r) => this.drawCircleCloseButton(r),
    });
  }

  private drawNotifications(model: RenderModel): UiRects {
    return drawNotificationsScreen({
      ctx: this.ctx,
      canvasHeight: this.currentLayout.canvasHeight,
      phoneClosing: this.phoneClosing,
      phoneCloseMs: this.phoneCloseMs,
      phoneOpenMs: this.phoneOpenMs,
      nowMs: () => this.nowMs(),
      phoneTabIcons: this.phoneTabIcons,
      drawPhoneMessages: (c, m, u, sx, sy, sw, sh, cx, cw, cr, p, met) =>
        this.drawPhoneMessages(c, m, u, sx, sy, sw, sh, cx, cw, cr, p, met),
      drawPhoneFriends: (c, m, u, sx, sy, sw, sh, cx, cw, cr, met) =>
        this.drawPhoneFriends(c, m, u, sx, sy, sw, sh, cx, cw, cr, met),
      drawPhoneHighscores: (c, m, u, sx, sy, sw, sh, cx, cw, cr, met) =>
        this.drawPhoneHighscores(c, m, u, sx, sy, sw, sh, cx, cw, cr, met),
      drawPhoneSettings: (c, m, u, sx, sy, sw, sh, cx, cw, cr, met) =>
        this.drawPhoneSettings(c, m, u, sx, sy, sw, sh, cx, cw, cr, met),
      drawPhoneWallet: (c, m, u, sx, sy, sw, sh, cx, cw, cr, met) =>
        this.drawPhoneWallet(c, m, u, sx, sy, sw, sh, cx, cw, cr, met),
    }, model);
  }

  // ── Pause Menu (Phone-style) ──

  private drawPauseMenu(model: RenderModel): UiRects {
    return drawPauseMenuScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        pauseMenuClosing: this.pauseMenuClosing,
        pauseMenuCloseMs: this.pauseMenuCloseMs,
        pauseMenuOpenMs: this.pauseMenuOpenMs,
        nowMs: () => this.nowMs(),
        drawPhoneSettings: (ctx, m, rects, scrX, scrY, scrW, scrH, cX, cW, cR, metrics) =>
          this.drawPhoneSettings(ctx, m, rects, scrX, scrY, scrW, scrH, cX, cW, cR, metrics),
      },
      model,
    );
  }

  private drawPhoneMessages(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number, px: number,
    metrics: PhoneMetrics,
  ): void {
    drawPhoneMessagesScreen(
      {
        drawPhoneMessageDetail: (c, m, msg, sX, sY, sW, sH, cX, cW, cR, met, ur) =>
          this.drawPhoneMessageDetail(c, m, msg, sX, sY, sW, sH, cX, cW, cR, met, ur),
        drawNotificationPanel: (rect, options) => this.drawNotificationPanel(rect, options),
        staggerAlpha: (index, delayMs, durationMs) => this.staggerAlpha(index, delayMs, durationMs),
        wrapTextToWidth: (text, maxW, maxLines) => this.wrapTextToWidth(text, maxW, maxLines),
        trimTextToWidth: (text, maxW) => this.trimTextToWidth(text, maxW),
      },
      ctx, model, uiRects,
      scrX, scrY, scrW, scrH,
      contentX, contentW, contentRight, px,
      metrics,
    );
  }

  private drawPhoneMessageDetail(
    ctx: CanvasRenderingContext2D, _model: RenderModel,
    msg: import("../social/messageService").InboxMessage,
    scrX: number, scrY: number, scrW: number, _scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics, uiRects: UiRects,
  ): void {
    drawPhoneMessageDetailScreen(
      {
        formatTimeAgo: (iso) => this.formatTimeAgo(iso),
        wrapTextToWidth: (text, maxW, maxLines) => this.wrapTextToWidth(text, maxW, maxLines),
      },
      ctx, _model, msg,
      scrX, scrY, scrW, _scrH,
      contentX, contentW, contentRight,
      metrics, uiRects,
    );
  }

  private formatTimeAgo(isoTimestamp: string): string {
    try {
      const diffMs = Date.now() - new Date(isoTimestamp).getTime();
      const diffSec = Math.floor(diffMs / 1000);
      if (diffSec < 60) return "NOW";
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `${diffMin}M`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}H`;
      const diffDays = Math.floor(diffHr / 24);
      return `${diffDays}D`;
    } catch {
      return "";
    }
  }

  private drawPhoneFriends(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics,
  ): void {
    const deps: PhoneFriendsDeps = {
      drawPhoneFriendDetail: (c, m, u, sx, sy, sw, sh, cx, cw, cr, met, friend) =>
        this.drawPhoneFriendDetail(c, m, u, sx, sy, sw, sh, cx, cw, cr, met, friend),
      drawPhoneAddFriendInline: (c, m, u, sx, sy, sw, sh, cx, cw, cr, met) =>
        this.drawPhoneAddFriendInline(c, m, u, sx, sy, sw, sh, cx, cw, cr, met),
      drawPhoneReferFriend: (c, m, u, sx, sy, sw, sh, cx, cw, cr, met) =>
        this.drawPhoneReferFriend(c, m, u, sx, sy, sw, sh, cx, cw, cr, met),
      drawNotificationPanel: (rect, opts) => this.drawNotificationPanel(rect, opts),
      drawNotificationEmptyState: (bounds, title, subtitle, color, met) =>
        this.drawNotificationEmptyState(bounds, title, subtitle, color, met),
      drawNotificationChip: (rect, label, accent, fontPx) =>
        this.drawNotificationChip(rect, label, accent, fontPx),
      staggerAlpha: (index, delayMs, durationMs) => this.staggerAlpha(index, delayMs, durationMs),
      drawCircularPortrait: (path, x, y, size) => this.drawCircularPortrait(path, x, y, size),
      drawNotificationPageNavButton: (rect, label, desktop, style, textColor) =>
        this.drawNotificationPageNavButton(rect, label, desktop, style, textColor),
      rafsbyPortrait: this.rafsbyPortrait,
    };
    drawPhoneFriendsScreen(deps, ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, metrics);
  }

  private drawPhoneReferFriend(
    ctx: CanvasRenderingContext2D, _model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, _contentRight: number,
    metrics: PhoneMetrics,
  ): void {
    drawPhoneReferFriendScreen(
      {
        drawCrateIcon: (x, y, s) => { if (this.crateClosedImage) ctx.drawImage(this.crateClosedImage, x, y, s, s); },
        drawGemIcon: (x, y, s) => { if (this.gemImage) ctx.drawImage(this.gemImage, x, y, s, s); },
      },
      ctx, _model, uiRects,
      scrX, scrY, scrW, scrH,
      contentX, contentW, _contentRight,
      metrics,
    );
  }

  private drawPhoneAddFriendInline(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, _scrH: number,
    contentX: number, contentW: number, _contentRight: number,
    metrics: PhoneMetrics,
  ): void {
    drawPhoneAddFriendInlineScreen(
      {},
      ctx, model, uiRects,
      scrX, scrY, scrW, _scrH,
      contentX, contentW, _contentRight,
      metrics,
    );
  }

  private drawPhoneFriendDetail(
    ctx: CanvasRenderingContext2D, _model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, _contentRight: number,
    metrics: PhoneMetrics,
    friend: { odId: string; username: string; level: number; online: boolean; equippedSkin: string; isNpc?: boolean; versusWins?: number; versusLosses?: number; versusElo?: number },
  ): void {
    drawPhoneFriendDetailScreen(
      {
        drawCircularPortrait: (p, x, y, s) => this.drawCircularPortrait(p, x, y, s),
        rafsbyPortrait: this.rafsbyPortrait,
      },
      ctx, _model, uiRects,
      scrX, scrY, scrW, scrH,
      contentX, contentW, _contentRight,
      metrics, friend,
    );
  }

  private drawPhoneHighscores(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics,
  ): void {
    drawPhoneHighscoresScreen(
      {
        trimTextToWidth: (t, w) => this.trimTextToWidth(t, w),
        drawLeaderboardTab: (r, l, a, c, f) => this.drawLeaderboardTab(r, l, a, c, f),
        drawSoloLeaderboardModeChips: (u, x, y, w, m, s) => this.drawSoloLeaderboardModeChips(u, x, y, w, m, s),
        drawHourglass: (cx, cy, s, c) => this.drawHourglass(cx, cy, s, c),
        drawNotificationEmptyState: (r, t, s, c, m) => this.drawNotificationEmptyState(r, t, s, c, m),
        drawNotificationPanel: (r, o) => this.drawNotificationPanel(r, o),
        leaderboardStaggerAlpha: (i, d, dur) => this.leaderboardStaggerAlpha(i, d, dur),
        leaderboardTierColor: (t) => this.leaderboardTierColor(t),
        nowSec: () => this.nowSec(),
        leaderboardScreenEnterTime: this.leaderboardScreenEnterTime,
        resolveLeaderboardScroll: (config) => this.resolveLeaderboardScroll(config),
      },
      ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, metrics,
    );
  }

  private drawPhoneSettings(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics,
  ): void {
    drawPhoneSettingsScreen(
      {
        drawPhoneEditUsername: (c, m, u, sx, sy, sw, sh, cx, cw, mt) =>
          this.drawPhoneEditUsername(c, m, u, sx, sy, sw, sh, cx, cw, mt),
        staggerAlpha: (i, d, dur) => this.staggerAlpha(i, d, dur),
        drawNotificationPanel: (r, o) => this.drawNotificationPanel(r, o),
        drawNotificationChip: (r, l, a, f) => this.drawNotificationChip(r, l, a, f),
        measureTextWidth: (t) => this.measureTextWidth(t),
        aboutHeadLogoImage: this.aboutHeadLogoImage,
        aboutWordmarkLogoImage: this.aboutWordmarkLogoImage,
      },
      ctx, model, uiRects,
      scrX, scrY, scrW, scrH,
      contentX, contentW, contentRight,
      metrics,
    );
  }

  private drawPhoneEditUsername(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, _scrH: number,
    contentX: number, contentW: number,
    metrics: PhoneMetrics,
  ): void {
    drawPhoneEditUsernameScreen(
      {},
      ctx, model, uiRects,
      scrX, scrY, scrW, _scrH,
      contentX, contentW,
      metrics,
    );
  }

  private drawPhoneWallet(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics,
  ): void {
    drawPhoneWalletScreen(
      {
        drawNotificationPanel: (r, o) => this.drawNotificationPanel(r, o),
        drawNotificationChip: (r, l, a, f) => this.drawNotificationChip(r, l, a, f),
        drawCharacterPortrait: (p, x, y, w, h) => this.drawCharacterPortrait(p, x, y, w, h),
        drawCoinIcon: (x, y, s) => { if (this.coinIconImage) ctx.drawImage(this.coinIconImage, x, y, s, s); },
        drawGemIcon: (x, y, s) => { if (this.gemImage) ctx.drawImage(this.gemImage, x, y, s, s); },
      },
      ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, metrics,
    );
  }

  // ── Onboarding Rafsby in-game speech bubble (how-to-play) ──────────
  private drawOnboardingRafsby(ctx: CanvasRenderingContext2D, model: RenderModel): void {
    const step = model.onboardingStep;
    if (!step) return;

    ctx.font = mFontPlain(5);
    const layout = resolveOnboardingRafsbyLayout({
      canvasWidth: CANVAS_WIDTH,
      message: step.message,
      measureTextWidth: (text) => this.measureTextWidthAtPx(text, 5, "")
    });

    // Draw portrait
    if (this.rafsbyPortrait) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(
        layout.portraitX + layout.portraitSize / 2,
        layout.portraitY + layout.portraitSize / 2,
        layout.portraitSize / 2,
        0,
        Math.PI * 2
      );
      ctx.clip();
      ctx.drawImage(this.rafsbyPortrait, layout.portraitX, layout.portraitY, layout.portraitSize, layout.portraitSize);
      ctx.restore();
      // Ring
      ctx.strokeStyle = "#4dff88";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(
        layout.portraitX + layout.portraitSize / 2,
        layout.portraitY + layout.portraitSize / 2,
        layout.portraitSize / 2 + 1,
        0,
        Math.PI * 2
      );
      ctx.stroke();
    }

    // Bubble background
    ctx.fillStyle = "rgba(10,16,32,0.9)";
    ctx.beginPath();
    ctx.roundRect(layout.bubbleX, layout.bubbleY, layout.bubbleW, layout.bubbleH, 6);
    ctx.fill();
    ctx.strokeStyle = "rgba(77,255,136,0.4)";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Bubble text
    ctx.fillStyle = "#e0e8ff";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    for (let i = 0; i < layout.lines.length; i++) {
      ctx.fillText(
        layout.lines[i],
        layout.bubbleX + layout.bubblePad,
        layout.bubbleY + layout.bubblePad + i * layout.lineH
      );
    }
  }

  // ── Onboarding Call (incoming call + world tour messages) ──────────
  // Renders the title screen with the Rafsby phone image in the corner
  // and speech bubbles popping out from it.
  private drawOnboardingCall(model: RenderModel): UiRects {
    return drawOnboardingCallScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        rafsbyPortrait: this.rafsbyPortrait,
        heartLifeImage: this.heartLifeImage,
        heartLifePremiumImage: this.heartLifePremiumImage,
        nowMs: () => this.nowMs(),
        measureTextWidth: (text) => this.measureTextWidth(text),
        drawTitle: (m) => this.drawTitle(m),
      },
      model,
    );
  }

  private drawOnboardingOverlay(model: RenderModel, uiRects: UiRects): void {
    drawOnboardingOverlayScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        nowMs: () => this.nowMs(),
        rafsbyPhoneImage: this.rafsbyPhoneImage,
        phoneImage: this.phoneImage,
      },
      model,
      uiRects,
    );
  }

  private drawShop(model: RenderModel): UiRects {
    const slideState = {
      shopPrevIdx: this.shopPrevIdx,
      shopPrevSkinPath: this.shopPrevSkinPath,
      shopSlideStartMs: this.shopSlideStartMs,
      shopSlideDir: this.shopSlideDir,
    };
    const result = drawShopScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        nowMs: () => this.nowMs(),
        gemShopDoorImage: this.gemShopDoorImage,
        slideState,
        drawBtn: (rect, label, options) => this.drawBtn(rect, label, options),
        drawCircleCloseButton: (rect) => this.drawCircleCloseButton(rect),
        drawGemIcon: (x, y, size) => this.drawGemIcon(x, y, size),
        drawCharacterPortrait: (path, x, y, w, h, align) => this.drawCharacterPortrait(path, x, y, w, h, align),
        drawCircleArrowButton: (rect, dir) => this.drawCircleArrowButton(rect, dir),
        measureTextWidth: (text) => this.measureTextWidth(text),
      },
      model
    );
    this.shopPrevIdx = slideState.shopPrevIdx;
    this.shopPrevSkinPath = slideState.shopPrevSkinPath;
    this.shopSlideStartMs = slideState.shopSlideStartMs;
    this.shopSlideDir = slideState.shopSlideDir;
    return result;
  }

  private drawGemShop(model: RenderModel): UiRects {
    const refs = {
      headerCoinIconRect: this.headerCoinIconRect,
      headerGemIconRect: this.headerGemIconRect,
      gemPackImageRects: this.gemPackImageRects,
    };
    const result = drawGemShopScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        nowMs: () => this.nowMs(),
        gemPackImages: this.gemPackImages,
        mutableRefs: refs,
        drawBtn: (rect, label, options) => this.drawBtn(rect, label, options),
        drawCircleCloseButton: (rect) => this.drawCircleCloseButton(rect),
        drawCoinIcon: (x, y, size) => this.drawCoinIcon(x, y, size),
        drawGemIcon: (x, y, size) => this.drawGemIcon(x, y, size),
        drawHourglass: (cx, cy, size, color) => this.drawHourglass(cx, cy, size, color),
        measureTextWidth: (text) => this.measureTextWidth(text),
        staggerAlpha: (index, delayMs, durationMs) => this.staggerAlpha(index, delayMs, durationMs),
      },
      model
    );
    // Sync mutable refs back
    this.headerCoinIconRect = refs.headerCoinIconRect;
    this.headerGemIconRect = refs.headerGemIconRect;
    return result;
  }

  private drawAdGate(_model: RenderModel): UiRects {
    const ctx = this.ctx;
    const centerX = CANVAS_WIDTH / 2;
    const btnW = 180;
    const btnH = menuBtnHLg();
    const gap = menuGap();

    // Dark backdrop — extend above translated origin to cover header
    ctx.fillStyle = "rgba(0,0,0,0.82)";
    ctx.fillRect(0, -MENU_CONTENT_OFFSET_PX, CANVAS_WIDTH, this.currentLayout.canvasHeight + MENU_CONTENT_OFFSET_PX);

    // Close button (top-right)
    const closeSize = uiCloseSize();
    const closeRect: Rect = { x: CANVAS_WIDTH - closeSize - UI_EDGE_PAD, y: UI_CLOSE_Y, w: closeSize, h: closeSize };
    this.drawCircleCloseButton(closeRect);

    // Title
    ctx.fillStyle = "#ff4444";
    ctx.font = mFont("bold", 10);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("OUT OF LIVES", centerX, 130);

    // Subtitle
    ctx.fillStyle = "#c8d8f0";
    ctx.font = mFontPlain(5);
    ctx.fillText("Watch an ad or connect a wallet", centerX, 152);
    ctx.fillText("to restore your lives.", centerX, 164);

    // Buttons — lobby style, centered
    const btnX = centerX - btnW / 2;
    const btnStartY = 190;
    const watchAd: Rect = { x: btnX, y: btnStartY, w: btnW, h: btnH };
    const connectWallet: Rect = { x: btnX, y: btnStartY + btnH + gap, w: btnW, h: btnH };

    this.drawBtn(watchAd, "WATCH AD", {
      fill: "rgba(42,26,61,0.85)",
      stroke: "rgba(255,153,102,0.6)",
      text: "#ffe0cc",
      font: mFont("bold", 5),
      variant: "lobby",
    });
    this.drawBtn(connectWallet, "CONNECT WALLET", {
      fill: "rgba(20,36,54,0.85)",
      stroke: "rgba(108,200,255,0.6)",
      text: "#e9f7ff",
      font: mFont("bold", 5),
      variant: "lobby",
    });

    // Helper text
    ctx.fillStyle = "#667788";
    ctx.font = mFontPlain(4);
    ctx.textAlign = "center";
    ctx.fillText("NFT or token holders get unlimited lives.", centerX, connectWallet.y + btnH + 20);

    return {
      adGateWatchAd: watchAd,
      adGateConnectWallet: connectWallet,
      mainMenu: closeRect,
    };
  }

  private drawTokenShop(model: RenderModel): UiRects {
    return drawTokenShopScreen({
      ctx: this.ctx,
      drawBtn: (r, l, o) => this.drawBtn(r, l, o),
      drawCircleCloseButton: (r) => this.drawCircleCloseButton(r),
      drawGemIcon: (x, y, s) => this.drawGemIcon(x, y, s),
      drawHourglass: (cx, cy, s, c) => this.drawHourglass(cx, cy, s, c),
    }, model);
  }

  private drawSettings(model: RenderModel): UiRects {
    return drawSettingsScreen(
      {
        ctx: this.ctx,
        drawBtn: (r, l, o) => this.drawBtn(r, l, o),
        drawMenuPageNavButton: (r, l, s) => this.drawMenuPageNavButton(r, l, s),
        settingsSlideOffset: () => this.settingsSlideOffset(),
        staggerAlpha: (i, d, dur) => this.staggerAlpha(i, d, dur),
        aboutHeadLogoImage: this.aboutHeadLogoImage,
        aboutWordmarkLogoImage: this.aboutWordmarkLogoImage,
      },
      model,
    );
  }

  private drawProfile(model: RenderModel): UiRects {
    const equippedId = (() => {
      try {
        return localStorage.getItem("bitbeast.equipped.v1") ?? getCachedEquipped();
      } catch {
        return getCachedEquipped();
      }
    })();
    const filter = model.profileFilter;

    // Detect equip changes for flash effect
    if (this.lastEquippedId !== "" && equippedId !== this.lastEquippedId) {
      this.lastEquipFlashMs = this.nowMs();
    }
    this.lastEquippedId = equippedId;

    const uiRects: UiRects = {};

    const profileItems = buildProfileVisibleItems(
      filter,
      model.ownedSkins,
      model.nftAvatars,
      equippedId,
    );

    if (model.profileViewMode === "browse") {
      return this.drawProfileBrowse(model, profileItems, uiRects, filter);
    }
    return this.drawProfileGrid(model, profileItems, uiRects, filter);
  }

  private drawProfileFilterTabs(
    ctx: CanvasRenderingContext2D,
    filter: "all" | "wallet",
    _hasWallet: boolean,
    curY: number,
    rowX: number,
    rowW: number,
    uiRects: UiRects
  ): void {
    ctx.font = mFont("bold", 5);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const pillY = curY + 1;

    if (isMobileViewport()) {
      const { allRect, walletRect } = resolveMobileProfileFilterTabRects(
        CANVAS_WIDTH,
        pillY,
        this.measureTextWidth("ALL"),
        this.measureTextWidth("WALLET")
      );
      const rects = [
        { rect: allRect, active: filter === "all", label: "ALL", activeFill: "#2a4a6e" },
        { rect: walletRect, active: filter === "wallet", label: "WALLET", activeFill: "#3a2a6e" }
      ];

      for (const { rect, active, label, activeFill } of rects) {
        roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, rect.h / 2);
        ctx.fillStyle = active ? activeFill : "#142436";
        ctx.fill();
        roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, rect.h / 2);
        ctx.strokeStyle = "#6cc8ff";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.fillStyle = active ? "#e9f7ff" : "#6a7a94";
        ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2);
      }

      uiRects.profileFilterAll = allRect;
      uiRects.profileFilterWallet = walletRect;
      return;
    }

    const pillH = 20;
    const pillW = 90;
    const pillR = pillH / 2;
    const pillX = rowX + rowW - pillW;
    const halfW = pillW / 2;

    roundedRectPath(ctx, pillX, pillY, pillW, pillH, pillR);
    ctx.fillStyle = "#142436";
    ctx.fill();

    ctx.save();
    roundedRectPath(ctx, pillX, pillY, pillW, pillH, pillR);
    ctx.clip();
    const activeX = filter === "all" ? pillX : pillX + halfW;
    ctx.fillStyle = filter === "all" ? "#2a4a6e" : "#3a2a6e";
    ctx.fillRect(activeX, pillY, halfW, pillH);
    ctx.restore();

    roundedRectPath(ctx, pillX, pillY, pillW, pillH, pillR);
    ctx.strokeStyle = "#6cc8ff";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    const midY = pillY + pillH / 2;

    ctx.fillStyle = filter === "all" ? "#e9f7ff" : "#6a7a94";
    ctx.fillText("ALL", pillX + halfW / 2, midY);

    ctx.fillStyle = filter === "wallet" ? "#e9f7ff" : "#6a7a94";
    ctx.fillText("WALLET", pillX + halfW + halfW / 2, midY);

    // Hit rects
    uiRects.profileFilterAll = { x: pillX, y: pillY, w: halfW, h: pillH };
    uiRects.profileFilterWallet = { x: pillX + halfW, y: pillY, w: halfW, h: pillH };
  }

  private drawProfileBrowse(
    model: RenderModel,
    profileItems: ProfileVisibleItem[],
    uiRects: UiRects,
    _filter: "all" | "wallet"
  ): UiRects {
    return drawProfileBrowseScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        nowMs: () => this.nowMs(),
        drawCircleCloseButton: (rect) => this.drawCircleCloseButton(rect),
        drawCirclePencilButton: (rect) => this.drawCirclePencilButton(rect),
        drawCharacterPortrait: (path, x, y, w, h, anchor?) => this.drawCharacterPortrait(path, x, y, w, h, anchor as "center" | "bottom" | undefined),
        leaderboardTierColor: (tier) => this.leaderboardTierColor(tier),
        rankVinylImages: this.rankVinylImages,
        soloSelectorLogoImages: this.soloSelectorLogoImages,
      },
      model, profileItems, uiRects, _filter,
    );
  }

  private drawProfileGrid(
    model: RenderModel,
    profileItems: ProfileVisibleItem[],
    uiRects: UiRects,
    filter: "all" | "wallet"
  ): UiRects {
    return drawProfileGridScreen(
      {
        ctx: this.ctx,
        nowMs: () => this.nowMs(),
        drawCircleCloseButton: (rect) => this.drawCircleCloseButton(rect),
        drawCirclePencilButton: (rect) => this.drawCirclePencilButton(rect),
        drawCircleArrowButton: (rect, dir) => this.drawCircleArrowButton(rect, dir),
        drawCharacterPortrait: (path, x, y, w, h) => this.drawCharacterPortrait(path, x, y, w, h),
        drawProfileFilterTabs: (ctx, f, hasWallet, curY, rowX, rowW, ur) =>
          this.drawProfileFilterTabs(ctx, f, hasWallet, curY, rowX, rowW, ur),
        staggerAlpha: (index, delayMs, durationMs) => this.staggerAlpha(index, delayMs, durationMs),
        lastEquipFlashMs: this.lastEquipFlashMs,
      },
      model, profileItems, uiRects, filter,
    );
  }

  private drawProfileSettings(model: RenderModel): UiRects {
    return drawProfileSettingsScreen(
      {
        ctx: this.ctx,
        drawBtnStagger: (r, l, i, o) => this.drawBtnStagger(r, l, i, o),
        drawCircleCloseButton: (r) => this.drawCircleCloseButton(r),
      },
      model,
    );
  }

  private drawLeaderboardScreen(model: RenderModel): UiRects {
    return drawLeaderboardScreen(
      {
        ctx: this.ctx,
        nowMs: () => this.nowMs(),
        nowSec: () => this.nowSec(),
        animState: { leaderboardScreenEnterTime: this.leaderboardScreenEnterTime },
        drawBtnStagger: (rect, label, index, options) => this.drawBtnStagger(rect, label, index, options),
        drawHourglass: (cx, cy, size, color) => this.drawHourglass(cx, cy, size, color),
        drawLeaderboardTab: (rect, label, active, accent, fontPx?) => this.drawLeaderboardTab(rect, label, active, accent, fontPx),
        drawSoloLeaderboardModeChips: (uiRects, originX, originY, width, mobile, selectedMode) =>
          this.drawSoloLeaderboardModeChips(uiRects, originX, originY, width, mobile, selectedMode),
        leaderboardStaggerAlpha: (index, delayMs, durationMs) => this.leaderboardStaggerAlpha(index, delayMs, durationMs),
        leaderboardTierColor: (tier) => this.leaderboardTierColor(tier),
        resolveLeaderboardScroll: (config) => this.resolveLeaderboardScroll(config),
      },
      model
    );
  }

  private drawDoomMeter(model: RenderModel): void {
    drawDoomMeterScreen(
      {
        ctx: this.ctx,
        box: this.box,
        boardY: this.boardY,
        bps: this.bps,
        nowMs: () => this.nowMs(),
        drawHourglass: (cx, cy, size, color) => this.drawHourglass(cx, cy, size, color),
        reducedMotionPreferred: this.reducedMotionPreferred,
        gameplayDoomDangerActive: this.gameplayDoomDangerActive,
      },
      model
    );
  }

  private drawLevelUpOverlay(model: RenderModel): void {
    const ctx = this.ctx;
    const centerX = this.box + this.bps / 2;
    const centerY = this.boardY + this.bps / 2;
    const progress = clamp01(
      model.levelUpDurationSec <= 0 ? 1 : 1 - model.levelUpTimer / model.levelUpDurationSec
    );
    const scale = 1 + Math.sin(progress * Math.PI) * 0.15;
    const rowRushSummary = model.soloMode === "row-rush" ? model.rowRushRoundSummary ?? null : null;
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.scale(scale, scale);

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.strokeStyle = "rgba(0,0,0,0.85)";
    if (rowRushSummary != null) {
      const scoreLabel = `+${rowRushSummary.score.toLocaleString()}`;
      ctx.fillStyle = "#ffffff";
      ctx.font = `bold 14px ${GAME_FONT}`;
      ctx.lineWidth = 4;
      ctx.strokeText(`ROUND ${rowRushSummary.round}`, 0, -18);
      ctx.fillText(`ROUND ${rowRushSummary.round}`, 0, -18);

      ctx.font = `bold 24px ${GAME_FONT}`;
      ctx.strokeText(scoreLabel, 0, 6);
      ctx.fillStyle = rowRushSummary.perfectBonusAwarded ? "#ffd75a" : "#ffffff";
      ctx.fillText(scoreLabel, 0, 6);

      if (rowRushSummary.perfectBonusAwarded) {
        ctx.font = `bold 10px ${GAME_FONT}`;
        ctx.fillStyle = "#9dffb4";
        ctx.strokeText("PERFECT +10,000", 0, 26);
        ctx.fillText("PERFECT +10,000", 0, 26);
      }
    } else {
      const levelLabel = `LEVEL ${model.run?.level ?? ""}`;
      ctx.fillStyle = "#ffffff";
      ctx.font = `bold 24px ${GAME_FONT}`;
      ctx.lineWidth = 5;
      ctx.strokeText(levelLabel, 0, 0);
      ctx.fillText(levelLabel, 0, 0);
    }
    ctx.shadowBlur = 0;

    ctx.restore();
  }

  /** Draw the radial menu overlay (called at end of draw()). */
  private drawRadialMenu(ctx: CanvasRenderingContext2D, menu: RadialMenuState): void {
    drawRadialMenuScreen(
      { nowMs: () => this.nowMs(), powerupIconImages: this.powerupIconImages },
      ctx,
      menu,
    );
  }

  private drawVersusMatch(model: RenderModel, feedbackMode: VersusFeedbackMode): UiRects {
    // Mobile portrait versus mode â€" single board with trap row below
    if (isMobileViewport()) {
      return this.drawMobileVersusMatch(model, feedbackMode);
    }

    const ctx = this.ctx;
    const vm = model.versusMatch;
    const frameDtSec = this.lastFrameMs > 0
      ? Math.min(0.1, (this.nowMs() - this.lastFrameMs) / 1000)
      : (1 / 60);
    const sampledFrameMs = frameDtSec * 1000;
    if (model.performance.opponentRenderMode === "SIMPLIFIED") {
      this.opponentAdaptivePerfState = updateOpponentAdaptivePerfState(
        this.opponentAdaptivePerfState,
        sampledFrameMs,
        frameDtSec
      );
    } else {
      this.opponentAdaptivePerfState = createOpponentAdaptivePerfState();
    }
    const effectiveOpponentMode = resolveEffectiveOpponentDisplayMode({
      requestedMode: model.performance.opponentRenderMode,
      isMobile: false,
      adaptivePerfState: this.opponentAdaptivePerfState
    });

    // Left panel: draw the local board using the normal board renderer
    // The local board stays at 0,0 with normal 360px width â€" no translation needed
    const settingsRect = resolveGameplayTopRightHudRect(26);
    const boardUi = this.drawBoard(model, feedbackMode);
    this.drawDoomMeter(model);

    // Divider + opponent panel
    if (vm != null) {
      drawDivider(
        ctx,
        vm.opponentStats.displayName,
        vm.opponentStats.connected,
        vm.disconnectCountdownSec
      );

      if (effectiveOpponentMode.renderTier === "FULL") {
        this.drawMirroredOpponentPanel(model, vm, feedbackMode);
      } else if (effectiveOpponentMode.renderTier === "SIMPLIFIED") {
        this.drawOpponentBoardSimplified(vm.opponentBoard);
      } else {
        this.drawOpponentStateSyncPanel(vm);
      }

      // Player skin portrait on opponent panel (bottom-right corner, mirrored)
      {
        const plSize = 160;
        const plTimeSec = this.nowSec();
        const plPulse = musicPulseForLevel(plTimeSec, model.run?.level ?? 1);
        const plBob = (plPulse - 0.5) * 3;
        const plBaseY = this.currentLayout.characterBaseY;
        const plY = plBaseY + plBob;
        const plX = VERSUS_OPPONENT_X + VERSUS_PANEL_WIDTH - plSize + 50;
        const localChar = model.equippedCharacter;
        ctx.save();
        ctx.translate(plX + plSize / 2, 0);
        ctx.scale(-1, 1);
        ctx.translate(-(plX + plSize / 2), 0);
        this.drawCharacterPortrait(localChar.imageSmallPath, plX, plY, plSize, plSize);
        ctx.restore();
        if (model.run?.groove.active === true) {
          this.drawPortraitGrooveActiveFx(ctx, plX, plY, plSize);
        }
      }

      // Disconnect overlay on opponent board
      if (!vm.opponentStats.connected && vm.disconnectCountdownSec != null) {
        drawDisconnectOverlay(ctx, vm.disconnectCountdownSec);
      }

      // Fog overlay on LOCAL board when fogged by opponent's trap
      if (vm.fogActive) {
        this.drawLocalFogOverlay(ctx, vm.fogRemainingSec);
      }

      // Opponent portrait hub (bottom-right, mirrored to match player portrait)
      const opSize = 160;
      const opTimeSec = this.nowSec();
      const opRunLevel = model.run?.level ?? 1;
      const opPulse = musicPulseForLevel(opTimeSec, opRunLevel);
      const opBob = (opPulse - 0.5) * 3;
      const opBaseY = this.currentLayout.characterBaseY;
      const opY = opBaseY + opBob;
      const opX = VERSUS_PANEL_WIDTH - opSize + 50; // mirrored: overflow right edge
      const remoteCharacter = getCharacterById(vm.opponentStats.equippedSkin) ?? model.equippedCharacter;
      ctx.save();
      ctx.translate(opX + opSize / 2, 0);
      ctx.scale(-1, 1);
      ctx.translate(-(opX + opSize / 2), 0);
      if (vm.opponentStats.equippedSkin === "rafsby" && this.rafsbyPortrait) {
        ctx.drawImage(this.rafsbyPortrait, opX, opY, opSize, opSize);
      } else {
        this.drawCharacterPortrait(remoteCharacter.imageSmallPath, opX, opY, opSize, opSize);
      }
      ctx.restore();

      // Trap FX overlay on opponent portrait
      if (vm.lastSentTrap != null) {
        this.drawTrapFxOverlay(ctx, vm.lastSentTrap.kind, vm.lastSentTrap.ageSec, opX, opY, opSize);
      }

      // Ambient state FX on opponent portrait
      this.drawOpponentStateFx(ctx, vm.opponentStats, opX, opY, opSize);

      const opponentHubRect: Rect | undefined = vm.trapActionsEnabled
        ? { x: VERSUS_PANEL_WIDTH - 70, y: opBaseY + 10, w: 70, h: 100 }
        : undefined;

      // â"€â"€ Bottom stats HUD aligned with turntables â"€â"€
      // Align stats X with turntable edges (tripleLeftX = (360 - 182) / 2 = 89)
      const statsTripleW = 58 + 4 + 58 + 4 + 58; // vinylSize + ctrlGap + mixerW + ctrlGap + vinylSize
      const statsLeftX = Math.floor((VERSUS_PANEL_WIDTH - statsTripleW) / 2);
      const statsRightX = statsLeftX + statsTripleW;
      this.drawVersusBottomStats(
        ctx, model, vm,
        statsLeftX, statsRightX
      );
      this.drawVersusSuddenDeathBanner(vm, false);
      const optionsUi = this.drawVersusOptionsOverlay(model, settingsRect, VERSUS_CANVAS_WIDTH);

      return {
        board: boardUi.board,
        boardGridSize: boardUi.boardGridSize,
        settings: settingsRect,
        ...optionsUi,
        rotateLeft: boardUi.rotateLeft,
        rotateRight: boardUi.rotateRight,
        keyTrigger: boardUi.keyTrigger,
        grooveMeter: boardUi.grooveMeter,
        grooveMixer: boardUi.grooveMixer,
        characterHub: boardUi.characterHub,
        opponentHub: opponentHubRect
      };
    }

    const optionsUi = this.drawVersusOptionsOverlay(model, settingsRect, VERSUS_CANVAS_WIDTH);
    // Fallback: no versus data yet, just show local board
    return {
      board: boardUi.board,
      boardGridSize: boardUi.boardGridSize,
      settings: settingsRect,
      ...optionsUi,
      rotateLeft: boardUi.rotateLeft,
      rotateRight: boardUi.rotateRight,
      keyTrigger: boardUi.keyTrigger,
      grooveMeter: boardUi.grooveMeter,
      grooveMixer: boardUi.grooveMixer,
      characterHub: boardUi.characterHub
    };
  }


  /**
   * Mobile portrait versus layout: local board only (no opponent board),
   * with trap buttons in a horizontal row below powerups, and a compact
   * opponent info strip at the bottom.
   */
  private drawMobileVersusMatch(model: RenderModel, feedbackMode: VersusFeedbackMode): UiRects {
    const ctx = this.ctx;
    const vm = model.versusMatch;
    const canvasW = VERSUS_MOBILE_CANVAS_WIDTH;

    // Shift board + HUD + groove + controls up; skins/stats stay at bottom
    const versusLift = 36;
    ctx.save();
    ctx.translate(0, -versusLift);
    const boardUi = this.drawBoard(model, feedbackMode, { hideCornerCharacter: true });
    ctx.restore();

    // Adjust touch rects to match shifted positions
    const liftRect = (r: Rect | undefined): Rect | undefined =>
      r ? { x: r.x, y: r.y - versusLift, w: r.w, h: r.h } : undefined;

    const settingsRect = resolveGameplayTopRightHudRect(26);

    // Draw player character at its normal bottom position (not shifted)
    const gameChar = model.equippedCharacter;
    const gpSize = 160;
    const gpX = -50;
    const gpTimeSec = this.nowSec();
    const gpPulse = musicPulseForLevel(gpTimeSec, model.run?.level ?? 1);
    const gpBob = (gpPulse - 0.5) * 3;
    const gpBaseY = this.currentLayout.characterBaseY;
    this.drawCharacterPortrait(gameChar.imageSmallPath, gpX, gpBaseY + gpBob, gpSize, gpSize);

    // Groove active FX on player portrait (mirrors opponent aura)
    if (model.run?.groove.active === true) {
      this.drawPortraitGrooveActiveFx(ctx, gpX, gpBaseY + gpBob, gpSize);
    }

    const characterHubRect: Rect = { x: 0, y: gpBaseY + 10, w: 70, h: 100 };

    if (vm != null) {
      // Fog overlay on LOCAL board (shifted with board)
      if (vm.fogActive) {
        ctx.save();
        ctx.translate(0, -versusLift);
        this.drawLocalFogOverlay(ctx, vm.fogRemainingSec);
        ctx.restore();
      }

      // Opponent portrait hub (bottom-right, mirrored)
      const opSize = 160;
      const opTimeSec = this.nowSec();
      const opRunLevel = model.run?.level ?? 1;
      const opPulse = musicPulseForLevel(opTimeSec, opRunLevel);
      const opBob = (opPulse - 0.5) * 3;
      const opBaseY = this.currentLayout.characterBaseY;
      const opY = opBaseY + opBob;
      const opX = canvasW - opSize + 50;
      const remoteCharacter = getCharacterById(vm.opponentStats.equippedSkin) ?? model.equippedCharacter;
      ctx.save();
      ctx.translate(opX + opSize / 2, 0);
      ctx.scale(-1, 1);
      ctx.translate(-(opX + opSize / 2), 0);
      if (vm.opponentStats.equippedSkin === "rafsby" && this.rafsbyPortrait) {
        ctx.drawImage(this.rafsbyPortrait, opX, opY, opSize, opSize);
      } else {
        this.drawCharacterPortrait(remoteCharacter.imageSmallPath, opX, opY, opSize, opSize);
      }
      ctx.restore();

      // Trap FX overlay on opponent portrait
      if (vm.lastSentTrap != null) {
        this.drawTrapFxOverlay(ctx, vm.lastSentTrap.kind, vm.lastSentTrap.ageSec, opX, opY, opSize);
      }

      // Ambient state FX on opponent portrait
      this.drawOpponentStateFx(ctx, vm.opponentStats, opX, opY, opSize);

      const opponentHubRect: Rect | undefined = vm.trapActionsEnabled
        ? { x: canvasW - 70, y: opBaseY + 10, w: 70, h: 100 }
        : undefined;

      if (vm.syncHealth !== "HEALTHY") {
        const badgeW = 142;
        const liftedBoardTop = boardUi.board != null ? boardUi.board.y - versusLift : 84;
        const badgeX = Math.round((canvasW - badgeW) / 2);
        const badgeY = Math.max(12, liftedBoardTop - 28);
        this.drawSyncStatusBadge(vm, badgeX, badgeY);
      }

      // â"€â"€ Two-row versus stats at portrait level â"€â"€
      this.drawMobileVersusStatsRows(ctx, model, vm, opBaseY);
      this.drawVersusSuddenDeathBanner(vm, true);
      const optionsUi = this.drawVersusOptionsOverlay(model, settingsRect, VERSUS_MOBILE_CANVAS_WIDTH);

      return {
        board: liftRect(boardUi.board),
        boardGridSize: boardUi.boardGridSize,
        settings: settingsRect,
        ...optionsUi,
        rotateLeft: liftRect(boardUi.rotateLeft),
        rotateRight: liftRect(boardUi.rotateRight),
        keyTrigger: liftRect(boardUi.keyTrigger),
        grooveMeter: liftRect(boardUi.grooveMeter),
        grooveMixer: liftRect(boardUi.grooveMixer),
        characterHub: characterHubRect,
        opponentHub: opponentHubRect
      };
    }

    const optionsUi = this.drawVersusOptionsOverlay(model, settingsRect, VERSUS_MOBILE_CANVAS_WIDTH);
    // Fallback: no versus data yet, just show local board
    return {
      board: liftRect(boardUi.board),
      boardGridSize: boardUi.boardGridSize,
      settings: settingsRect,
      ...optionsUi,
      rotateLeft: liftRect(boardUi.rotateLeft),
      rotateRight: liftRect(boardUi.rotateRight),
      keyTrigger: liftRect(boardUi.keyTrigger),
      grooveMeter: liftRect(boardUi.grooveMeter),
      grooveMixer: liftRect(boardUi.grooveMixer),
      characterHub: characterHubRect
    };
  }

  private drawSyncStatusBadge(vm: VersusMatchRenderData, x: number, y: number): void {
    drawSyncStatusBadgeScreen(this.ctx, vm, x, y);
  }

  private drawVersusSuddenDeathBanner(vm: VersusMatchRenderData, mobile: boolean): void {
    drawVersusSuddenDeathBannerScreen(this.ctx, vm, mobile, this.canvas.width / this.dpr);
  }

  private drawVersusOptionsOverlay(model: RenderModel, settingsRect: Rect, canvasWidth: number): Partial<UiRects> {
    if (!model.versusOptionsOpen) {
      return {};
    }

    const ctx = this.ctx;
    const mobile = isMobileViewport();
    const panelPad = mobile ? 5 : 6;
    const rowH = mobile ? 18 : 20;
    const rowGap = 4;
    const panelRect = resolveVersusOptionsPanelRect(settingsRect, mobile, canvasWidth);
    const panelW = panelRect.w;
    const panelX = panelRect.x;
    const panelY = panelRect.y;
    const panelH = panelRect.h;

    roundedRectPath(ctx, panelX, panelY, panelW, panelH, 5);
    ctx.fillStyle = "rgba(12,18,33,0.95)";
    ctx.fill();
    roundedRectPath(ctx, panelX, panelY, panelW, panelH, 5);
    ctx.strokeStyle = "rgba(114,150,204,0.85)";
    ctx.lineWidth = 1;
    ctx.stroke();

    const rowX = panelX + panelPad;
    const rowW = panelW - panelPad * 2;
    const musicRect: Rect = { x: rowX, y: panelY + panelPad, w: rowW, h: rowH };
    const hapticsRect: Rect = { x: rowX, y: musicRect.y + rowH + rowGap, w: rowW, h: rowH };
    const forfeitRect: Rect = { x: rowX, y: hapticsRect.y + rowH + rowGap, w: rowW, h: rowH };

    this.drawBtn(musicRect, `MUSIC: ${model.musicEnabled ? "ON" : "OFF"}`, {
      fill: "#1a2a4c",
      stroke: model.musicEnabled ? "#69e6ff" : "#6b7ea6",
      font: mFont("bold", mobile ? 5 : 6),
      variant: "lobby"
    });
    this.drawBtn(hapticsRect, `HAPTICS: ${model.hapticsEnabled ? "ON" : "OFF"}`, {
      fill: "#1f2e44",
      stroke: model.hapticsEnabled ? "#6bff88" : "#6b7ea6",
      font: mFont("bold", mobile ? 5 : 6),
      variant: "lobby"
    });
    this.drawBtn(forfeitRect, "FORFEIT", {
      fill: "#3a1616",
      stroke: "#ff7a7a",
      font: mFont("bold", mobile ? 5 : 6),
      variant: "lobby"
    });

    return {
      music: musicRect,
      settingsHaptics: hapticsRect,
      versusForfeit: forfeitRect,
      versusOptionsBounds: panelRect
    };
  }

  private drawVersusBottomStats(
    ctx: CanvasRenderingContext2D,
    model: RenderModel,
    vm: VersusMatchRenderData,
    leftStatsX: number,
    rightStatsX: number
  ): void {
    drawVersusBottomStatsScreen(ctx, model, vm, leftStatsX, rightStatsX, this.currentLayout.characterBaseY, (x, y, s) => this.drawHudStarIcon(x, y, s));
  }

  private drawMobileVersusStatsRows(
    ctx: CanvasRenderingContext2D,
    model: RenderModel,
    vm: VersusMatchRenderData,
    baseY: number
  ): void {
    drawMobileVersusStatsRowsScreen(ctx, model, vm, baseY, (x, y, s) => this.drawHudStarIcon(x, y, s), (x, y, s) => this.drawHudCashIcon(x, y, s));
  }

  private drawMirroredOpponentPanel(
    model: RenderModel,
    vm: VersusMatchRenderData,
    feedbackMode: VersusFeedbackMode
  ): void {
    const ctx = this.ctx;
    const localRun = model.run;
    if (localRun == null || vm.opponentBoard == null) {
      this.drawOpponentBoardAnimated(vm);
      return;
    }

    const oppLevel = Math.max(1, Math.floor(vm.opponentStats.level));
    const oppLevelTarget = versusScoreTargetForLevel(oppLevel);
    const oppLevelScore = Math.round(oppLevelTarget * clamp01(vm.opponentStats.levelProgressPct));
    const oppDoomClockMax = doomClockCapForLevel(oppLevel);
    const oppDoomClockSec = oppDoomClockMax * (1 - clamp01(vm.opponentStats.doomPct));
    const remoteCharacter = getCharacterById(vm.opponentStats.equippedSkin)
      ?? (vm.opponentStats.equippedSkin === "rafsby" ? {
        id: "rafsby", name: "Rafsby", rarity: "EPIC" as const, priceGems: 0,
        color: "#ffd700", icon: "R",
        imagePath: "/assets/characters/rafsby.png",
        imageSmallPath: "/assets/icons/rafsby-portrait.png",
      } : null)
      ?? model.equippedCharacter;

    const opponentRun: RunState = {
      ...localRun,
      board: vm.opponentBoard,
      level: oppLevel,
      levelScore: Math.max(0, oppLevelScore),
      totalScore: Math.max(0, Math.floor(vm.opponentStats.score)),
      doomClockSec: Math.max(0, Math.min(oppDoomClockMax, oppDoomClockSec)),
      rotationKeys: Math.max(0, Math.floor(vm.opponentStats.rotationKeys)),
      cashBalance: Math.max(0, Math.floor(vm.opponentStats.cash)),
      powerupInventory: {
        HINT: vm.opponentStats.powerups.HINT,
        ROWRIP: vm.opponentStats.powerups.ROWRIP,
        COLCUT: vm.opponentStats.powerups.COLCUT,
        TRANSMUTE: vm.opponentStats.powerups.TRANSMUTE,
        ZIGZAGBOLT: vm.opponentStats.powerups.ZIGZAGBOLT
      },
      groove: {
        ...localRun.groove,
        groove: clamp01(vm.opponentStats.groovePct),
        active: vm.opponentStats.grooveActive,
        armed: vm.opponentStats.grooveActive || vm.opponentStats.grooveReady,
        readyWindowSec: vm.opponentStats.grooveReady ? Math.max(0.6, localRun.groove.readyWindowSec) : 0
      }
    };

    const opponentModel: RenderModel = {
      ...model,
      run: opponentRun,
      soloMode: "classic",
      soloModeLabel: soloModeLabel("classic"),
      activeObjective: null,
      activeObjectiveLabel: null,
      activeObjectiveReward: null,
      goalLabel: null,
      goalProgress: 0,
      goalTarget: null,
      goalRemaining: 0,
      goalRewardSummary: null,
      goalTargetRows: [],
      goalTargetColors: [],
      displayScore: opponentRun.totalScore,
      levelTarget: oppLevelTarget,
      pointsToNextLevel: Math.max(0, oppLevelTarget - opponentRun.levelScore),
      pendingRotation: vm.opponentStats.pendingRotation,
      warningActive: false,
      message: "",
      rotationAnim: vm.opponentRotationAnim,
      collapseAnim: vm.opponentCollapseAnim,
      swapAnim: vm.opponentSwapAnim,
      lastScorePopup: null,
      currentChainDepth: 1,
      hypeText: "",
      hypeTimer: 0,
      doomClockSec: opponentRun.doomClockSec,
      doomClockMaxSec: oppDoomClockMax,
      doomClockFrozen: false,
      doomClockTimePopup: null,
      cashPopupAmount: 0,
      cashDenyFlashSec: 0,
      armedPowerup: null,
      hintFirstPick: null,
      powerupExecuting: false,
      powerupExecutionRemainingSec: 0,
      dragState: null,
      hoverCell: null,
      hoveredPowerup: null,
      hintActive: false,
      hintCells: [],
      versusMatch: null,
      equippedCharacter: remoteCharacter,
      hideCornerCharacter: false
    };

    ctx.save();
    ctx.translate(VERSUS_OPPONENT_X, 0);
    ctx.beginPath();
    ctx.rect(0, 0, VERSUS_PANEL_WIDTH, CANVAS_HEIGHT);
    ctx.clip();
    this.drawHeader(opponentModel, feedbackMode);
    this.drawBoard(opponentModel, feedbackMode);
    this.drawDoomMeter(opponentModel);
    ctx.restore();
  }

  /**
   * Draw the opponent board with full animations (swap, rotation, collapse)
   * by translating the canvas to the opponent panel and reusing the same
   * drawing methods used for the local board.
   */
  private drawOpponentBoardAnimated(vm: VersusMatchRenderData): void {
    const ctx = this.ctx;
    const board = vm.opponentBoard;

    if (board == null) {
      const cx = VERSUS_OPPONENT_X + VERSUS_PANEL_WIDTH / 2;
      const cy = this.boardY + this.bps / 2;
      this.drawHourglass(cx, cy - 10, 28, "#4a6a9a");
      ctx.font = mFont("bold", 7);
      ctx.textAlign = "center";
      ctx.fillStyle = "#4a6a9a";
      ctx.fillText("WAITING...", cx, cy + 14);
      return;
    }

    // Translate context so this.box/Y land inside the opponent panel.
    // The opponent panel starts at VERSUS_OPPONENT_X, same 360px width as
    // the local canvas, so the board constants map 1:1.
    ctx.save();
    ctx.translate(VERSUS_OPPONENT_X, 0);

    // Clip to the opponent panel to prevent overflow into the divider
    ctx.beginPath();
    ctx.rect(0, 0, VERSUS_PANEL_WIDTH, CANVAS_HEIGHT);
    ctx.clip();

    if (vm.opponentRotationAnim?.active) {
      this.drawRotationBoard(vm.opponentRotationAnim);
    } else if (vm.opponentSwapAnim?.active) {
      this.drawSwapBoard(board, vm.opponentSwapAnim, false);
    } else if (vm.opponentCollapseAnim?.active) {
      this.drawCollapseBoard(board, vm.opponentCollapseAnim);
    } else {
      this.drawBoardSurface(board, {});
    }
    this.drawOpponentDecisionCue(vm);

    ctx.restore();
  }

  private drawOpponentBoardSimplified(board: BoardState | null): void {
    const ctx = this.ctx;
    if (board == null) {
      const cx = VERSUS_OPPONENT_X + VERSUS_PANEL_WIDTH / 2;
      const cy = this.boardY + this.bps / 2;
      this.drawHourglass(cx, cy - 10, 28, "#4a6a9a");
      ctx.font = mFont("bold", 7);
      ctx.textAlign = "center";
      ctx.fillStyle = "#4a6a9a";
      ctx.fillText("WAITING...", cx, cy + 14);
      return;
    }

    ctx.save();
    ctx.translate(VERSUS_OPPONENT_X, 0);
    ctx.beginPath();
    ctx.rect(0, 0, VERSUS_PANEL_WIDTH, CANVAS_HEIGHT);
    ctx.clip();
    this.drawBoardSurface(board, {});
    ctx.restore();
  }

  private drawOpponentDecisionCue(vm: VersusMatchRenderData): void {
    const ctx = this.ctx;
    let label: string | null = null;
    let stroke = "#6fd7ff";
    if (vm.opponentRotationAnim?.active) {
      label = "ROTATE";
      stroke = "#6fd7ff";
    } else if (vm.opponentSwapAnim?.active) {
      label = "SWAP";
      stroke = "#ffd676";
    } else if (vm.opponentCollapseAnim?.active) {
      label = "COLLAPSE";
      stroke = "#ff9f7a";
    }
    if (label == null) {
      return;
    }

    const pulse = 0.55 + 0.45 * Math.sin(this.nowMs() / 110);
    const frameX = this.box - 2;
    const frameY = this.boardY - 2;
    const frameW = this.bps + 4;
    const frameH = this.bps + 4;
    const badgeW = 84;
    const badgeH = 16;
    const badgeX = frameX + 8;
    const badgeY = frameY + 8;

    ctx.save();
    ctx.globalAlpha = 0.6 + 0.25 * pulse;
    roundedRectPath(ctx, frameX, frameY, frameW, frameH, 8);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.globalAlpha = 0.92;
    roundedRectPath(ctx, badgeX, badgeY, badgeW, badgeH, 4);
    ctx.fillStyle = "rgba(5,12,25,0.84)";
    ctx.fill();
    roundedRectPath(ctx, badgeX, badgeY, badgeW, badgeH, 4);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = mFont("bold", 4);
    ctx.fillStyle = "#e8f7ff";
    ctx.fillText(label, badgeX + badgeW / 2, badgeY + badgeH / 2);
    ctx.restore();
  }

  private drawOpponentStateSyncPanel(vm: VersusMatchRenderData): void {
    const ctx = this.ctx;
    const op = vm.opponentStats;
    const panelX = VERSUS_OPPONENT_X + this.box;
    const panelY = this.boardY;
    const panelW = this.bps;
    const panelH = this.bps;
    const doomPct = Math.round((1 - clamp01(op.doomPct)) * 100);
    const groovePct = Math.round(clamp01(op.groovePct) * 100);
    const level = Math.max(1, Math.floor(op.level));
    const score = Math.max(0, Math.floor(op.score)).toLocaleString();
    const pendingRotation = op.pendingRotation ?? "NONE";
    const powerupsLine = `H${op.powerups.HINT} R${op.powerups.ROWRIP} C${op.powerups.COLCUT} T${op.powerups.TRANSMUTE} Z${op.powerups.ZIGZAGBOLT}`;

    ctx.save();
    roundedRectPath(ctx, panelX, panelY, panelW, panelH, 10);
    const panelFill = ctx.createLinearGradient(panelX, panelY, panelX, panelY + panelH);
    panelFill.addColorStop(0, "rgba(14,24,44,0.94)");
    panelFill.addColorStop(1, "rgba(8,14,28,0.94)");
    ctx.fillStyle = panelFill;
    ctx.fill();
    roundedRectPath(ctx, panelX, panelY, panelW, panelH, 10);
    ctx.strokeStyle = "rgba(123,166,235,0.92)";
    ctx.lineWidth = 1.2;
    ctx.stroke();

    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#d6e8ff";
    ctx.font = mFont("bold", 6);
    ctx.fillText("STATE SYNC", panelX + 12, panelY + 12);
    ctx.font = mFontPlain(4);
    ctx.fillStyle = "rgba(173,201,240,0.92)";
    ctx.fillText("DESKTOP SIMPLE VIEW", panelX + 12, panelY + 24);

    const valueX = panelX + 14;
    let rowY = panelY + 52;
    const rowGap = 18;
    ctx.font = mFontPlain(5);
    ctx.fillStyle = "#e7f2ff";
    ctx.fillText(`LEVEL ${level}`, valueX, rowY);
    rowY += rowGap;
    ctx.fillText(`SCORE ${score}`, valueX, rowY);
    rowY += rowGap;
    ctx.fillText(`DOOM ${doomPct}%   GROOVE ${groovePct}%`, valueX, rowY);
    rowY += rowGap;
    ctx.fillText(`ROT KEYS ${Math.max(0, Math.floor(op.rotationKeys))}`, valueX, rowY);
    rowY += rowGap;
    ctx.fillText(`PENDING ${pendingRotation}`, valueX, rowY);
    rowY += rowGap;
    ctx.fillText(`PWR ${powerupsLine}`, valueX, rowY);

    // Keep portrait effects and trap FX active outside this panel.
    if (!op.connected && vm.disconnectCountdownSec != null) {
      ctx.fillStyle = "rgba(255,120,120,0.9)";
      ctx.font = mFont("bold", 5);
      ctx.fillText(`RECONNECT ${Math.max(0, Math.ceil(vm.disconnectCountdownSec))}s`, panelX + 12, panelY + panelH - 24);
    }
    ctx.restore();
  }

  private drawLocalFogOverlay(ctx: CanvasRenderingContext2D, remainingSec: number): void {
    ctx.fillStyle = "rgba(20, 30, 50, 0.82)";
    ctx.fillRect(this.box, this.boardY, this.bps, this.bps);

    ctx.save();
    ctx.font = `bold 14px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#ff8844";
    ctx.shadowColor = "#ff8844";
    ctx.shadowBlur = 10;
    ctx.fillText(
      "FOG",
      this.box + this.bps / 2,
      this.boardY + this.bps / 2 - 10
    );
    ctx.shadowBlur = 0;
    ctx.font = mFont("bold", 8);
    ctx.fillStyle = "#ffaa66";
    ctx.fillText(
      `${remainingSec.toFixed(1)}s`,
      this.box + this.bps / 2,
      this.boardY + this.bps / 2 + 14
    );
    ctx.restore();
  }

  /**
   * Animated FX overlay on opponent portrait when a trap is sent.
   * Each trap type has a unique visual effect that fades over ~3 seconds.
   */
  private drawTrapFxOverlay(
    ctx: CanvasRenderingContext2D,
    kind: string,
    ageSec: number,
    px: number, py: number, size: number
  ): void {
    drawTrapFxOverlayScreen(ctx, kind, ageSec, px, py, size, this.nowSec());
  }

  /**
   * Ambient state FX overlaid on the opponent's portrait based on their current state.
   * - Groove active: rainbow shimmer aura
   * - Groove ready: pulsing green glow
   * - Low doom (<25%): red danger vignette
   * - Disconnected: static/glitch overlay
   */
  private drawOpponentStateFx(
    ctx: CanvasRenderingContext2D,
    stats: { grooveActive: boolean; grooveReady: boolean; doomPct: number; connected: boolean },
    px: number, py: number, size: number
  ): void {
    const now = this.nowSec();

    // â"€â"€ Groove active: bold rainbow shimmer aura â"€â"€
    if (stats.grooveActive) {
      this.drawPortraitGrooveActiveFx(ctx, px, py, size);
    }

    // â"€â"€ Disconnected: heavy static/glitch scanlines â"€â"€
    if (!stats.connected) {
      ctx.save();
      ctx.globalAlpha = 0.6;
      for (let y = py; y < py + size; y += 4) {
        const glitchX = Math.sin(y * 0.3 + now * 10) * 3;
        ctx.fillStyle = y % 8 < 4 ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.2)";
        ctx.fillRect(px + glitchX, y, size, 2);
      }
      ctx.restore();
    }
  }

  /**
   * Rainbow shimmer aura on any portrait when groove is active.
   * Shared between player and opponent portraits.
   */
  private drawPortraitGrooveActiveFx(
    ctx: CanvasRenderingContext2D,
    px: number, py: number, size: number
  ): void {
    const now = this.nowSec();
    const cx = px + size / 2;
    const cy = py + size / 2;

    ctx.save();
    ctx.globalAlpha = 0.7;
    const hue = (now * 140) % 360;
    for (let ring = 0; ring < 4; ring++) {
      const r = size * (0.3 + ring * 0.1) + Math.sin(now * 3.5 + ring) * 5;
      const ringHue = (hue + ring * 35) % 360;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = `hsla(${ringHue}, 100%, 65%, ${0.8 - ring * 0.12})`;
      ctx.lineWidth = 4 - ring * 0.6;
      ctx.shadowColor = `hsla(${ringHue}, 100%, 70%, 0.8)`;
      ctx.shadowBlur = 10;
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
    for (let i = 0; i < 10; i++) {
      const angle = now * 2 + i * (Math.PI * 2 / 10);
      const dist = size * 0.4 + Math.sin(now * 5 + i * 1.1) * 8;
      const sx = cx + Math.cos(angle) * dist;
      const sy = cy + Math.sin(angle) * dist;
      const sparkHue = (hue + i * 36) % 360;
      ctx.beginPath();
      ctx.arc(sx, sy, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = `hsla(${sparkHue}, 100%, 80%, 0.95)`;
      ctx.shadowColor = `hsla(${sparkHue}, 100%, 70%, 1)`;
      ctx.shadowBlur = 6;
      ctx.fill();
      ctx.shadowBlur = 0;
    }
    ctx.restore();
  }

  private drawVersusResult(model: RenderModel): UiRects {
    return drawVersusResultScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        nowMs: () => this.nowMs(),
        nowSec: () => this.nowSec(),
        rankVinylImages: this.rankVinylImages,
        animState: this.versusResultAnimState,
        drawBtn: (rect, label, options) => this.drawBtn(rect, label, options),
        drawCharacterPortrait: (path, x, y, w, h) => this.drawCharacterPortrait(path, x, y, w, h),
        drawGemIcon: (x, y, size) => this.drawGemIcon(x, y, size),
      },
      model
    );
  }

  private drawUsernamePick(model: RenderModel): UiRects {
    return drawUsernamePickScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        menuPhaseEnterMs: this.menuPhaseEnterMs,
        nowMs: () => this.nowMs(),
        drawCharacterPortrait: (path, x, y, w, h) => this.drawCharacterPortrait(path, x, y, w, h),
      },
      model,
    );
  }

  private drawFriends(model: RenderModel): UiRects {
    return drawFriendsScreen({
      ctx: this.ctx,
      drawBtn: (r, l, o) => this.drawBtn(r, l, o),
      drawBtnStagger: (r, l, i, o) => this.drawBtnStagger(r, l, i, o),
      drawMenuPageNavButton: (r, l, s) => this.drawMenuPageNavButton(r, l, s),
      drawCharacterPortrait: (img, x, y, w, h) => this.drawCharacterPortrait(img, x, y, w, h),
      nowMs: () => this.nowMs(),
      measureTextWidth: (t) => this.measureTextWidth(t),
    }, model);
  }

  private drawAddFriend(model: RenderModel): UiRects {
    const ctx = this.ctx;
    const centerX = CANVAS_WIDTH / 2;
    const menuCols = menuButtonColumns(centerX);
    const uiRects: UiRects = {};

    // Input placeholder area (the actual HTML input overlays this)
    const inputY = 136;
    const inputW = menuCols.fullW;
    const afInputH = menuBtnHLg();
    const addFriendInputRect: Rect = { x: menuCols.fullX, y: inputY, w: inputW, h: afInputH };
    roundedRectPath(ctx, menuCols.fullX, inputY, inputW, afInputH, 6);
    ctx.fillStyle = "rgba(15,22,40,0.95)";
    ctx.fill();
    roundedRectPath(ctx, menuCols.fullX, inputY, inputW, afInputH, 6);
    ctx.strokeStyle = "#5c8cd4";
    ctx.lineWidth = 1;
    ctx.stroke();
    uiRects.addFriendInput = addFriendInputRect;

    // SEND button
    const sendRect: Rect = { x: menuCols.fullX, y: inputY + afInputH + menuGap(), w: menuCols.fullW, h: menuBtnH() };
    this.drawBtn(sendRect, "SEND REQUEST", { fill: "#1a3a2a", stroke: "#4dff88", font: mFont("bold", 7) });
    uiRects.addFriendSubmit = sendRect;

    // Error/success message
    if (model.friendsError) {
      const isSuccess = model.friendsError.includes("SENT");
      ctx.fillStyle = isSuccess ? "#4dff88" : "#ff6b6b";
      ctx.font = mFont("bold", 6);
      ctx.textAlign = "center";
      ctx.fillText(model.friendsError, centerX, inputY + 80);
    }

    // Back button (centered, matching menu layout)
    const addFriendBackRect: Rect = { x: menuCols.fullX, y: 470, w: menuCols.fullW, h: menuBtnH() };
    this.drawBtn(addFriendBackRect, "BACK", { fill: "#1a2a4c", stroke: "#5c8cd4", font: mFont("bold", 7) });
    uiRects.addFriendBack = addFriendBackRect;

    return uiRects;
  }

  private drawVersusLobby(model: RenderModel): UiRects {
    return drawVersusLobbyScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        nowSec: () => this.nowSec(),
        rafsbyPortrait: this.rafsbyPortrait,
        uiRefreshIcon: this.uiRefreshIcon,
        menuSafeAreaTop: this._menuSafeAreaTop,
        drawCharacterPortrait: (path, x, y, w, h) => this.drawCharacterPortrait(path, x, y, w, h),
        drawCircleCloseButton: (rect) => this.drawCircleCloseButton(rect),
        drawHourglass: (cx, cy, size, color) => this.drawHourglass(cx, cy, size, color),
      },
      model
    );
  }

  private drawGameOver(model: RenderModel): UiRects {
    return drawGameOverScreen(
      {
        ctx: this.ctx,
        canvasHeight: this.currentLayout.canvasHeight,
        nowMs: () => this.nowMs(),
        soloSelectorLogoImages: this.soloSelectorLogoImages,
        flashState: this.gameOverFlashState,
        drawCoinIcon: (x, y, size) => this.drawCoinIcon(x, y, size),
        drawHourglass: (cx, cy, size, color) => this.drawHourglass(cx, cy, size, color),
        resolveLeaderboardScroll: (config) => this.resolveLeaderboardScroll(config),
      },
      model
    );
  }
}

